import {
  InfiniteData,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  UseSuspenseInfiniteQueryOptions,
  UseSuspenseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query"

import { publicFetcher } from "./fetcher"
import * as Types from "./public-types"

export const AuthUserFragmentDoc = `
    fragment AuthUser on AuthUser {
  pid
  isAuthenticated
  isAdmin
  email
  name
  org {
    pid
    name
    logoUrl
    irrFlavor
  }
}
    `
export const SiteLabelRefFragmentDoc = `
    fragment SiteLabelRef on SiteLabel {
  pid
  name
  themeColor
  sites
}
    `
export const UserRefFragmentDoc = `
    fragment UserRef on UserRef {
  pid
  email
  name
  isAdmin
  avatarUrl
}
    `
export const SiteLabelDetailFragmentDoc = `
    fragment SiteLabelDetail on SiteLabel {
  ...SiteLabelRef
  createdBy {
    ...UserRef
  }
  perms {
    canAdd
    canRemove
  }
}
    `
export const PolicyBriefFragmentDoc = `
    fragment PolicyBrief on Policy {
  pid
  name
  publicName
  description
  publicDescription
  sources {
    text
    url
  }
  userVisible
  application {
    status
  }
}
    `
export const BundleFragmentDoc = `
    fragment Bundle on Bundle {
  pid
  createdAt
  name
  note
  state
  optedOut
  biddingOpenAt
  biddingClosedAt
  hasParent
  canDelete
  archived
  type
  recvr {
    pid
    name
  }
  stats {
    numSites
    capex
    postTaxItcDeprIrr
    pvSizeKw
  }
  downloadTheBigZipHref
  documents {
    canDelete
    contentType
    dateCreated
    fileName
    ownerName
    pid
    tag
    downloadHref
    belongsToBundleParent
  }
}
    `
export const SiteConfigFragmentDoc = `
    fragment SiteConfig on SiteConfig {
  createdAt
  createdBy {
    ...UserRef
  }
  scenario {
    pid
    name
    loadCategory
    solarLocation
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
}
    `
export const FootprintFragmentDoc = `
    fragment Footprint on Footprint {
  latitude
  longitude
  bounds
  obstructionShape
  roofShape
  roofShapeGeojson
  roofShapeUpdatedAt
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShape
  roofSetbackShapeGeojson
  roofSetbackShapeUpdatedAt
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShape
  carportShapeGeojson
  carportShapeUpdatedAt
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShape
  groundmountShapeGeojson
  groundmountShapeUpdatedAt
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShape
  keepoutsShapeGeojson
  keepoutsShapeUpdatedAt
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
}
    `
export const RevampSiteBasicsFragmentDoc = `
    fragment RevampSiteBasics on RevampSite {
  pid
  name
  isFavorite
  isBehindTheMeter
  isEnergyCommunity
  isLowIncomeCommunity
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  vnemAllowed
  doeReferenceType
  usingBillingMeters
  numParkingSpaces
  selectedConfig {
    ...SiteConfig
  }
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  maxPvKwPolicyLimited
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
    `
export const BillingMeterOptionFragmentDoc = `
    fragment BillingMeterOption on BillingMeter {
  pid
  bauMeterPid
  view {
    name
    isArchived
    calc2 {
      __typename
      ... on Projected {
        annualKwh
      }
    }
  }
}
    `
export const KwhFlowFragmentDoc = `
    fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
    `
export const RevampSystemFragmentDoc = `
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    assumptions {
      battChemName
    }
  }
}
    `
export const FinmodelCostFragmentDoc = `
    fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
    `
export const RevampFinmodelFragmentDoc = `
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
    `
export const RevampCsFragmentDoc = `
    fragment RevampCS on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    lifetimeUsd
    annualUsd
    usdPerSqft
    npv
    discountRate
    roofRentPerYear
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  leaseProjections {
    systemSizeKw
    annualRentLowerRangeUsd
    annualRentUpperRangeUsd
    methodology
  }
}
    `
export const HelioscopeFragmentDoc = `
    fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  pdfHref
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}
    `
export const ResultFragmentDoc = `
    fragment Result on Result {
  id
  pid
  createdAt
  salesPdfReportHref
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
    `
export const BundleRowFragmentDoc = `
    fragment BundleRow on BundleRow {
  id
  site {
    ...RevampSiteBasics
    billingMeters {
      ...BillingMeterOption
    }
  }
  result {
    ...Result
    result {
      ... on RevampFinmodel {
        commOpDate
        investorDetails {
          irrs {
            postTaxItcDepr
          }
          capex
          investorXlsxHref
        }
        itcAssumptions {
          rate
          inLowIncomeCommunity
          inEnergyCommunity
          domesticMaterialsAdder
        }
      }
    }
  }
}
    `
export const BundleTableRowFragmentDoc = `
    fragment BundleTableRow on BundleRow {
  id
  state
  reason
  site {
    pid
    name
    fullAddress
    streetAddress
    city
    state
    zipCode
    targetUtility {
      name
      hostingCapacityMaps {
        url
        requiresLogin
        types
      }
    }
    siteDetails {
      roofReplacedYr
      roofCondition
    }
    doeReferenceType
    yearBuilt
    sqft
    roofSqft
    roofMaxPvKw
    numStories
    buildingClass
    isLowIncomeCommunity
    isEnergyCommunity
  }
  helioscope {
    pdfHref
    systemProductivityKwhPerKw
  }
  result {
    id
    result {
      __typename
      ... on RevampFinmodel {
        buildingOwnerXlsxHref
        commOpDate
        goal
        investorDetails {
          irrs {
            postTaxItcDepr
          }
          capex
          investorXlsxHref
        }
        buildingOwner {
          noiAnnual
        }
        utilityCosts {
          der {
            totalKwh
            usdPerKwh
            tariffs {
              name
              code
            }
          }
        }
        systems {
          ...RevampSystem
        }
        ppa {
          __typename
          ppaType
          ppaCostPerKwh
          ppaTermYears
          ppaEsc
          roofRent {
            annualUsd
          }
          srecToInvestor
          offtakerXlsxHref
          product {
            productName
            financierName
          }
        }
        loan {
          __typename
          loanRecvrXlsxHref
          loanTermYears
          ownershipTermYears
          interestPct
          product {
            productName
            financierName
            loanDealerFeePct
          }
        }
      }
      ... on RevampCS {
        id
        termYears
        goal
        systems {
          ...RevampSystem
        }
        roofRent {
          lifetimeUsd
          annualUsd
          usdPerSqft
        }
      }
    }
  }
}
    `
export const DecodedRateFragmentDoc = `
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
    `
export const CostOrTaxItemFragmentDoc = `
    fragment CostOrTaxItem on CostItemTaxItem {
  __typename
  ... on CostItem {
    rateObj {
      ...DecodedRate
    }
    name
    rateId
    chargeType
    chargeCategory
    volume
    rate
    cost
  }
  ... on TaxItem {
    taxId
    subtotal
    cost
    percentage
  }
}
    `
export const BillSectionFragmentDoc = `
    fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...CostOrTaxItem
  }
}
    `
export const FormattedBillFragmentDoc = `
    fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}
    `
export const SiteBauMeterFragmentDoc = `
    fragment SiteBauMeter on BauMeter {
  id
  pid
  userVisibleLabel
  loadOrigin
  loadCategory
  formattedBill {
    ...FormattedBill
  }
  tariff {
    id
    name
    code
  }
  rollup {
    season
    days
    numbers
  }
  intervals {
    maxKw
    year
    units
    totalKwh
  }
  tariffUsdPerKwh
  totalCost
  importsKwh
}
    `
export const SiteMeterSetFragmentDoc = `
    fragment SiteMeterSet on RevampSite {
  meterSet {
    intervalsBauXlsxHref
    loadKwh
    loadMaxKw
    loadOrigin
    totalCost
    rollup {
      season
      days
      numbers
    }
    meters {
      ...SiteBauMeter
    }
  }
}
    `
export const ShineStatsFragmentDoc = `
    fragment ShineStats on ShineStats {
  numProperties
  pvKwInstalled
  totalCo2ReducedTonnes
  totalGrossRevenue
  totalPvProductionKwh
}
    `
export const ShineUnitFragmentDoc = `
    fragment ShineUnit on ShineUnit {
  duration
  start
  actualPvProductionKwh
  expectedPvProductionKwh
  actualGrossRevenue
  expectedGrossRevenue
  co2EmissionsReductionTonnes
}
    `
export const CleanRowDataFragmentDoc = `
    fragment CleanRowData on CleanRowData {
  customerId
  streetAddress
  city
  state
  zipCode
  buildingType
  sqft
}
    `
export const DirtyRowDataFragmentDoc = `
    fragment DirtyRowData on DirtyRowData {
  name
  assetManager
  customerId
  numStories
  streetAddress
  city
  state
  zipCode
  buildingType
  sqft
  yearBuilt
}
    `
export const CsvImportRowFragmentDoc = `
    fragment CsvImportRow on CsvImportRow {
  id
  site {
    pid
    name
  }
  dirty {
    ...DirtyRowData
  }
}
    `
export const CsvImportLabelsFragmentDoc = `
    fragment CsvImportLabels on CsvImport {
  pid
  labels {
    pid
    themeColor
    name
  }
}
    `
export const FancyFloatFragmentDoc = `
    fragment FancyFloat on FancyFloat {
  source
  number
}
    `
export const ProviderFragmentDoc = `
    fragment Provider on Provider {
  id
  createdAt
  user {
    ...UserRef
  }
  document {
    pid
    fileName
    contentType
    downloadHref
    viewHref
  }
  espmConsumption {
    id
    createdAt
    lastUpdatedAt
    externalConsumptionId
    externalData
    start
    end
    usageKwh
    costUsd
  }
}
    `
export const BillingMonthViewFragmentDoc = `
    fragment BillingMonthView on BillingMonthView {
  id
  start
  end
  usageKwh {
    ...FancyFloat
  }
  peakKw {
    ...FancyFloat
  }
  totalCostUsd {
    ...FancyFloat
  }
  supplyCostUsd {
    ...FancyFloat
  }
  transmissionCostUsd {
    ...FancyFloat
  }
  provider {
    ...Provider
  }
  supplyProvider {
    ...Provider
  }
}
    `
export const GroupedLineItemFragmentDoc = `
    fragment GroupedLineItem on GroupedLineItem {
  cost
  usage
  name
  rate
  group
  unit
}
    `
export const BillGroupFragmentDoc = `
    fragment BillGroup on BillGroup {
  group
  items {
    ...GroupedLineItem
  }
}
    `
export const BillingMeterProjectedFragmentDoc = `
    fragment BillingMeterProjected on Projected {
  year
  annualKwh
  peakKw
  annualCostUsd
  monthlyUsageKwh
  monthlyCostUsd
  kwh8760 {
    timeZone
    start
    year
  }
  costAndKwhDownloadHref
  tariff {
    id
    pid
    name
    code
  }
  monthlyBills {
    start
    end
    sections {
      ...BillGroup
    }
  }
}
    `
export const BillingIntervalViewFragmentDoc = `
    fragment BillingIntervalView on BillingIntervalView {
  id
  start
  end
  timeZone
  units
  numbers
  timestamps
  document {
    id
    pid
    fileName
    downloadHref
  }
}
    `
export const BillingMeterFragmentDoc = `
    fragment BillingMeter on BillingMeter {
  id
  pid
  view {
    name
    utilityMeterId
    supplyUtilityMeterId
    doeReferenceType
    loadCategory
    isDisabled
    isArchived
    utility {
      pid
      name
      shortName
    }
    tariff {
      id
      pid
      name
      code
    }
    tenantDetails {
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    numBills
    numIntervals
    totalAnnualKwh {
      ...FancyFloat
    }
    peakAnnualKw {
      ...FancyFloat
    }
    weeklyRollup
    months {
      ...BillingMonthView
    }
    calc2 {
      __typename
      ... on E2Error {
        msg
      }
      ... on Projected {
        ...BillingMeterProjected
      }
    }
    intervals {
      ...BillingIntervalView
    }
  }
}
    `
export const PublicTariffFragmentDoc = `
    fragment PublicTariff on PublicTariff {
  id
  pid
  name
  code
  tariffType
  isPreferred
  isNem2
  isClosed
  isPrimaryConnection
  customTariffId
  notes
  updatedAt
  trashedAt
  requiresPv
  requiresStorage
  hasTouRates
  hasNetMetering
  customerLikelihood
  customerCount
  customerClass
  minMonthlyKw
  maxMonthlyKw
  minMonthlyKwh
  maxMonthlyKwh
  latestRateId
}
    `
export const BasicBundleParentFragmentDoc = `
    fragment BasicBundleParent on BundleParent {
  id
  createdAt
  pid
  name
  type
  note
  pricingNote
  state
  biddingOpenAt
  biddingClosedAt
  opsOwner {
    ...UserRef
  }
  recvr {
    pid
    name
  }
  canAddNewBids
  canEditBids
  hiddenFromRecvr
  hiddenFromFinancier
  showAllBidsReport
}
    `
export const PvSystemSpecFragmentDoc = `
    fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
  }
  productionCsv {
    downloadHref
  }
  productionSource {
    id
    origin
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
    `
export const SystemFragmentDoc = `
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
    `
export const PpaBidSpecFragmentDoc = `
    fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
}
    `
export const SystemLiteFragmentDoc = `
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
    notes
  }
}
    `
export const CsBidSpecFragmentDoc = `
    fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
}
    `
export const InvestorBidSpecOptOutFragmentDoc = `
    fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
    `
export const BidSpecFragmentDoc = `
    fragment BidSpec on BidSpec {
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
}
    `
export const BundleParentFragmentDoc = `
    fragment BundleParent on BundleParent {
  ...BasicBundleParent
  bundles {
    name
    pid
    financier {
      pid
      name
      orgSlug
    }
  }
  rows {
    specs {
      ...BidSpec
    }
    site {
      pid
      name
      streetAddress
      city
      state
      zipCode
    }
    notes
    status
    closedAt
    closeReason
    closeCustomReason
    awardedFinancier {
      pid
      name
      logoUrl
      logomarkUrl
    }
    recommendedBid {
      ppa {
        pid
      }
      cs {
        pid
      }
    }
  }
  documents {
    id
    pid
    tag
    fileName
    contentType
    dateCreated
    downloadHref
    canDelete
    ownerName
  }
}
    `
export const CustomBidReportFragmentDoc = `
    fragment CustomBidReport on CustomBidReport {
  pid
  name
  notes
  buildingOwnerNotes
  visibleToDevelopers
  visibleToBuildingOwners
  bids {
    ppa {
      pid
      financier {
        orgSlug
      }
    }
    cs {
      pid
      financier {
        orgSlug
      }
    }
  }
}
    `
export const DocumentParseRequestFragmentDoc = `
    fragment DocumentParseRequest on DocumentParseRequest {
  id
  createdAt
  status
  errorClass
  errorMessage
  document {
    id
    pid
    fileName
    contentType
    downloadHref
    viewHref
    ownerName
    ownerEmail
    dateCreated
  }
  results {
    parse {
      id
      start
      end
      totalKwh
      peakKw
      totalCostUsd
      supplyCostUsd
      transmissionCostUsd
      meterId
      tariffLabel
      kind
    }
    target {
      id
      pid
      label
      utilityMeterId
    }
    kind
  }
}
    `
export const InvestorOptOutFragmentDoc = `
    fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
    `
export const BundleListItemFragmentDoc = `
    fragment BundleListItem on Bundle2 {
  pid
  name
  type
  note
  pricingNote
  state
  archivedAt
  lastViewed
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
    `
export const InvestorProfileFragmentDoc = `
    fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    btmEnergizedPvKw
    btmAwardedPvKw
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmSeekingProjects
  }
}
    `
export const PpaInvestorBidFragmentDoc = `
    fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  tariffProvision
  otherNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
}
    `
export const CsInvestorBidFragmentDoc = `
    fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  tariffProvision
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCS
  }
}
    `
export const InvestorBidFragmentDoc = `
    fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
}
    `
export const BundleDetailRowFragmentDoc = `
    fragment BundleDetailRow on BundleRow2 {
  id
  ppaType
  state
  reason
  customReason
  notes
  optOut {
    ...InvestorOptOut
  }
  bids {
    ...InvestorBid
  }
  specs {
    ...BidSpec
  }
}
    `
export const BoxplotStatsFragmentDoc = `
    fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
    `
export const CsSiteBidReportFragmentDoc = `
    fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    isFavorite
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    tariffProvision
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
    `
export const PpaSiteBidReportFragmentDoc = `
    fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    isFavorite
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    tariffProvision
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
    `
export const BidReportFragmentDoc = `
    fragment BidReport on PackageBidReport {
  pid
  name
  notes
  visible
  totalDevelopers
  totalBids
  cs {
    ...CSSiteBidReport
  }
  ppa {
    ...PPASiteBidReport
  }
}
    `
export const BundleDetailFragmentDoc = `
    fragment BundleDetail on Bundle2 {
  ...BundleListItem
  opsOwner {
    ...UserRef
    meetingSchedulingUrl
  }
  parentRef {
    pid
  }
  documents {
    canDelete
    contentType
    dateCreated
    fileName
    ownerName
    pid
    tag
    downloadHref
    viewHref
    belongsToBundleParent
  }
  downloadBundleDocsZipHref
  downloadHelioscopesDotZipHref
  downloadBundleDotXlsxHref
  rows {
    ...BundleDetailRow
    site {
      pid
      name
      fullAddress
      streetAddress
      city
      state
      zipCode
      county
      cbsaName
      buildingData {
        ownerName
      }
      doeReferenceType
      siteDetails {
        roofReplacedYr
        roofCondition
      }
      yearBuilt
      targetUtility {
        pid
        name
        hostingCapacityMaps {
          url
          requiresLogin
          types
        }
      }
      isEnergyCommunity
      isLowIncomeCommunity
      sqft
      roofSqft
      roofMaxPvKw
      numStories
      buildingClass
    }
  }
  bidReports {
    ...BidReport
  }
}
    `
export const BundleRowDetailFragmentDoc = `
    fragment BundleRowDetail on BundleRow2 {
  ...BundleDetailRow
  site {
    ...RevampSiteBasics
    meterSet {
      totalCost
      loadKwh
    }
    billingMeters {
      ...BillingMeterOption
    }
  }
}
    `
export const Helioscope2RefFragmentDoc = `
    fragment Helioscope2Ref on Helioscope2 {
  id
  pdf {
    downloadHref
  }
  roofImage {
    viewHref
  }
  productionCsv {
    downloadHref
  }
}
    `
export const BasicBidPackageFragmentDoc = `
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
    `
export const BidPackageDetailFragmentDoc = `
    fragment BidPackageDetail on BidPackage {
  ...BasicBidPackage
  documents {
    canDelete
    contentType
    dateCreated
    fileName
    ownerName
    pid
    tag
    downloadHref
    belongsToBundleParent
  }
}
    `
export const BidReviewFragmentDoc = `
    fragment BidReview on BidReview {
  recommendedBidPids
  reports {
    properties
    bidders
    uniqueBids
    avgRoofRentY1Usd
    isCustom
    report {
      ...BidReport
    }
  }
}
    `
export const RateBandFragmentDoc = `
    fragment RateBand on RateBand {
  tariffRateBandId
  rateUnit
  price
  isCredit
  applicabilityValue
}
    `
export const SeasonFragmentDoc = `
    fragment Season on Season {
  seasonFromDay
  seasonFromMonth
  seasonToDay
  seasonToMonth
}
    `
export const RateFragmentDoc = `
    fragment Rate on Rate {
  name
  groupName
  tariffRateId
  chargeClass
  chargeType
  transactionType
  chargePeriod
  applicabilityKey
  applicabilityValues
  quantityKey
  applicableBands {
    ...RateBand
  }
  bands {
    ...RateBand
  }
  tou {
    touName
    touType
    touPeriods {
      fromHour
      fromMinute
      fromDayOfWeek
      toHour
      toMinute
      toDayOfWeek
    }
    season {
      ...Season
    }
  }
  season {
    ...Season
  }
}
    `
export const CustomRateFragmentDoc = `
    fragment CustomRate on CustomRate {
  name
  chargeClass
  chargeType
  transactionType
  price
  rateUnit
  chargePeriod
}
    `
export const TariffOpFragmentDoc = `
    fragment TariffOp on AddRateSubRateOverrideProperty {
  __typename
  ... on AddRate {
    rate {
      ...CustomRate
    }
    op
  }
  ... on SubRate {
    chargeClass
    rateId
    op
  }
  ... on OverrideProperty {
    keyName
    propertyValue
    op
  }
}
    `
export const ChangeLogEntryFragmentDoc = `
    fragment ChangeLogEntry on ChangeLogEntry {
  source
  sourceId
  oldValue
  newValue
  columnType
  changedAt
}
    `
export const FieldChangeLogHistoryFragmentDoc = `
    fragment FieldChangeLogHistory on FieldChangeLogHistory {
  latest {
    ...ChangeLogEntry
  }
  history {
    ...ChangeLogEntry
  }
}
    `
export const SiteFieldChangeLogFragmentDoc = `
    fragment SiteFieldChangeLog on SiteFieldChangeLog {
  occupancyType {
    ...FieldChangeLogHistory
  }
  leaseType {
    ...FieldChangeLogHistory
  }
  targetUtilityId {
    ...FieldChangeLogHistory
  }
  tenantLeaseTerm {
    ...FieldChangeLogHistory
  }
}
    `
export const SiteDetailsFieldChangeLogFragmentDoc = `
    fragment SiteDetailsFieldChangeLog on SiteDetailsFieldChangeLog {
  buildingLegalName {
    ...FieldChangeLogHistory
  }
  constructionEmail {
    ...FieldChangeLogHistory
  }
  counselApproval {
    ...FieldChangeLogHistory
  }
  externalLeaseApprovers {
    ...FieldChangeLogHistory
  }
  isFannieMaeLender {
    ...FieldChangeLogHistory
  }
  isFmGlobalInsurer {
    ...FieldChangeLogHistory
  }
  leaseApprovers {
    ...FieldChangeLogHistory
  }
  leaseLegalOwnerName {
    ...FieldChangeLogHistory
  }
  leasePropertyLegalDescription {
    ...FieldChangeLogHistory
  }
  leaseSignatory {
    ...FieldChangeLogHistory
  }
  needsTenantApproval {
    ...FieldChangeLogHistory
  }
  propertyFundName {
    ...FieldChangeLogHistory
  }
  propertyInsurer {
    ...FieldChangeLogHistory
  }
  propertyLenders {
    ...FieldChangeLogHistory
  }
  propertySiteVisitContacts {
    ...FieldChangeLogHistory
  }
  roofCondition {
    ...FieldChangeLogHistory
  }
  roofReplacedYr {
    ...FieldChangeLogHistory
  }
  roofRightsHolder {
    ...FieldChangeLogHistory
  }
  salePlannedYear {
    ...FieldChangeLogHistory
  }
  salePlans {
    ...FieldChangeLogHistory
  }
  signatureBlocks {
    ...FieldChangeLogHistory
  }
}
    `
export const MutBillingMeterFragmentDoc = `
    fragment MutBillingMeter on BillingMeter {
  __typename
  ... on BillingMeter {
    pid
    label
    config {
      loadCategory
      doeReferenceType
    }
  }
}
    `
export const EtbExtractFragmentDoc = `
    fragment EtbExtract on ETBExtract {
  id
  document {
    id
    pid
    fileName
    downloadHref
  }
  pv {
    sizeKwDc
    sizeKwAc
    etbMeterId
    etbMeterName
  }
  battery {
    sizeKwDc
    sizeKwh
    etbMeterId
    etbMeterName
  }
}
    `
export const PossibleTariffFragmentDoc = `
    fragment PossibleTariff on Tariff {
  id
  name
  code
  customerClass
  customerCount
  customerLikelihood
  isPreferred
  minMonthlyKw
  maxMonthlyKw
  minMonthlyKwh
  maxMonthlyKwh
  masterTariffId
  hasNetMetering
  requiresPv
  requiresStorage
  tariffType
}
    `
export const ResultRefFragmentDoc = `
    fragment ResultRef on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
}
    `
export const ResultWithEnergyDataFragmentDoc = `
    fragment ResultWithEnergyData on Result {
  id
  result {
    ... on RevampFinmodel {
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
  }
}
    `
export const BidRevampSiteFragmentDoc = `
    fragment BidRevampSite on RevampSite {
  pid
  name
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  doeReferenceType
  meterSet {
    totalCost
    loadKwh
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  siteDetails {
    salePlans
    roofCondition
    roofReplacedYr
  }
  roofMaxPvKw
  buildingClass
  targetUtility {
    name
    pid
    id
  }
  ppaType
}
    `
export const SiteVisualsFragmentDoc = `
    fragment SiteVisuals on SiteVisuals {
  solarPanels {
    latitude
    longitude
    angle
  }
}
    `
export const BasicEvCalcFragmentDoc = `
    fragment BasicEVCalc on EVCalc {
  id
  createdAt
  projectInputs {
    projectStartDate
    installType
    termYears
    chargerKw
    yr0NumTenantEvs
    discountRate
  }
  outputs {
    irr
    npv
    yr0NumTenantEvs
    breakevenYear
    numChargersDemandedSeries
    cashflowSeries
    revenueSeries
    incentiveUsd
  }
  numChargers
  optimalNumChargers
  npv
  markupUsdPerKwh
  tariffRate {
    parent {
      name
      code
    }
  }
  utility {
    shortName
  }
  timeZone
  dayRates
  seasonalDayRates {
    name
    startMonth
    endMonth
    dayRates
  }
  downloadHourlyRatesHref
  xlsx
  downloadXlsxHref
  optIters {
    numChargers
    npv
  }
  yr3Revenue
  yr3Cashflow
  yr3NumEvs
  parkingRatio
  year3Expenses {
    label
    value
  }
  installCosts {
    label
    value
  }
}
    `
export const DocumentFragmentDoc = `
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}
    `
export const SetPropertyNameDocument = `
    mutation SetPropertyName($propertyPid: String!, $propertyName: String!) {
  setPropertyName(sitePid: $propertyPid, propertyName: $propertyName) {
    __typename
    ... on PortfolioSite {
      pid
      propertyName
    }
    ... on Error {
      message
    }
  }
}
    `

export const useSetPropertyNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPropertyNameMutation,
    TError,
    Types.SetPropertyNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPropertyNameMutation,
    TError,
    Types.SetPropertyNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetPropertyName"],
    mutationFn: (variables?: Types.SetPropertyNameMutationVariables) =>
      publicFetcher<
        Types.SetPropertyNameMutation,
        Types.SetPropertyNameMutationVariables
      >(SetPropertyNameDocument, variables)(),
    ...options,
  })
}

useSetPropertyNameMutation.fetcher = (
  variables: Types.SetPropertyNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetPropertyNameMutation, Types.SetPropertyNameMutationVariables>(
    SetPropertyNameDocument,
    variables,
    options,
  )

export const TrackEventDocument = `
    mutation TrackEvent($input: RecordUserEventInput!) {
  recordUserEvent(input: $input) {
    __typename
    message
  }
}
    `

export const useTrackEventMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrackEventMutation,
    TError,
    Types.TrackEventMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrackEventMutation,
    TError,
    Types.TrackEventMutationVariables,
    TContext
  >({
    mutationKey: ["TrackEvent"],
    mutationFn: (variables?: Types.TrackEventMutationVariables) =>
      publicFetcher<Types.TrackEventMutation, Types.TrackEventMutationVariables>(
        TrackEventDocument,
        variables,
      )(),
    ...options,
  })
}

useTrackEventMutation.fetcher = (
  variables: Types.TrackEventMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TrackEventMutation, Types.TrackEventMutationVariables>(
    TrackEventDocument,
    variables,
    options,
  )

export const LogPageViewDocument = `
    mutation LogPageView($input: LogPageViewInput!) {
  logPageView(input: $input) {
    __typename
    message
  }
}
    `

export const useLogPageViewMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.LogPageViewMutation,
    TError,
    Types.LogPageViewMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LogPageViewMutation,
    TError,
    Types.LogPageViewMutationVariables,
    TContext
  >({
    mutationKey: ["LogPageView"],
    mutationFn: (variables?: Types.LogPageViewMutationVariables) =>
      publicFetcher<Types.LogPageViewMutation, Types.LogPageViewMutationVariables>(
        LogPageViewDocument,
        variables,
      )(),
    ...options,
  })
}

useLogPageViewMutation.fetcher = (
  variables: Types.LogPageViewMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.LogPageViewMutation, Types.LogPageViewMutationVariables>(
    LogPageViewDocument,
    variables,
    options,
  )

export const AcceptSelectOffersLicenseAgreementDocument = `
    mutation AcceptSelectOffersLicenseAgreement {
  acceptLicenseAgreement(version: 1, agreementType: SELECT_OFFERS) {
    __typename
    message
  }
}
    `

export const useAcceptSelectOffersLicenseAgreementMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AcceptSelectOffersLicenseAgreementMutation,
    TError,
    Types.AcceptSelectOffersLicenseAgreementMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AcceptSelectOffersLicenseAgreementMutation,
    TError,
    Types.AcceptSelectOffersLicenseAgreementMutationVariables,
    TContext
  >({
    mutationKey: ["AcceptSelectOffersLicenseAgreement"],
    mutationFn: (
      variables?: Types.AcceptSelectOffersLicenseAgreementMutationVariables,
    ) =>
      publicFetcher<
        Types.AcceptSelectOffersLicenseAgreementMutation,
        Types.AcceptSelectOffersLicenseAgreementMutationVariables
      >(AcceptSelectOffersLicenseAgreementDocument, variables)(),
    ...options,
  })
}

useAcceptSelectOffersLicenseAgreementMutation.fetcher = (
  variables?: Types.AcceptSelectOffersLicenseAgreementMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AcceptSelectOffersLicenseAgreementMutation,
    Types.AcceptSelectOffersLicenseAgreementMutationVariables
  >(AcceptSelectOffersLicenseAgreementDocument, variables, options)

export const SetOrgSettingsDocument = `
    mutation SetOrgSettings($orgPid: String!, $irrFlavor: IRRType!) {
  setOrgSettings(orgPid: $orgPid, irrFlavor: $irrFlavor) {
    __typename
    ... on Org {
      pid
      irrFlavor
    }
  }
}
    `

export const useSetOrgSettingsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgSettingsMutation,
    TError,
    Types.SetOrgSettingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgSettingsMutation,
    TError,
    Types.SetOrgSettingsMutationVariables,
    TContext
  >({
    mutationKey: ["SetOrgSettings"],
    mutationFn: (variables?: Types.SetOrgSettingsMutationVariables) =>
      publicFetcher<
        Types.SetOrgSettingsMutation,
        Types.SetOrgSettingsMutationVariables
      >(SetOrgSettingsDocument, variables)(),
    ...options,
  })
}

useSetOrgSettingsMutation.fetcher = (
  variables: Types.SetOrgSettingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetOrgSettingsMutation, Types.SetOrgSettingsMutationVariables>(
    SetOrgSettingsDocument,
    variables,
    options,
  )

export const ClaimOpenInviteDocument = `
    mutation ClaimOpenInvite($input: ClaimOpenInviteInput!) {
  claimOpenInvite(input: $input) {
    __typename
    ... on AuthUser {
      ...AuthUser
    }
    ... on Error {
      message
    }
  }
}
    ${AuthUserFragmentDoc}`

export const useClaimOpenInviteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ClaimOpenInviteMutation,
    TError,
    Types.ClaimOpenInviteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ClaimOpenInviteMutation,
    TError,
    Types.ClaimOpenInviteMutationVariables,
    TContext
  >({
    mutationKey: ["ClaimOpenInvite"],
    mutationFn: (variables?: Types.ClaimOpenInviteMutationVariables) =>
      publicFetcher<
        Types.ClaimOpenInviteMutation,
        Types.ClaimOpenInviteMutationVariables
      >(ClaimOpenInviteDocument, variables)(),
    ...options,
  })
}

useClaimOpenInviteMutation.fetcher = (
  variables: Types.ClaimOpenInviteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ClaimOpenInviteMutation, Types.ClaimOpenInviteMutationVariables>(
    ClaimOpenInviteDocument,
    variables,
    options,
  )

export const InviteToOrgDocument = `
    mutation InviteToOrg($input: InviteToOrgInput!) {
  inviteToOrg(input: $input) {
    __typename
    pid
  }
}
    `

export const useInviteToOrgMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToOrgMutation,
    TError,
    Types.InviteToOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToOrgMutation,
    TError,
    Types.InviteToOrgMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToOrg"],
    mutationFn: (variables?: Types.InviteToOrgMutationVariables) =>
      publicFetcher<Types.InviteToOrgMutation, Types.InviteToOrgMutationVariables>(
        InviteToOrgDocument,
        variables,
      )(),
    ...options,
  })
}

useInviteToOrgMutation.fetcher = (
  variables: Types.InviteToOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InviteToOrgMutation, Types.InviteToOrgMutationVariables>(
    InviteToOrgDocument,
    variables,
    options,
  )

export const InviteToPortfolioDocument = `
    mutation InviteToPortfolio($input: InviteToPortfolioInput!) {
  inviteToPortfolio(input: $input) {
    __typename
    ... on Portfolio {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `

export const useInviteToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToPortfolio"],
    mutationFn: (variables?: Types.InviteToPortfolioMutationVariables) =>
      publicFetcher<
        Types.InviteToPortfolioMutation,
        Types.InviteToPortfolioMutationVariables
      >(InviteToPortfolioDocument, variables)(),
    ...options,
  })
}

useInviteToPortfolioMutation.fetcher = (
  variables: Types.InviteToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InviteToPortfolioMutation,
    Types.InviteToPortfolioMutationVariables
  >(InviteToPortfolioDocument, variables, options)

export const CreateAndApplySiteLabelDocument = `
    mutation CreateAndApplySiteLabel($input: CreateAndApplySiteLabelInput!) {
  createAndApplySiteLabel(input: $input) {
    __typename
    ... on Error {
      message
    }
    ... on CreateAndApplySiteLabelOutput {
      label {
        ...SiteLabelDetail
      }
    }
  }
}
    ${SiteLabelDetailFragmentDoc}
${SiteLabelRefFragmentDoc}
${UserRefFragmentDoc}`

export const useCreateAndApplySiteLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["CreateAndApplySiteLabel"],
    mutationFn: (variables?: Types.CreateAndApplySiteLabelMutationVariables) =>
      publicFetcher<
        Types.CreateAndApplySiteLabelMutation,
        Types.CreateAndApplySiteLabelMutationVariables
      >(CreateAndApplySiteLabelDocument, variables)(),
    ...options,
  })
}

useCreateAndApplySiteLabelMutation.fetcher = (
  variables: Types.CreateAndApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateAndApplySiteLabelMutation,
    Types.CreateAndApplySiteLabelMutationVariables
  >(CreateAndApplySiteLabelDocument, variables, options)

export const ApplyLabelToSitesDocument = `
    mutation ApplyLabelToSites($input: ApplyLabelToSitesInput!) {
  applyLabelToSites(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `

export const useApplyLabelToSitesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ApplyLabelToSitesMutation,
    TError,
    Types.ApplyLabelToSitesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApplyLabelToSitesMutation,
    TError,
    Types.ApplyLabelToSitesMutationVariables,
    TContext
  >({
    mutationKey: ["ApplyLabelToSites"],
    mutationFn: (variables?: Types.ApplyLabelToSitesMutationVariables) =>
      publicFetcher<
        Types.ApplyLabelToSitesMutation,
        Types.ApplyLabelToSitesMutationVariables
      >(ApplyLabelToSitesDocument, variables)(),
    ...options,
  })
}

useApplyLabelToSitesMutation.fetcher = (
  variables: Types.ApplyLabelToSitesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ApplyLabelToSitesMutation,
    Types.ApplyLabelToSitesMutationVariables
  >(ApplyLabelToSitesDocument, variables, options)

export const RemoveLabelFromSitesDocument = `
    mutation RemoveLabelFromSites($input: RemoveLabelFromSitesInput!) {
  removeLabelFromSites(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `

export const useRemoveLabelFromSitesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveLabelFromSitesMutation,
    TError,
    Types.RemoveLabelFromSitesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveLabelFromSitesMutation,
    TError,
    Types.RemoveLabelFromSitesMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveLabelFromSites"],
    mutationFn: (variables?: Types.RemoveLabelFromSitesMutationVariables) =>
      publicFetcher<
        Types.RemoveLabelFromSitesMutation,
        Types.RemoveLabelFromSitesMutationVariables
      >(RemoveLabelFromSitesDocument, variables)(),
    ...options,
  })
}

useRemoveLabelFromSitesMutation.fetcher = (
  variables: Types.RemoveLabelFromSitesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveLabelFromSitesMutation,
    Types.RemoveLabelFromSitesMutationVariables
  >(RemoveLabelFromSitesDocument, variables, options)

export const DeleteSiteLabelDocument = `
    mutation DeleteSiteLabel($input: DeleteSiteLabelInput!) {
  deleteSiteLabel(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `

export const useDeleteSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteSiteLabel"],
    mutationFn: (variables?: Types.DeleteSiteLabelMutationVariables) =>
      publicFetcher<
        Types.DeleteSiteLabelMutation,
        Types.DeleteSiteLabelMutationVariables
      >(DeleteSiteLabelDocument, variables)(),
    ...options,
  })
}

useDeleteSiteLabelMutation.fetcher = (
  variables: Types.DeleteSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteSiteLabelMutation, Types.DeleteSiteLabelMutationVariables>(
    DeleteSiteLabelDocument,
    variables,
    options,
  )

export const RenameSiteLabelDocument = `
    mutation RenameSiteLabel($input: RenameSiteLabelInput!) {
  renameSiteLabel(input: $input) {
    __typename
    ... on Error {
      message
    }
    ... on RenameSiteLabelOutput {
      label {
        ...SiteLabelDetail
      }
    }
  }
}
    ${SiteLabelDetailFragmentDoc}
${SiteLabelRefFragmentDoc}
${UserRefFragmentDoc}`

export const useRenameSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RenameSiteLabelMutation,
    TError,
    Types.RenameSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RenameSiteLabelMutation,
    TError,
    Types.RenameSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["RenameSiteLabel"],
    mutationFn: (variables?: Types.RenameSiteLabelMutationVariables) =>
      publicFetcher<
        Types.RenameSiteLabelMutation,
        Types.RenameSiteLabelMutationVariables
      >(RenameSiteLabelDocument, variables)(),
    ...options,
  })
}

useRenameSiteLabelMutation.fetcher = (
  variables: Types.RenameSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RenameSiteLabelMutation, Types.RenameSiteLabelMutationVariables>(
    RenameSiteLabelDocument,
    variables,
    options,
  )

export const ChangeSiteLabelColorDocument = `
    mutation ChangeSiteLabelColor($input: ChangeSiteLabelColorInput!) {
  changeSiteLabelColor(input: $input) {
    __typename
    ... on Error {
      message
    }
    ... on ChangeSiteLabelColorOutput {
      label {
        ...SiteLabelDetail
      }
    }
  }
}
    ${SiteLabelDetailFragmentDoc}
${SiteLabelRefFragmentDoc}
${UserRefFragmentDoc}`

export const useChangeSiteLabelColorMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ChangeSiteLabelColorMutation,
    TError,
    Types.ChangeSiteLabelColorMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ChangeSiteLabelColorMutation,
    TError,
    Types.ChangeSiteLabelColorMutationVariables,
    TContext
  >({
    mutationKey: ["ChangeSiteLabelColor"],
    mutationFn: (variables?: Types.ChangeSiteLabelColorMutationVariables) =>
      publicFetcher<
        Types.ChangeSiteLabelColorMutation,
        Types.ChangeSiteLabelColorMutationVariables
      >(ChangeSiteLabelColorDocument, variables)(),
    ...options,
  })
}

useChangeSiteLabelColorMutation.fetcher = (
  variables: Types.ChangeSiteLabelColorMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ChangeSiteLabelColorMutation,
    Types.ChangeSiteLabelColorMutationVariables
  >(ChangeSiteLabelColorDocument, variables, options)

export const UploadDocumentDocument = `
    mutation UploadDocument($sitePid: String, $bundlePid: String, $bundleParentPid: String, $file: Upload!, $tag: String) {
  documents {
    uploadDocument(
      sitePid: $sitePid
      bundlePid: $bundlePid
      bundleParentPid: $bundleParentPid
      upload: $file
      tag: $tag
    ) {
      __typename
      ... on Document {
        ...Document
        csvPeek
      }
      ... on Error {
        message
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useUploadDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UploadDocumentMutation,
    TError,
    Types.UploadDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UploadDocumentMutation,
    TError,
    Types.UploadDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["UploadDocument"],
    mutationFn: (variables?: Types.UploadDocumentMutationVariables) =>
      publicFetcher<
        Types.UploadDocumentMutation,
        Types.UploadDocumentMutationVariables
      >(UploadDocumentDocument, variables)(),
    ...options,
  })
}

useUploadDocumentMutation.fetcher = (
  variables: Types.UploadDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UploadDocumentMutation, Types.UploadDocumentMutationVariables>(
    UploadDocumentDocument,
    variables,
    options,
  )

export const DeleteDocumentDocument = `
    mutation DeleteDocument($documentPid: String!) {
  documents {
    deleteDocument(documentPid: $documentPid) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `

export const useDeleteDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteDocumentMutation,
    TError,
    Types.DeleteDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteDocumentMutation,
    TError,
    Types.DeleteDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteDocument"],
    mutationFn: (variables?: Types.DeleteDocumentMutationVariables) =>
      publicFetcher<
        Types.DeleteDocumentMutation,
        Types.DeleteDocumentMutationVariables
      >(DeleteDocumentDocument, variables)(),
    ...options,
  })
}

useDeleteDocumentMutation.fetcher = (
  variables: Types.DeleteDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteDocumentMutation, Types.DeleteDocumentMutationVariables>(
    DeleteDocumentDocument,
    variables,
    options,
  )

export const SetDocumentTagDocument = `
    mutation SetDocumentTag($pid: String!, $sitePid: String, $bundlePid: String, $bundleParentPid: String, $tag: DocumentTag!) {
  documents {
    setDocumentTag(
      pid: $pid
      sitePid: $sitePid
      bundlePid: $bundlePid
      bundleParentPid: $bundleParentPid
      tag: $tag
    ) {
      __typename
      ... on Document {
        pid
        tag
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useSetDocumentTagMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetDocumentTagMutation,
    TError,
    Types.SetDocumentTagMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetDocumentTagMutation,
    TError,
    Types.SetDocumentTagMutationVariables,
    TContext
  >({
    mutationKey: ["SetDocumentTag"],
    mutationFn: (variables?: Types.SetDocumentTagMutationVariables) =>
      publicFetcher<
        Types.SetDocumentTagMutation,
        Types.SetDocumentTagMutationVariables
      >(SetDocumentTagDocument, variables)(),
    ...options,
  })
}

useSetDocumentTagMutation.fetcher = (
  variables: Types.SetDocumentTagMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetDocumentTagMutation, Types.SetDocumentTagMutationVariables>(
    SetDocumentTagDocument,
    variables,
    options,
  )

export const OrgSetIsFavoriteDocument = `
    mutation OrgSetIsFavorite($orgSlug: String!, $sitePids: [String!]!, $isFavorite: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    setIsFavorite(sitePids: $sitePids, isFavorite: $isFavorite) {
      message
    }
  }
}
    `

export const useOrgSetIsFavoriteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.OrgSetIsFavoriteMutation,
    TError,
    Types.OrgSetIsFavoriteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.OrgSetIsFavoriteMutation,
    TError,
    Types.OrgSetIsFavoriteMutationVariables,
    TContext
  >({
    mutationKey: ["OrgSetIsFavorite"],
    mutationFn: (variables?: Types.OrgSetIsFavoriteMutationVariables) =>
      publicFetcher<
        Types.OrgSetIsFavoriteMutation,
        Types.OrgSetIsFavoriteMutationVariables
      >(OrgSetIsFavoriteDocument, variables)(),
    ...options,
  })
}

useOrgSetIsFavoriteMutation.fetcher = (
  variables: Types.OrgSetIsFavoriteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OrgSetIsFavoriteMutation,
    Types.OrgSetIsFavoriteMutationVariables
  >(OrgSetIsFavoriteDocument, variables, options)

export const BundleCreateDocument = `
    mutation BundleCreate($orgSlug: String!, $name: String!, $financierPid: String!, $type: FinProductType!, $note: String, $biddingOpenAt: DateTime, $biddingClosedAt: DateTime) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      create(
        name: $name
        financierPid: $financierPid
        type: $type
        note: $note
        biddingOpenAt: $biddingOpenAt
        biddingClosedAt: $biddingClosedAt
      ) {
        ...Bundle
      }
    }
  }
}
    ${BundleFragmentDoc}`

export const useBundleCreateMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleCreateMutation,
    TError,
    Types.BundleCreateMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleCreateMutation,
    TError,
    Types.BundleCreateMutationVariables,
    TContext
  >({
    mutationKey: ["BundleCreate"],
    mutationFn: (variables?: Types.BundleCreateMutationVariables) =>
      publicFetcher<Types.BundleCreateMutation, Types.BundleCreateMutationVariables>(
        BundleCreateDocument,
        variables,
      )(),
    ...options,
  })
}

useBundleCreateMutation.fetcher = (
  variables: Types.BundleCreateMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleCreateMutation, Types.BundleCreateMutationVariables>(
    BundleCreateDocument,
    variables,
    options,
  )

export const BundleUpsertSiteDocument = `
    mutation BundleUpsertSite($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $finproductId: Int) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        upsertSite(sitePid: $sitePid, finproductId: $finproductId) {
          site {
            pid
          }
          scenario {
            pid
          }
          financing {
            id
            name
          }
        }
      }
    }
  }
}
    `

export const useBundleUpsertSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleUpsertSiteMutation,
    TError,
    Types.BundleUpsertSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleUpsertSiteMutation,
    TError,
    Types.BundleUpsertSiteMutationVariables,
    TContext
  >({
    mutationKey: ["BundleUpsertSite"],
    mutationFn: (variables?: Types.BundleUpsertSiteMutationVariables) =>
      publicFetcher<
        Types.BundleUpsertSiteMutation,
        Types.BundleUpsertSiteMutationVariables
      >(BundleUpsertSiteDocument, variables)(),
    ...options,
  })
}

useBundleUpsertSiteMutation.fetcher = (
  variables: Types.BundleUpsertSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleUpsertSiteMutation,
    Types.BundleUpsertSiteMutationVariables
  >(BundleUpsertSiteDocument, variables, options)

export const BundleRemoveSiteDocument = `
    mutation BundleRemoveSite($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        removeSite(sitePid: $sitePid)
      }
    }
  }
}
    `

export const useBundleRemoveSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleRemoveSiteMutation,
    TError,
    Types.BundleRemoveSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleRemoveSiteMutation,
    TError,
    Types.BundleRemoveSiteMutationVariables,
    TContext
  >({
    mutationKey: ["BundleRemoveSite"],
    mutationFn: (variables?: Types.BundleRemoveSiteMutationVariables) =>
      publicFetcher<
        Types.BundleRemoveSiteMutation,
        Types.BundleRemoveSiteMutationVariables
      >(BundleRemoveSiteDocument, variables)(),
    ...options,
  })
}

useBundleRemoveSiteMutation.fetcher = (
  variables: Types.BundleRemoveSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleRemoveSiteMutation,
    Types.BundleRemoveSiteMutationVariables
  >(BundleRemoveSiteDocument, variables, options)

export const BundleArchiveBundleDocument = `
    mutation BundleArchiveBundle($orgSlug: String!, $bundlePid: String!, $archive: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        archiveBundle(archive: $archive)
      }
    }
  }
}
    `

export const useBundleArchiveBundleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleArchiveBundleMutation,
    TError,
    Types.BundleArchiveBundleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleArchiveBundleMutation,
    TError,
    Types.BundleArchiveBundleMutationVariables,
    TContext
  >({
    mutationKey: ["BundleArchiveBundle"],
    mutationFn: (variables?: Types.BundleArchiveBundleMutationVariables) =>
      publicFetcher<
        Types.BundleArchiveBundleMutation,
        Types.BundleArchiveBundleMutationVariables
      >(BundleArchiveBundleDocument, variables)(),
    ...options,
  })
}

useBundleArchiveBundleMutation.fetcher = (
  variables: Types.BundleArchiveBundleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleArchiveBundleMutation,
    Types.BundleArchiveBundleMutationVariables
  >(BundleArchiveBundleDocument, variables, options)

export const SetMeteringDocument = `
    mutation SetMetering($input: SetMeteringInput!) {
  setMetering(input: $input)
}
    `

export const useSetMeteringMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >({
    mutationKey: ["SetMetering"],
    mutationFn: (variables?: Types.SetMeteringMutationVariables) =>
      publicFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
        SetMeteringDocument,
        variables,
      )(),
    ...options,
  })
}

useSetMeteringMutation.fetcher = (
  variables: Types.SetMeteringMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
    SetMeteringDocument,
    variables,
    options,
  )

export const SetSiteShapesDocument = `
    mutation SetSiteShapes($input: SetSiteShapesInput!) {
  setSiteShapes(input: $input) {
    pid
  }
}
    `

export const useSetSiteShapesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteShapesMutation,
    TError,
    Types.SetSiteShapesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteShapesMutation,
    TError,
    Types.SetSiteShapesMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteShapes"],
    mutationFn: (variables?: Types.SetSiteShapesMutationVariables) =>
      publicFetcher<Types.SetSiteShapesMutation, Types.SetSiteShapesMutationVariables>(
        SetSiteShapesDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSiteShapesMutation.fetcher = (
  variables: Types.SetSiteShapesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetSiteShapesMutation, Types.SetSiteShapesMutationVariables>(
    SetSiteShapesDocument,
    variables,
    options,
  )

export const SaveSiteShapesDocument = `
    mutation SaveSiteShapes($input: SaveSiteShapesInput!) {
  saveSiteShapes(input: $input)
}
    `

export const useSaveSiteShapesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SaveSiteShapesMutation,
    TError,
    Types.SaveSiteShapesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SaveSiteShapesMutation,
    TError,
    Types.SaveSiteShapesMutationVariables,
    TContext
  >({
    mutationKey: ["SaveSiteShapes"],
    mutationFn: (variables?: Types.SaveSiteShapesMutationVariables) =>
      publicFetcher<
        Types.SaveSiteShapesMutation,
        Types.SaveSiteShapesMutationVariables
      >(SaveSiteShapesDocument, variables)(),
    ...options,
  })
}

useSaveSiteShapesMutation.fetcher = (
  variables: Types.SaveSiteShapesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SaveSiteShapesMutation, Types.SaveSiteShapesMutationVariables>(
    SaveSiteShapesDocument,
    variables,
    options,
  )

export const UpdatePropertyDataDocument = `
    mutation UpdatePropertyData($input: UpdatePropertyDataInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  updatePropertyData(input: $input, sourceType: $sourceType, sourceId: $sourceId)
}
    `

export const useUpdatePropertyDataMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdatePropertyDataMutation,
    TError,
    Types.UpdatePropertyDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdatePropertyDataMutation,
    TError,
    Types.UpdatePropertyDataMutationVariables,
    TContext
  >({
    mutationKey: ["UpdatePropertyData"],
    mutationFn: (variables?: Types.UpdatePropertyDataMutationVariables) =>
      publicFetcher<
        Types.UpdatePropertyDataMutation,
        Types.UpdatePropertyDataMutationVariables
      >(UpdatePropertyDataDocument, variables)(),
    ...options,
  })
}

useUpdatePropertyDataMutation.fetcher = (
  variables: Types.UpdatePropertyDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdatePropertyDataMutation,
    Types.UpdatePropertyDataMutationVariables
  >(UpdatePropertyDataDocument, variables, options)

export const SetSiteStatusDocument = `
    mutation SetSiteStatus($orgSlug: String!, $sitePids: [String!]!, $status: SiteStatus!, $reason: StatusReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    setStatus(
      status: $status
      sitePids: $sitePids
      reason: $reason
      customReason: $customReason
    ) {
      message
    }
  }
}
    `

export const useSetSiteStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteStatus"],
    mutationFn: (variables?: Types.SetSiteStatusMutationVariables) =>
      publicFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
        SetSiteStatusDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSiteStatusMutation.fetcher = (
  variables: Types.SetSiteStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
    SetSiteStatusDocument,
    variables,
    options,
  )

export const NewCsvImportDocument = `
    mutation NewCsvImport($portfolioPid: String!, $upload: Upload!) {
  newCsvImport(portfolioPid: $portfolioPid, upload: $upload) {
    __typename
    ... on CsvImport {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `

export const useNewCsvImportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.NewCsvImportMutation,
    TError,
    Types.NewCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.NewCsvImportMutation,
    TError,
    Types.NewCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["NewCsvImport"],
    mutationFn: (variables?: Types.NewCsvImportMutationVariables) =>
      publicFetcher<Types.NewCsvImportMutation, Types.NewCsvImportMutationVariables>(
        NewCsvImportDocument,
        variables,
      )(),
    ...options,
  })
}

useNewCsvImportMutation.fetcher = (
  variables: Types.NewCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.NewCsvImportMutation, Types.NewCsvImportMutationVariables>(
    NewCsvImportDocument,
    variables,
    options,
  )

export const SetCsvImportSchemaDocument = `
    mutation SetCsvImportSchema($csvImportPid: String!, $schemaPid: String!) {
  setCsvImportSchema(csvImportPid: $csvImportPid, schemaPid: $schemaPid) {
    __typename
  }
}
    `

export const useSetCsvImportSchemaMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCsvImportSchemaMutation,
    TError,
    Types.SetCsvImportSchemaMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCsvImportSchemaMutation,
    TError,
    Types.SetCsvImportSchemaMutationVariables,
    TContext
  >({
    mutationKey: ["SetCsvImportSchema"],
    mutationFn: (variables?: Types.SetCsvImportSchemaMutationVariables) =>
      publicFetcher<
        Types.SetCsvImportSchemaMutation,
        Types.SetCsvImportSchemaMutationVariables
      >(SetCsvImportSchemaDocument, variables)(),
    ...options,
  })
}

useSetCsvImportSchemaMutation.fetcher = (
  variables: Types.SetCsvImportSchemaMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCsvImportSchemaMutation,
    Types.SetCsvImportSchemaMutationVariables
  >(SetCsvImportSchemaDocument, variables, options)

export const UpdateCsvImportColumnMappingsDocument = `
    mutation UpdateCsvImportColumnMappings($csvImportPid: String!, $columnMappings: ColumnMappingsInput!) {
  updateCsvImportColumnMappings(
    pid: $csvImportPid
    columnMappings: $columnMappings
  ) {
    __typename
    ... on CsvImport {
      pid
      buildingTypes
      guessedBuildingTypeMappings {
        office
        multifamily
        hotel
        hospital
        outpatient
        retailStandalone
        retailStripMall
        quickServiceRestaurant
        fullServiceRestaurant
        primarySchool
        secondarySchool
        singleFamilyDetached
        warehouse
        dataCenter
        coldStorage
        industrial
        parkingLot
        supermarket
      }
    }
    ... on Error {
      message
    }
  }
}
    `

export const useUpdateCsvImportColumnMappingsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateCsvImportColumnMappingsMutation,
    TError,
    Types.UpdateCsvImportColumnMappingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateCsvImportColumnMappingsMutation,
    TError,
    Types.UpdateCsvImportColumnMappingsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateCsvImportColumnMappings"],
    mutationFn: (variables?: Types.UpdateCsvImportColumnMappingsMutationVariables) =>
      publicFetcher<
        Types.UpdateCsvImportColumnMappingsMutation,
        Types.UpdateCsvImportColumnMappingsMutationVariables
      >(UpdateCsvImportColumnMappingsDocument, variables)(),
    ...options,
  })
}

useUpdateCsvImportColumnMappingsMutation.fetcher = (
  variables: Types.UpdateCsvImportColumnMappingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateCsvImportColumnMappingsMutation,
    Types.UpdateCsvImportColumnMappingsMutationVariables
  >(UpdateCsvImportColumnMappingsDocument, variables, options)

export const AddLabelToCsvImportDocument = `
    mutation AddLabelToCsvImport($csvImportPid: String!, $labelPid: String!) {
  addLabelToCsvImport(csvImportPid: $csvImportPid, labelPid: $labelPid) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useAddLabelToCsvImportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddLabelToCsvImportMutation,
    TError,
    Types.AddLabelToCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddLabelToCsvImportMutation,
    TError,
    Types.AddLabelToCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["AddLabelToCsvImport"],
    mutationFn: (variables?: Types.AddLabelToCsvImportMutationVariables) =>
      publicFetcher<
        Types.AddLabelToCsvImportMutation,
        Types.AddLabelToCsvImportMutationVariables
      >(AddLabelToCsvImportDocument, variables)(),
    ...options,
  })
}

useAddLabelToCsvImportMutation.fetcher = (
  variables: Types.AddLabelToCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddLabelToCsvImportMutation,
    Types.AddLabelToCsvImportMutationVariables
  >(AddLabelToCsvImportDocument, variables, options)

export const RemoveLabelFromCsvImportDocument = `
    mutation RemoveLabelFromCsvImport($csvImportPid: String!, $labelPid: String!) {
  removeLabelFromCsvImport(csvImportPid: $csvImportPid, labelPid: $labelPid) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useRemoveLabelFromCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveLabelFromCsvImportMutation,
    TError,
    Types.RemoveLabelFromCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveLabelFromCsvImportMutation,
    TError,
    Types.RemoveLabelFromCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveLabelFromCsvImport"],
    mutationFn: (variables?: Types.RemoveLabelFromCsvImportMutationVariables) =>
      publicFetcher<
        Types.RemoveLabelFromCsvImportMutation,
        Types.RemoveLabelFromCsvImportMutationVariables
      >(RemoveLabelFromCsvImportDocument, variables)(),
    ...options,
  })
}

useRemoveLabelFromCsvImportMutation.fetcher = (
  variables: Types.RemoveLabelFromCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveLabelFromCsvImportMutation,
    Types.RemoveLabelFromCsvImportMutationVariables
  >(RemoveLabelFromCsvImportDocument, variables, options)

export const CreateLabelForCsvImportDocument = `
    mutation CreateLabelForCsvImport($csvImportPid: String!, $labelName: String!, $labelColor: LabelColor) {
  createAndApplyLabelToCsvImport(
    csvImportPid: $csvImportPid
    labelName: $labelName
    labelColor: $labelColor
  ) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useCreateLabelForCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateLabelForCsvImportMutation,
    TError,
    Types.CreateLabelForCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateLabelForCsvImportMutation,
    TError,
    Types.CreateLabelForCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["CreateLabelForCsvImport"],
    mutationFn: (variables?: Types.CreateLabelForCsvImportMutationVariables) =>
      publicFetcher<
        Types.CreateLabelForCsvImportMutation,
        Types.CreateLabelForCsvImportMutationVariables
      >(CreateLabelForCsvImportDocument, variables)(),
    ...options,
  })
}

useCreateLabelForCsvImportMutation.fetcher = (
  variables: Types.CreateLabelForCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateLabelForCsvImportMutation,
    Types.CreateLabelForCsvImportMutationVariables
  >(CreateLabelForCsvImportDocument, variables, options)

export const DeleteLabelForCsvImportDocument = `
    mutation DeleteLabelForCsvImport($csvImportPid: String!, $labelPid: String!) {
  deleteLabelAndRemoveFromCsvImport(
    csvImportPid: $csvImportPid
    labelPid: $labelPid
  ) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useDeleteLabelForCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.DeleteLabelForCsvImportMutation,
    TError,
    Types.DeleteLabelForCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteLabelForCsvImportMutation,
    TError,
    Types.DeleteLabelForCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteLabelForCsvImport"],
    mutationFn: (variables?: Types.DeleteLabelForCsvImportMutationVariables) =>
      publicFetcher<
        Types.DeleteLabelForCsvImportMutation,
        Types.DeleteLabelForCsvImportMutationVariables
      >(DeleteLabelForCsvImportDocument, variables)(),
    ...options,
  })
}

useDeleteLabelForCsvImportMutation.fetcher = (
  variables: Types.DeleteLabelForCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteLabelForCsvImportMutation,
    Types.DeleteLabelForCsvImportMutationVariables
  >(DeleteLabelForCsvImportDocument, variables, options)

export const UpdateDoeReferenceTypeMappingsDocument = `
    mutation UpdateDoeReferenceTypeMappings($csvImportPid: String!, $mappings: DoeReferenceTypeMappingsInput!) {
  updateDoeReferenceTypeMappings(csvImportPid: $csvImportPid, mappings: $mappings) {
    __typename
    ... on CsvImport {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `

export const useUpdateDoeReferenceTypeMappingsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateDoeReferenceTypeMappingsMutation,
    TError,
    Types.UpdateDoeReferenceTypeMappingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateDoeReferenceTypeMappingsMutation,
    TError,
    Types.UpdateDoeReferenceTypeMappingsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateDoeReferenceTypeMappings"],
    mutationFn: (variables?: Types.UpdateDoeReferenceTypeMappingsMutationVariables) =>
      publicFetcher<
        Types.UpdateDoeReferenceTypeMappingsMutation,
        Types.UpdateDoeReferenceTypeMappingsMutationVariables
      >(UpdateDoeReferenceTypeMappingsDocument, variables)(),
    ...options,
  })
}

useUpdateDoeReferenceTypeMappingsMutation.fetcher = (
  variables: Types.UpdateDoeReferenceTypeMappingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateDoeReferenceTypeMappingsMutation,
    Types.UpdateDoeReferenceTypeMappingsMutationVariables
  >(UpdateDoeReferenceTypeMappingsDocument, variables, options)

export const DropCsvImportRowDocument = `
    mutation DropCsvImportRow($rowId: Int!) {
  dropCsvImportRow(rowId: $rowId) {
    id
    droppedAt
  }
}
    `

export const useDropCsvImportRowMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DropCsvImportRowMutation,
    TError,
    Types.DropCsvImportRowMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DropCsvImportRowMutation,
    TError,
    Types.DropCsvImportRowMutationVariables,
    TContext
  >({
    mutationKey: ["DropCsvImportRow"],
    mutationFn: (variables?: Types.DropCsvImportRowMutationVariables) =>
      publicFetcher<
        Types.DropCsvImportRowMutation,
        Types.DropCsvImportRowMutationVariables
      >(DropCsvImportRowDocument, variables)(),
    ...options,
  })
}

useDropCsvImportRowMutation.fetcher = (
  variables: Types.DropCsvImportRowMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DropCsvImportRowMutation,
    Types.DropCsvImportRowMutationVariables
  >(DropCsvImportRowDocument, variables, options)

export const AmendCsvImportRowDocument = `
    mutation AmendCsvImportRow($rowId: Int!, $amendments: BuildingDataAmendmentsInput!) {
  amendCsvImportRow(rowId: $rowId, amendments: $amendments) {
    id
    amendments {
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
  }
}
    `

export const useAmendCsvImportRowMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AmendCsvImportRowMutation,
    TError,
    Types.AmendCsvImportRowMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AmendCsvImportRowMutation,
    TError,
    Types.AmendCsvImportRowMutationVariables,
    TContext
  >({
    mutationKey: ["AmendCsvImportRow"],
    mutationFn: (variables?: Types.AmendCsvImportRowMutationVariables) =>
      publicFetcher<
        Types.AmendCsvImportRowMutation,
        Types.AmendCsvImportRowMutationVariables
      >(AmendCsvImportRowDocument, variables)(),
    ...options,
  })
}

useAmendCsvImportRowMutation.fetcher = (
  variables: Types.AmendCsvImportRowMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AmendCsvImportRowMutation,
    Types.AmendCsvImportRowMutationVariables
  >(AmendCsvImportRowDocument, variables, options)

export const SetCsvImportRowBuildingDataDocument = `
    mutation SetCsvImportRowBuildingData($rowId: Int!, $buildingDataId: Int!) {
  setCsvImportRowBuildingData(rowId: $rowId, buildingDataId: $buildingDataId) {
    id
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
  }
}
    `

export const useSetCsvImportRowBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCsvImportRowBuildingDataMutation,
    TError,
    Types.SetCsvImportRowBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCsvImportRowBuildingDataMutation,
    TError,
    Types.SetCsvImportRowBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["SetCsvImportRowBuildingData"],
    mutationFn: (variables?: Types.SetCsvImportRowBuildingDataMutationVariables) =>
      publicFetcher<
        Types.SetCsvImportRowBuildingDataMutation,
        Types.SetCsvImportRowBuildingDataMutationVariables
      >(SetCsvImportRowBuildingDataDocument, variables)(),
    ...options,
  })
}

useSetCsvImportRowBuildingDataMutation.fetcher = (
  variables: Types.SetCsvImportRowBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCsvImportRowBuildingDataMutation,
    Types.SetCsvImportRowBuildingDataMutationVariables
  >(SetCsvImportRowBuildingDataDocument, variables, options)

export const UnsetCsvImportRowBuildingDataDocument = `
    mutation UnsetCsvImportRowBuildingData($rowId: Int!) {
  unsetCsvImportRowBuildingData(rowId: $rowId) {
    id
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    amendments {
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
  }
}
    `

export const useUnsetCsvImportRowBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UnsetCsvImportRowBuildingDataMutation,
    TError,
    Types.UnsetCsvImportRowBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UnsetCsvImportRowBuildingDataMutation,
    TError,
    Types.UnsetCsvImportRowBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["UnsetCsvImportRowBuildingData"],
    mutationFn: (variables?: Types.UnsetCsvImportRowBuildingDataMutationVariables) =>
      publicFetcher<
        Types.UnsetCsvImportRowBuildingDataMutation,
        Types.UnsetCsvImportRowBuildingDataMutationVariables
      >(UnsetCsvImportRowBuildingDataDocument, variables)(),
    ...options,
  })
}

useUnsetCsvImportRowBuildingDataMutation.fetcher = (
  variables: Types.UnsetCsvImportRowBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UnsetCsvImportRowBuildingDataMutation,
    Types.UnsetCsvImportRowBuildingDataMutationVariables
  >(UnsetCsvImportRowBuildingDataDocument, variables, options)

export const UpdateSiteConfigDocument = `
    mutation UpdateSiteConfig($sitePid: String!, $finproductId: Int!, $scenarioPid: String!) {
  siteConfig {
    updateSelectedConfig(
      sitePid: $sitePid
      finproductId: $finproductId
      scenarioPid: $scenarioPid
    )
  }
}
    `

export const useUpdateSiteConfigMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateSiteConfigMutation,
    TError,
    Types.UpdateSiteConfigMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateSiteConfigMutation,
    TError,
    Types.UpdateSiteConfigMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateSiteConfig"],
    mutationFn: (variables?: Types.UpdateSiteConfigMutationVariables) =>
      publicFetcher<
        Types.UpdateSiteConfigMutation,
        Types.UpdateSiteConfigMutationVariables
      >(UpdateSiteConfigDocument, variables)(),
    ...options,
  })
}

useUpdateSiteConfigMutation.fetcher = (
  variables: Types.UpdateSiteConfigMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateSiteConfigMutation,
    Types.UpdateSiteConfigMutationVariables
  >(UpdateSiteConfigDocument, variables, options)

export const RequestBidsDocument = `
    mutation RequestBids($input: RequestBidsInput!) {
  requestBids(input: $input)
}
    `

export const useRequestBidsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RequestBidsMutation,
    TError,
    Types.RequestBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RequestBidsMutation,
    TError,
    Types.RequestBidsMutationVariables,
    TContext
  >({
    mutationKey: ["RequestBids"],
    mutationFn: (variables?: Types.RequestBidsMutationVariables) =>
      publicFetcher<Types.RequestBidsMutation, Types.RequestBidsMutationVariables>(
        RequestBidsDocument,
        variables,
      )(),
    ...options,
  })
}

useRequestBidsMutation.fetcher = (
  variables: Types.RequestBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RequestBidsMutation, Types.RequestBidsMutationVariables>(
    RequestBidsDocument,
    variables,
    options,
  )

export const CreateThirdPartySupplierDocument = `
    mutation CreateThirdPartySupplier($name: String!) {
  createThirdPartySupplier(name: $name) {
    id
    pid
    name
  }
}
    `

export const useCreateThirdPartySupplierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateThirdPartySupplierMutation,
    TError,
    Types.CreateThirdPartySupplierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateThirdPartySupplierMutation,
    TError,
    Types.CreateThirdPartySupplierMutationVariables,
    TContext
  >({
    mutationKey: ["CreateThirdPartySupplier"],
    mutationFn: (variables?: Types.CreateThirdPartySupplierMutationVariables) =>
      publicFetcher<
        Types.CreateThirdPartySupplierMutation,
        Types.CreateThirdPartySupplierMutationVariables
      >(CreateThirdPartySupplierDocument, variables)(),
    ...options,
  })
}

useCreateThirdPartySupplierMutation.fetcher = (
  variables: Types.CreateThirdPartySupplierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateThirdPartySupplierMutation,
    Types.CreateThirdPartySupplierMutationVariables
  >(CreateThirdPartySupplierDocument, variables, options)

export const AddMeterDocument = `
    mutation AddMeter($sitePid: String!, $loadCategory: LoadCategory!, $label: String, $doeReferenceType: BuildingType, $tariffPid: String) {
  billingMeters(sitePid: $sitePid) {
    addMeter(
      loadCategory: $loadCategory
      label: $label
      doeReferenceType: $doeReferenceType
      tariffPid: $tariffPid
    ) {
      ...MutBillingMeter
    }
  }
}
    ${MutBillingMeterFragmentDoc}`

export const useAddMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddMeterMutation,
    TError,
    Types.AddMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddMeterMutation,
    TError,
    Types.AddMeterMutationVariables,
    TContext
  >({
    mutationKey: ["AddMeter"],
    mutationFn: (variables?: Types.AddMeterMutationVariables) =>
      publicFetcher<Types.AddMeterMutation, Types.AddMeterMutationVariables>(
        AddMeterDocument,
        variables,
      )(),
    ...options,
  })
}

useAddMeterMutation.fetcher = (
  variables: Types.AddMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddMeterMutation, Types.AddMeterMutationVariables>(
    AddMeterDocument,
    variables,
    options,
  )

export const EditBillingMeterAnnualDocument = `
    mutation EditBillingMeterAnnual($sitePid: String!, $meterPid: String!, $kwh: Float, $kw: Float, $costUsd: Float) {
  billingMeters(sitePid: $sitePid) {
    editAnnual(meterPid: $meterPid, kwh: $kwh, kw: $kw, costUsd: $costUsd) {
      ...MutBillingMeter
    }
  }
}
    ${MutBillingMeterFragmentDoc}`

export const useEditBillingMeterAnnualMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterAnnualMutation,
    TError,
    Types.EditBillingMeterAnnualMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterAnnualMutation,
    TError,
    Types.EditBillingMeterAnnualMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterAnnual"],
    mutationFn: (variables?: Types.EditBillingMeterAnnualMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterAnnualMutation,
        Types.EditBillingMeterAnnualMutationVariables
      >(EditBillingMeterAnnualDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterAnnualMutation.fetcher = (
  variables: Types.EditBillingMeterAnnualMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterAnnualMutation,
    Types.EditBillingMeterAnnualMutationVariables
  >(EditBillingMeterAnnualDocument, variables, options)

export const EditBillingMeterLabelDocument = `
    mutation EditBillingMeterLabel($sitePid: String!, $meterPid: String!, $label: String!) {
  billingMeters(sitePid: $sitePid) {
    editLabel(meterPid: $meterPid, label: $label) {
      ...MutBillingMeter
    }
  }
}
    ${MutBillingMeterFragmentDoc}`

export const useEditBillingMeterLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterLabelMutation,
    TError,
    Types.EditBillingMeterLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterLabelMutation,
    TError,
    Types.EditBillingMeterLabelMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterLabel"],
    mutationFn: (variables?: Types.EditBillingMeterLabelMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterLabelMutation,
        Types.EditBillingMeterLabelMutationVariables
      >(EditBillingMeterLabelDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterLabelMutation.fetcher = (
  variables: Types.EditBillingMeterLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterLabelMutation,
    Types.EditBillingMeterLabelMutationVariables
  >(EditBillingMeterLabelDocument, variables, options)

export const EditBillingMeterDoeReferenceTypeDocument = `
    mutation EditBillingMeterDoeReferenceType($sitePid: String!, $meterPid: String!, $doeReferenceType: BuildingType!) {
  billingMeters(sitePid: $sitePid) {
    editDoeReferenceType(meterPid: $meterPid, doeReferenceType: $doeReferenceType) {
      __typename
    }
  }
}
    `

export const useEditBillingMeterDoeReferenceTypeMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EditBillingMeterDoeReferenceTypeMutation,
    TError,
    Types.EditBillingMeterDoeReferenceTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterDoeReferenceTypeMutation,
    TError,
    Types.EditBillingMeterDoeReferenceTypeMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterDoeReferenceType"],
    mutationFn: (variables?: Types.EditBillingMeterDoeReferenceTypeMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterDoeReferenceTypeMutation,
        Types.EditBillingMeterDoeReferenceTypeMutationVariables
      >(EditBillingMeterDoeReferenceTypeDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterDoeReferenceTypeMutation.fetcher = (
  variables: Types.EditBillingMeterDoeReferenceTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterDoeReferenceTypeMutation,
    Types.EditBillingMeterDoeReferenceTypeMutationVariables
  >(EditBillingMeterDoeReferenceTypeDocument, variables, options)

export const EditBillingMeterTariffDocument = `
    mutation EditBillingMeterTariff($sitePid: String!, $meterPid: String!, $tariffPid: String!) {
  billingMeters(sitePid: $sitePid) {
    editBauTariff(meterPid: $meterPid, tariffPid: $tariffPid) {
      __typename
    }
  }
}
    `

export const useEditBillingMeterTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterTariffMutation,
    TError,
    Types.EditBillingMeterTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterTariffMutation,
    TError,
    Types.EditBillingMeterTariffMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterTariff"],
    mutationFn: (variables?: Types.EditBillingMeterTariffMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterTariffMutation,
        Types.EditBillingMeterTariffMutationVariables
      >(EditBillingMeterTariffDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterTariffMutation.fetcher = (
  variables: Types.EditBillingMeterTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterTariffMutation,
    Types.EditBillingMeterTariffMutationVariables
  >(EditBillingMeterTariffDocument, variables, options)

export const AddBillingMeterIntervalsDocument = `
    mutation AddBillingMeterIntervals($sitePid: String!, $input: BillingIntervalsExtraction!) {
  billingMeters(sitePid: $sitePid) {
    addIntervals(input: $input)
  }
}
    `

export const useAddBillingMeterIntervalsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBillingMeterIntervalsMutation,
    TError,
    Types.AddBillingMeterIntervalsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBillingMeterIntervalsMutation,
    TError,
    Types.AddBillingMeterIntervalsMutationVariables,
    TContext
  >({
    mutationKey: ["AddBillingMeterIntervals"],
    mutationFn: (variables?: Types.AddBillingMeterIntervalsMutationVariables) =>
      publicFetcher<
        Types.AddBillingMeterIntervalsMutation,
        Types.AddBillingMeterIntervalsMutationVariables
      >(AddBillingMeterIntervalsDocument, variables)(),
    ...options,
  })
}

useAddBillingMeterIntervalsMutation.fetcher = (
  variables: Types.AddBillingMeterIntervalsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBillingMeterIntervalsMutation,
    Types.AddBillingMeterIntervalsMutationVariables
  >(AddBillingMeterIntervalsDocument, variables, options)

export const AddBillingMeterMonthliesDocument = `
    mutation AddBillingMeterMonthlies($sitePid: String!, $input: MonthlyExtraction!) {
  billingMeters(sitePid: $sitePid) {
    addMonthlies(input: $input)
  }
}
    `

export const useAddBillingMeterMonthliesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBillingMeterMonthliesMutation,
    TError,
    Types.AddBillingMeterMonthliesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBillingMeterMonthliesMutation,
    TError,
    Types.AddBillingMeterMonthliesMutationVariables,
    TContext
  >({
    mutationKey: ["AddBillingMeterMonthlies"],
    mutationFn: (variables?: Types.AddBillingMeterMonthliesMutationVariables) =>
      publicFetcher<
        Types.AddBillingMeterMonthliesMutation,
        Types.AddBillingMeterMonthliesMutationVariables
      >(AddBillingMeterMonthliesDocument, variables)(),
    ...options,
  })
}

useAddBillingMeterMonthliesMutation.fetcher = (
  variables: Types.AddBillingMeterMonthliesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBillingMeterMonthliesMutation,
    Types.AddBillingMeterMonthliesMutationVariables
  >(AddBillingMeterMonthliesDocument, variables, options)

export const BillingMeterEditTenantDetailsDocument = `
    mutation BillingMeterEditTenantDetails($sitePid: String!, $meterPid: String!, $input: MFUnitBreakdownInput!) {
  billingMeters(sitePid: $sitePid) {
    editTenantDetails(meterPid: $meterPid, input: $input)
  }
}
    `

export const useBillingMeterEditTenantDetailsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.BillingMeterEditTenantDetailsMutation,
    TError,
    Types.BillingMeterEditTenantDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BillingMeterEditTenantDetailsMutation,
    TError,
    Types.BillingMeterEditTenantDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["BillingMeterEditTenantDetails"],
    mutationFn: (variables?: Types.BillingMeterEditTenantDetailsMutationVariables) =>
      publicFetcher<
        Types.BillingMeterEditTenantDetailsMutation,
        Types.BillingMeterEditTenantDetailsMutationVariables
      >(BillingMeterEditTenantDetailsDocument, variables)(),
    ...options,
  })
}

useBillingMeterEditTenantDetailsMutation.fetcher = (
  variables: Types.BillingMeterEditTenantDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BillingMeterEditTenantDetailsMutation,
    Types.BillingMeterEditTenantDetailsMutationVariables
  >(BillingMeterEditTenantDetailsDocument, variables, options)

export const RemoveBillingMeterIntervalsDocument = `
    mutation RemoveBillingMeterIntervals($sitePid: String!, $meterPid: String!, $intervalsId: Int!) {
  billingMeters(sitePid: $sitePid) {
    removeIntervals(meterPid: $meterPid, intervalsId: $intervalsId)
  }
}
    `

export const useRemoveBillingMeterIntervalsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBillingMeterIntervalsMutation,
    TError,
    Types.RemoveBillingMeterIntervalsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBillingMeterIntervalsMutation,
    TError,
    Types.RemoveBillingMeterIntervalsMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBillingMeterIntervals"],
    mutationFn: (variables?: Types.RemoveBillingMeterIntervalsMutationVariables) =>
      publicFetcher<
        Types.RemoveBillingMeterIntervalsMutation,
        Types.RemoveBillingMeterIntervalsMutationVariables
      >(RemoveBillingMeterIntervalsDocument, variables)(),
    ...options,
  })
}

useRemoveBillingMeterIntervalsMutation.fetcher = (
  variables: Types.RemoveBillingMeterIntervalsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBillingMeterIntervalsMutation,
    Types.RemoveBillingMeterIntervalsMutationVariables
  >(RemoveBillingMeterIntervalsDocument, variables, options)

export const SetArchivedBillingMeterDocument = `
    mutation SetArchivedBillingMeter($sitePid: String!, $meterPid: String!, $archived: Boolean!) {
  billingMeters(sitePid: $sitePid) {
    editArchived(meterPid: $meterPid, archived: $archived) {
      __typename
    }
  }
}
    `

export const useSetArchivedBillingMeterMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetArchivedBillingMeterMutation,
    TError,
    Types.SetArchivedBillingMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchivedBillingMeterMutation,
    TError,
    Types.SetArchivedBillingMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchivedBillingMeter"],
    mutationFn: (variables?: Types.SetArchivedBillingMeterMutationVariables) =>
      publicFetcher<
        Types.SetArchivedBillingMeterMutation,
        Types.SetArchivedBillingMeterMutationVariables
      >(SetArchivedBillingMeterDocument, variables)(),
    ...options,
  })
}

useSetArchivedBillingMeterMutation.fetcher = (
  variables: Types.SetArchivedBillingMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchivedBillingMeterMutation,
    Types.SetArchivedBillingMeterMutationVariables
  >(SetArchivedBillingMeterDocument, variables, options)

export const EditBillingMeterMonthDocument = `
    mutation EditBillingMeterMonth($sitePid: String!, $meterPid: String!, $start: Date!, $end: Date!, $totalKwh: Float!, $peakKw: Float, $totalCostUsd: Float, $supplyCostUsd: Float, $transmissionCostUsd: Float) {
  billingMeters(sitePid: $sitePid) {
    editMonth(
      meterPid: $meterPid
      start: $start
      end: $end
      totalKwh: $totalKwh
      peakKw: $peakKw
      totalCostUsd: $totalCostUsd
      supplyCostUsd: $supplyCostUsd
      transmissionCostUsd: $transmissionCostUsd
    ) {
      __typename
    }
  }
}
    `

export const useEditBillingMeterMonthMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterMonthMutation,
    TError,
    Types.EditBillingMeterMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterMonthMutation,
    TError,
    Types.EditBillingMeterMonthMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterMonth"],
    mutationFn: (variables?: Types.EditBillingMeterMonthMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterMonthMutation,
        Types.EditBillingMeterMonthMutationVariables
      >(EditBillingMeterMonthDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterMonthMutation.fetcher = (
  variables: Types.EditBillingMeterMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterMonthMutation,
    Types.EditBillingMeterMonthMutationVariables
  >(EditBillingMeterMonthDocument, variables, options)

export const RemoveBillingMeterMonthDocument = `
    mutation RemoveBillingMeterMonth($sitePid: String!, $meterPid: String!, $monthId: Int!) {
  billingMeters(sitePid: $sitePid) {
    removeMonth(meterPid: $meterPid, monthId: $monthId) {
      __typename
    }
  }
}
    `

export const useRemoveBillingMeterMonthMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBillingMeterMonthMutation,
    TError,
    Types.RemoveBillingMeterMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBillingMeterMonthMutation,
    TError,
    Types.RemoveBillingMeterMonthMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBillingMeterMonth"],
    mutationFn: (variables?: Types.RemoveBillingMeterMonthMutationVariables) =>
      publicFetcher<
        Types.RemoveBillingMeterMonthMutation,
        Types.RemoveBillingMeterMonthMutationVariables
      >(RemoveBillingMeterMonthDocument, variables)(),
    ...options,
  })
}

useRemoveBillingMeterMonthMutation.fetcher = (
  variables: Types.RemoveBillingMeterMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBillingMeterMonthMutation,
    Types.RemoveBillingMeterMonthMutationVariables
  >(RemoveBillingMeterMonthDocument, variables, options)

export const MoveDocumentBillingMonthDocument = `
    mutation MoveDocumentBillingMonth($sitePid: String!, $billingMonthId: Int!, $kind: BillType!, $fromMeterPid: String, $toMeterPid: String) {
  billingMeters(sitePid: $sitePid) {
    moveDocumentBillingMonth(
      dbmId: $billingMonthId
      kind: $kind
      fromMeterPid: $fromMeterPid
      toMeterPid: $toMeterPid
    )
  }
}
    `

export const useMoveDocumentBillingMonthMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.MoveDocumentBillingMonthMutation,
    TError,
    Types.MoveDocumentBillingMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MoveDocumentBillingMonthMutation,
    TError,
    Types.MoveDocumentBillingMonthMutationVariables,
    TContext
  >({
    mutationKey: ["MoveDocumentBillingMonth"],
    mutationFn: (variables?: Types.MoveDocumentBillingMonthMutationVariables) =>
      publicFetcher<
        Types.MoveDocumentBillingMonthMutation,
        Types.MoveDocumentBillingMonthMutationVariables
      >(MoveDocumentBillingMonthDocument, variables)(),
    ...options,
  })
}

useMoveDocumentBillingMonthMutation.fetcher = (
  variables: Types.MoveDocumentBillingMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MoveDocumentBillingMonthMutation,
    Types.MoveDocumentBillingMonthMutationVariables
  >(MoveDocumentBillingMonthDocument, variables, options)

export const CreateBuildingDataFromGooglePlaceDocument = `
    mutation CreateBuildingDataFromGooglePlace($placeId: String!) {
  buildingData {
    createFromGooglePlace(placeId: $placeId) {
      __typename
      ... on BuildingData {
        id
        fullAddress
        buildingType
        propertyName
        roofSqft
        sqft
        numStories
        latitude
        longitude
        buildingFootprintGeojson
        yearBuilt
        numUnitsTotal
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreateBuildingDataFromGooglePlaceMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    TError,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    TError,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBuildingDataFromGooglePlace"],
    mutationFn: (
      variables?: Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    ) =>
      publicFetcher<
        Types.CreateBuildingDataFromGooglePlaceMutation,
        Types.CreateBuildingDataFromGooglePlaceMutationVariables
      >(CreateBuildingDataFromGooglePlaceDocument, variables)(),
    ...options,
  })
}

useCreateBuildingDataFromGooglePlaceMutation.fetcher = (
  variables: Types.CreateBuildingDataFromGooglePlaceMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables
  >(CreateBuildingDataFromGooglePlaceDocument, variables, options)

export const CreateSiteFromBuildingDataDocument = `
    mutation CreateSiteFromBuildingData($portfolioPid: String!, $buildingDataId: Int!, $amendments: BuildingDataAmendmentsInput!) {
  buildingData {
    createSite(
      portfolioPid: $portfolioPid
      buildingDataId: $buildingDataId
      amendments: $amendments
    ) {
      __typename
      ... on SiteRef {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreateSiteFromBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateSiteFromBuildingDataMutation,
    TError,
    Types.CreateSiteFromBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateSiteFromBuildingDataMutation,
    TError,
    Types.CreateSiteFromBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["CreateSiteFromBuildingData"],
    mutationFn: (variables?: Types.CreateSiteFromBuildingDataMutationVariables) =>
      publicFetcher<
        Types.CreateSiteFromBuildingDataMutation,
        Types.CreateSiteFromBuildingDataMutationVariables
      >(CreateSiteFromBuildingDataDocument, variables)(),
    ...options,
  })
}

useCreateSiteFromBuildingDataMutation.fetcher = (
  variables: Types.CreateSiteFromBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateSiteFromBuildingDataMutation,
    Types.CreateSiteFromBuildingDataMutationVariables
  >(CreateSiteFromBuildingDataDocument, variables, options)

export const CreateBundleParentDocument = `
    mutation CreateBundleParent($orgSlug: String!, $name: String!, $type: FinProductType!, $note: String!, $pricingNote: String!, $biddingOpenAt: DateTime, $biddingClosedAt: DateTime) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      createParent(
        name: $name
        type: $type
        note: $note
        pricingNote: $pricingNote
        biddingOpenAt: $biddingOpenAt
        biddingClosedAt: $biddingClosedAt
      ) {
        id
        pid
        name
      }
    }
  }
}
    `

export const useCreateBundleParentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateBundleParentMutation,
    TError,
    Types.CreateBundleParentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBundleParentMutation,
    TError,
    Types.CreateBundleParentMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBundleParent"],
    mutationFn: (variables?: Types.CreateBundleParentMutationVariables) =>
      publicFetcher<
        Types.CreateBundleParentMutation,
        Types.CreateBundleParentMutationVariables
      >(CreateBundleParentDocument, variables)(),
    ...options,
  })
}

useCreateBundleParentMutation.fetcher = (
  variables: Types.CreateBundleParentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBundleParentMutation,
    Types.CreateBundleParentMutationVariables
  >(CreateBundleParentDocument, variables, options)

export const EditBundleParentDocument = `
    mutation EditBundleParent($orgSlug: String!, $bundleParentPid: String!, $name: String!, $note: String!, $pricingNote: String!, $biddingOpenAt: DateTime, $biddingClosedAt: DateTime) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setName(name: $name)
        setNote(note: $note)
        setPricingNote(pricingNote: $pricingNote)
        setBiddingOpenAt(biddingOpenAt: $biddingOpenAt)
        setBiddingClosedAt(biddingClosedAt: $biddingClosedAt)
      }
    }
  }
}
    `

export const useEditBundleParentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBundleParentMutation,
    TError,
    Types.EditBundleParentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBundleParentMutation,
    TError,
    Types.EditBundleParentMutationVariables,
    TContext
  >({
    mutationKey: ["EditBundleParent"],
    mutationFn: (variables?: Types.EditBundleParentMutationVariables) =>
      publicFetcher<
        Types.EditBundleParentMutation,
        Types.EditBundleParentMutationVariables
      >(EditBundleParentDocument, variables)(),
    ...options,
  })
}

useEditBundleParentMutation.fetcher = (
  variables: Types.EditBundleParentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBundleParentMutation,
    Types.EditBundleParentMutationVariables
  >(EditBundleParentDocument, variables, options)

export const AddBundleParentFinancierDocument = `
    mutation AddBundleParentFinancier($orgSlug: String!, $bundleParentPid: String!, $financierPids: [String!]!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addFinanciers(financierPids: $financierPids)
      }
    }
  }
}
    `

export const useAddBundleParentFinancierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBundleParentFinancierMutation,
    TError,
    Types.AddBundleParentFinancierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentFinancierMutation,
    TError,
    Types.AddBundleParentFinancierMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentFinancier"],
    mutationFn: (variables?: Types.AddBundleParentFinancierMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentFinancierMutation,
        Types.AddBundleParentFinancierMutationVariables
      >(AddBundleParentFinancierDocument, variables)(),
    ...options,
  })
}

useAddBundleParentFinancierMutation.fetcher = (
  variables: Types.AddBundleParentFinancierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentFinancierMutation,
    Types.AddBundleParentFinancierMutationVariables
  >(AddBundleParentFinancierDocument, variables, options)

export const AddBundleParentSiteDocument = `
    mutation AddBundleParentSite($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $projectParams: ProjectParamsInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        upsertSite(sitePid: $sitePid, projectParams: $projectParams)
      }
    }
  }
}
    `

export const useAddBundleParentSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleParentSiteMutation,
    TError,
    Types.AddBundleParentSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentSiteMutation,
    TError,
    Types.AddBundleParentSiteMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentSite"],
    mutationFn: (variables?: Types.AddBundleParentSiteMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentSiteMutation,
        Types.AddBundleParentSiteMutationVariables
      >(AddBundleParentSiteDocument, variables)(),
    ...options,
  })
}

useAddBundleParentSiteMutation.fetcher = (
  variables: Types.AddBundleParentSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentSiteMutation,
    Types.AddBundleParentSiteMutationVariables
  >(AddBundleParentSiteDocument, variables, options)

export const CreateBundleParentSystemDocument = `
    mutation CreateBundleParentSystem($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $input: AddSystemInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addSystem(sitePid: $sitePid, input: $input) {
          system {
            pid
          }
          lite {
            pid
          }
        }
      }
    }
  }
}
    `

export const useCreateBundleParentSystemMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateBundleParentSystemMutation,
    TError,
    Types.CreateBundleParentSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBundleParentSystemMutation,
    TError,
    Types.CreateBundleParentSystemMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBundleParentSystem"],
    mutationFn: (variables?: Types.CreateBundleParentSystemMutationVariables) =>
      publicFetcher<
        Types.CreateBundleParentSystemMutation,
        Types.CreateBundleParentSystemMutationVariables
      >(CreateBundleParentSystemDocument, variables)(),
    ...options,
  })
}

useCreateBundleParentSystemMutation.fetcher = (
  variables: Types.CreateBundleParentSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBundleParentSystemMutation,
    Types.CreateBundleParentSystemMutationVariables
  >(CreateBundleParentSystemDocument, variables, options)

export const RemoveBundleParentSiteDocument = `
    mutation RemoveBundleParentSite($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        removeSite(sitePid: $sitePid)
      }
    }
  }
}
    `

export const useRemoveBundleParentSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveBundleParentSiteMutation,
    TError,
    Types.RemoveBundleParentSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBundleParentSiteMutation,
    TError,
    Types.RemoveBundleParentSiteMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBundleParentSite"],
    mutationFn: (variables?: Types.RemoveBundleParentSiteMutationVariables) =>
      publicFetcher<
        Types.RemoveBundleParentSiteMutation,
        Types.RemoveBundleParentSiteMutationVariables
      >(RemoveBundleParentSiteDocument, variables)(),
    ...options,
  })
}

useRemoveBundleParentSiteMutation.fetcher = (
  variables: Types.RemoveBundleParentSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBundleParentSiteMutation,
    Types.RemoveBundleParentSiteMutationVariables
  >(RemoveBundleParentSiteDocument, variables, options)

export const AddInvestorBidDocument = `
    mutation AddInvestorBid($orgSlug: String!, $bundlePid: String!, $input: InvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addInvestorBid(input: $input) {
        ppa {
          pid
        }
        cs {
          pid
        }
      }
    }
  }
}
    `

export const useAddInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddInvestorBidMutation,
    TError,
    Types.AddInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddInvestorBidMutation,
    TError,
    Types.AddInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["AddInvestorBid"],
    mutationFn: (variables?: Types.AddInvestorBidMutationVariables) =>
      publicFetcher<
        Types.AddInvestorBidMutation,
        Types.AddInvestorBidMutationVariables
      >(AddInvestorBidDocument, variables)(),
    ...options,
  })
}

useAddInvestorBidMutation.fetcher = (
  variables: Types.AddInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddInvestorBidMutation, Types.AddInvestorBidMutationVariables>(
    AddInvestorBidDocument,
    variables,
    options,
  )

export const EditInvestorBidDocument = `
    mutation EditInvestorBid($orgSlug: String!, $bundlePid: String!, $bidPid: String!, $input: InvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      editInvestorBid(bidPid: $bidPid, input: $input) {
        ppa {
          pid
        }
        cs {
          pid
        }
      }
    }
  }
}
    `

export const useEditInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditInvestorBidMutation,
    TError,
    Types.EditInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditInvestorBidMutation,
    TError,
    Types.EditInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["EditInvestorBid"],
    mutationFn: (variables?: Types.EditInvestorBidMutationVariables) =>
      publicFetcher<
        Types.EditInvestorBidMutation,
        Types.EditInvestorBidMutationVariables
      >(EditInvestorBidDocument, variables)(),
    ...options,
  })
}

useEditInvestorBidMutation.fetcher = (
  variables: Types.EditInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EditInvestorBidMutation, Types.EditInvestorBidMutationVariables>(
    EditInvestorBidDocument,
    variables,
    options,
  )

export const DuplicateInvestorBidDocument = `
    mutation DuplicateInvestorBid($orgSlug: String!, $bundlePid: String!, $input: DuplicateInvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      duplicateInvestorBid(input: $input) {
        ppa {
          pid
        }
        cs {
          pid
        }
      }
    }
  }
}
    `

export const useDuplicateInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DuplicateInvestorBidMutation,
    TError,
    Types.DuplicateInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DuplicateInvestorBidMutation,
    TError,
    Types.DuplicateInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["DuplicateInvestorBid"],
    mutationFn: (variables?: Types.DuplicateInvestorBidMutationVariables) =>
      publicFetcher<
        Types.DuplicateInvestorBidMutation,
        Types.DuplicateInvestorBidMutationVariables
      >(DuplicateInvestorBidDocument, variables)(),
    ...options,
  })
}

useDuplicateInvestorBidMutation.fetcher = (
  variables: Types.DuplicateInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DuplicateInvestorBidMutation,
    Types.DuplicateInvestorBidMutationVariables
  >(DuplicateInvestorBidDocument, variables, options)

export const AddSystemDocument = `
    mutation AddSystem($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $input: AddSystemInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addSystem(sitePid: $sitePid, input: $input) {
        system {
          pid
        }
        lite {
          pid
        }
      }
    }
  }
}
    `

export const useAddSystemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddSystemMutation,
    TError,
    Types.AddSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddSystemMutation,
    TError,
    Types.AddSystemMutationVariables,
    TContext
  >({
    mutationKey: ["AddSystem"],
    mutationFn: (variables?: Types.AddSystemMutationVariables) =>
      publicFetcher<Types.AddSystemMutation, Types.AddSystemMutationVariables>(
        AddSystemDocument,
        variables,
      )(),
    ...options,
  })
}

useAddSystemMutation.fetcher = (
  variables: Types.AddSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddSystemMutation, Types.AddSystemMutationVariables>(
    AddSystemDocument,
    variables,
    options,
  )

export const UpdateEvOverridesDocument = `
    mutation UpdateEVOverrides($sitePid: String!, $projectStartDate: DateTime, $discountRate: Float, $usdPerKwhMarkup: Float, $incentivePct: Float, $salesTaxPct: Float, $omUsdPerChargerPerYr: Float, $rateManagementUsdPerChargerPerYr: Float, $cmsUsdPerChargerPerYr: Float, $demandUsdPerKw: Float, $installType: ChargerInstallType, $termYears: Int, $lowEnergyUsdPerKwh: Float, $highEnergyUsdPerKwh: Float, $onPeakChargingPct: Float, $numChargers: Float, $chargerLocation: ChargerLocation, $chargerKw: Float, $milesDrivenPerYear: Float, $milesPerKwh: Float, $maxUtilization: Float, $transactionFeePct: Float, $onSiteChargingPct: Float, $yr0NumTenantEvs: Int, $evGrowthPct: Float, $incentiveCapNumChargers: Int, $maxEvAdoptionPct: Float, $installCostUsd: Float) {
  evOverrides(sitePid: $sitePid) {
    update(
      projectStartDate: $projectStartDate
      discountRate: $discountRate
      usdPerKwhMarkup: $usdPerKwhMarkup
      incentivePct: $incentivePct
      salesTaxPct: $salesTaxPct
      omUsdPerChargerPerYr: $omUsdPerChargerPerYr
      rateManagementUsdPerChargerPerYr: $rateManagementUsdPerChargerPerYr
      cmsUsdPerChargerPerYr: $cmsUsdPerChargerPerYr
      demandUsdPerKw: $demandUsdPerKw
      installType: $installType
      termYears: $termYears
      lowEnergyUsdPerKwh: $lowEnergyUsdPerKwh
      highEnergyUsdPerKwh: $highEnergyUsdPerKwh
      onPeakChargingPct: $onPeakChargingPct
      numChargers: $numChargers
      chargerLocation: $chargerLocation
      chargerKw: $chargerKw
      milesDrivenPerYear: $milesDrivenPerYear
      milesPerKwh: $milesPerKwh
      maxUtilization: $maxUtilization
      transactionFeePct: $transactionFeePct
      onSiteChargingPct: $onSiteChargingPct
      yr0NumTenantEvs: $yr0NumTenantEvs
      evGrowthPct: $evGrowthPct
      incentiveCapNumChargers: $incentiveCapNumChargers
      maxEvAdoptionPct: $maxEvAdoptionPct
      installCostUsd: $installCostUsd
    )
  }
}
    `

export const useUpdateEvOverridesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateEvOverridesMutation,
    TError,
    Types.UpdateEvOverridesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateEvOverridesMutation,
    TError,
    Types.UpdateEvOverridesMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateEVOverrides"],
    mutationFn: (variables?: Types.UpdateEvOverridesMutationVariables) =>
      publicFetcher<
        Types.UpdateEvOverridesMutation,
        Types.UpdateEvOverridesMutationVariables
      >(UpdateEvOverridesDocument, variables)(),
    ...options,
  })
}

useUpdateEvOverridesMutation.fetcher = (
  variables: Types.UpdateEvOverridesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateEvOverridesMutation,
    Types.UpdateEvOverridesMutationVariables
  >(UpdateEvOverridesDocument, variables, options)

export const ArchiveSiteDocument = `
    mutation ArchiveSite($sitePid: String!, $reason: StatusReason!) {
  archiveSite(sitePid: $sitePid, reason: $reason) {
    __typename
  }
}
    `

export const useArchiveSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveSiteMutation,
    TError,
    Types.ArchiveSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveSiteMutation,
    TError,
    Types.ArchiveSiteMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveSite"],
    mutationFn: (variables?: Types.ArchiveSiteMutationVariables) =>
      publicFetcher<Types.ArchiveSiteMutation, Types.ArchiveSiteMutationVariables>(
        ArchiveSiteDocument,
        variables,
      )(),
    ...options,
  })
}

useArchiveSiteMutation.fetcher = (
  variables: Types.ArchiveSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ArchiveSiteMutation, Types.ArchiveSiteMutationVariables>(
    ArchiveSiteDocument,
    variables,
    options,
  )

export const ResolveSiteFlagDocument = `
    mutation ResolveSiteFlag($orgSlug: String!, $sitePid: String!, $flagType: SiteFlagType!) {
  orgBySlug(slug: $orgSlug) {
    flags {
      resolveSiteFlag(sitePid: $sitePid, flagType: $flagType) {
        __typename
      }
    }
  }
}
    `

export const useResolveSiteFlagMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ResolveSiteFlagMutation,
    TError,
    Types.ResolveSiteFlagMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ResolveSiteFlagMutation,
    TError,
    Types.ResolveSiteFlagMutationVariables,
    TContext
  >({
    mutationKey: ["ResolveSiteFlag"],
    mutationFn: (variables?: Types.ResolveSiteFlagMutationVariables) =>
      publicFetcher<
        Types.ResolveSiteFlagMutation,
        Types.ResolveSiteFlagMutationVariables
      >(ResolveSiteFlagDocument, variables)(),
    ...options,
  })
}

useResolveSiteFlagMutation.fetcher = (
  variables: Types.ResolveSiteFlagMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResolveSiteFlagMutation, Types.ResolveSiteFlagMutationVariables>(
    ResolveSiteFlagDocument,
    variables,
    options,
  )

export const CreatePortfolioDocument = `
    mutation CreatePortfolio($orgSlug: String!, $name: String!) {
  orgBySlug(slug: $orgSlug) {
    createPortfolio(name: $name) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreatePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePortfolio"],
    mutationFn: (variables?: Types.CreatePortfolioMutationVariables) =>
      publicFetcher<
        Types.CreatePortfolioMutation,
        Types.CreatePortfolioMutationVariables
      >(CreatePortfolioDocument, variables)(),
    ...options,
  })
}

useCreatePortfolioMutation.fetcher = (
  variables: Types.CreatePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreatePortfolioMutation, Types.CreatePortfolioMutationVariables>(
    CreatePortfolioDocument,
    variables,
    options,
  )

export const GrantRoleDocument = `
    mutation GrantRole($orgSlug: String!, $input: UserRoleInput!) {
  orgBySlug(slug: $orgSlug) {
    roles {
      grantRole(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useGrantRoleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.GrantRoleMutation,
    TError,
    Types.GrantRoleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.GrantRoleMutation,
    TError,
    Types.GrantRoleMutationVariables,
    TContext
  >({
    mutationKey: ["GrantRole"],
    mutationFn: (variables?: Types.GrantRoleMutationVariables) =>
      publicFetcher<Types.GrantRoleMutation, Types.GrantRoleMutationVariables>(
        GrantRoleDocument,
        variables,
      )(),
    ...options,
  })
}

useGrantRoleMutation.fetcher = (
  variables: Types.GrantRoleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GrantRoleMutation, Types.GrantRoleMutationVariables>(
    GrantRoleDocument,
    variables,
    options,
  )

export const RevokeRoleDocument = `
    mutation RevokeRole($orgSlug: String!, $input: UserRoleInput!) {
  orgBySlug(slug: $orgSlug) {
    roles {
      revokeRole(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useRevokeRoleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RevokeRoleMutation,
    TError,
    Types.RevokeRoleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RevokeRoleMutation,
    TError,
    Types.RevokeRoleMutationVariables,
    TContext
  >({
    mutationKey: ["RevokeRole"],
    mutationFn: (variables?: Types.RevokeRoleMutationVariables) =>
      publicFetcher<Types.RevokeRoleMutation, Types.RevokeRoleMutationVariables>(
        RevokeRoleDocument,
        variables,
      )(),
    ...options,
  })
}

useRevokeRoleMutation.fetcher = (
  variables: Types.RevokeRoleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RevokeRoleMutation, Types.RevokeRoleMutationVariables>(
    RevokeRoleDocument,
    variables,
    options,
  )

export const UpdateInvestorProfileDocument = `
    mutation UpdateInvestorProfile($orgSlug: String!, $hq: String, $yearFounded: Int, $website: String, $managementTeam: [String!]!, $description: String, $distinguishingFeatures: String, $investorBacking: String, $longTermOwnershipApproach: String, $subscriptionMgmtApproach: String, $committedFunding: String, $experiences: [ExperienceInput!]!) {
  orgBySlug(slug: $orgSlug) {
    investorProfile {
      updateProfile(
        hq: $hq
        yearFounded: $yearFounded
        website: $website
        managementTeam: $managementTeam
        description: $description
        distinguishingFeatures: $distinguishingFeatures
        investorBacking: $investorBacking
        longTermOwnershipApproach: $longTermOwnershipApproach
        subscriptionMgmtApproach: $subscriptionMgmtApproach
        committedFunding: $committedFunding
        experiences: $experiences
      )
    }
  }
}
    `

export const useUpdateInvestorProfileMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateInvestorProfileMutation,
    TError,
    Types.UpdateInvestorProfileMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateInvestorProfileMutation,
    TError,
    Types.UpdateInvestorProfileMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateInvestorProfile"],
    mutationFn: (variables?: Types.UpdateInvestorProfileMutationVariables) =>
      publicFetcher<
        Types.UpdateInvestorProfileMutation,
        Types.UpdateInvestorProfileMutationVariables
      >(UpdateInvestorProfileDocument, variables)(),
    ...options,
  })
}

useUpdateInvestorProfileMutation.fetcher = (
  variables: Types.UpdateInvestorProfileMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateInvestorProfileMutation,
    Types.UpdateInvestorProfileMutationVariables
  >(UpdateInvestorProfileDocument, variables, options)

export const UpdateInvestorBuyBoxDocument = `
    mutation UpdateInvestorBuyBox($orgSlug: String!, $buyBoxes: [BuyBoxInput!]!) {
  orgBySlug(slug: $orgSlug) {
    investorProfile {
      updateBuyBox(buyBoxes: $buyBoxes)
    }
  }
}
    `

export const useUpdateInvestorBuyBoxMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateInvestorBuyBoxMutation,
    TError,
    Types.UpdateInvestorBuyBoxMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateInvestorBuyBoxMutation,
    TError,
    Types.UpdateInvestorBuyBoxMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateInvestorBuyBox"],
    mutationFn: (variables?: Types.UpdateInvestorBuyBoxMutationVariables) =>
      publicFetcher<
        Types.UpdateInvestorBuyBoxMutation,
        Types.UpdateInvestorBuyBoxMutationVariables
      >(UpdateInvestorBuyBoxDocument, variables)(),
    ...options,
  })
}

useUpdateInvestorBuyBoxMutation.fetcher = (
  variables: Types.UpdateInvestorBuyBoxMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateInvestorBuyBoxMutation,
    Types.UpdateInvestorBuyBoxMutationVariables
  >(UpdateInvestorBuyBoxDocument, variables, options)

export const EvButtonDocument = `
    mutation EVButton($sitePid: String!, $button: String!) {
  evButton(sitePid: $sitePid) {
    press(button: $button)
  }
}
    `

export const useEvButtonMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EvButtonMutation,
    TError,
    Types.EvButtonMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EvButtonMutation,
    TError,
    Types.EvButtonMutationVariables,
    TContext
  >({
    mutationKey: ["EVButton"],
    mutationFn: (variables?: Types.EvButtonMutationVariables) =>
      publicFetcher<Types.EvButtonMutation, Types.EvButtonMutationVariables>(
        EvButtonDocument,
        variables,
      )(),
    ...options,
  })
}

useEvButtonMutation.fetcher = (
  variables: Types.EvButtonMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EvButtonMutation, Types.EvButtonMutationVariables>(
    EvButtonDocument,
    variables,
    options,
  )

export const SetBundleParentPreBidMeetingDocument = `
    mutation SetBundleParentPreBidMeeting($orgSlug: String!, $bundleParentPid: String!, $milestone: MilestoneInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setPreBidMeeting(milestone: $milestone)
      }
    }
  }
}
    `

export const useSetBundleParentPreBidMeetingMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentPreBidMeetingMutation,
    TError,
    Types.SetBundleParentPreBidMeetingMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentPreBidMeetingMutation,
    TError,
    Types.SetBundleParentPreBidMeetingMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentPreBidMeeting"],
    mutationFn: (variables?: Types.SetBundleParentPreBidMeetingMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentPreBidMeetingMutation,
        Types.SetBundleParentPreBidMeetingMutationVariables
      >(SetBundleParentPreBidMeetingDocument, variables)(),
    ...options,
  })
}

useSetBundleParentPreBidMeetingMutation.fetcher = (
  variables: Types.SetBundleParentPreBidMeetingMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentPreBidMeetingMutation,
    Types.SetBundleParentPreBidMeetingMutationVariables
  >(SetBundleParentPreBidMeetingDocument, variables, options)

export const SetBundleParentBidReviewMeetingDocument = `
    mutation SetBundleParentBidReviewMeeting($orgSlug: String!, $bundleParentPid: String!, $milestone: MilestoneInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setBidReviewMeeting(milestone: $milestone)
      }
    }
  }
}
    `

export const useSetBundleParentBidReviewMeetingMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentBidReviewMeetingMutation,
    TError,
    Types.SetBundleParentBidReviewMeetingMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentBidReviewMeetingMutation,
    TError,
    Types.SetBundleParentBidReviewMeetingMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentBidReviewMeeting"],
    mutationFn: (variables?: Types.SetBundleParentBidReviewMeetingMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentBidReviewMeetingMutation,
        Types.SetBundleParentBidReviewMeetingMutationVariables
      >(SetBundleParentBidReviewMeetingDocument, variables)(),
    ...options,
  })
}

useSetBundleParentBidReviewMeetingMutation.fetcher = (
  variables: Types.SetBundleParentBidReviewMeetingMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentBidReviewMeetingMutation,
    Types.SetBundleParentBidReviewMeetingMutationVariables
  >(SetBundleParentBidReviewMeetingDocument, variables, options)

export const SetArchiveInvestorBidDocument = `
    mutation SetArchiveInvestorBid($orgSlug: String!, $bundlePid: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      archiveInvestorBid(archive: $value, bidPid: $bidPid)
    }
  }
}
    `

export const useSetArchiveInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetArchiveInvestorBidMutation,
    TError,
    Types.SetArchiveInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchiveInvestorBidMutation,
    TError,
    Types.SetArchiveInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchiveInvestorBid"],
    mutationFn: (variables?: Types.SetArchiveInvestorBidMutationVariables) =>
      publicFetcher<
        Types.SetArchiveInvestorBidMutation,
        Types.SetArchiveInvestorBidMutationVariables
      >(SetArchiveInvestorBidDocument, variables)(),
    ...options,
  })
}

useSetArchiveInvestorBidMutation.fetcher = (
  variables: Types.SetArchiveInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchiveInvestorBidMutation,
    Types.SetArchiveInvestorBidMutationVariables
  >(SetArchiveInvestorBidDocument, variables, options)

export const SetBundleRowOptOutDocument = `
    mutation SetBundleRowOptOut($orgSlug: String!, $bundlePid: String!, $bundleRowId: Int!, $value: Boolean!, $reason: InvestorOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleOptOut(
        optOut: $value
        input: {bundleRowId: $bundleRowId}
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `

export const useSetBundleRowOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBundleRowOptOutMutation,
    TError,
    Types.SetBundleRowOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleRowOptOutMutation,
    TError,
    Types.SetBundleRowOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleRowOptOut"],
    mutationFn: (variables?: Types.SetBundleRowOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBundleRowOptOutMutation,
        Types.SetBundleRowOptOutMutationVariables
      >(SetBundleRowOptOutDocument, variables)(),
    ...options,
  })
}

useSetBundleRowOptOutMutation.fetcher = (
  variables: Types.SetBundleRowOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleRowOptOutMutation,
    Types.SetBundleRowOptOutMutationVariables
  >(SetBundleRowOptOutDocument, variables, options)

export const SetBundleOptOutDocument = `
    mutation SetBundleOptOut($orgSlug: String!, $bundlePid: String!, $value: Boolean!, $reason: InvestorOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleOptOut(
        optOut: $value
        input: {bundlePid: $bundlePid}
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `

export const useSetBundleOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBundleOptOutMutation,
    TError,
    Types.SetBundleOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleOptOutMutation,
    TError,
    Types.SetBundleOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleOptOut"],
    mutationFn: (variables?: Types.SetBundleOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBundleOptOutMutation,
        Types.SetBundleOptOutMutationVariables
      >(SetBundleOptOutDocument, variables)(),
    ...options,
  })
}

useSetBundleOptOutMutation.fetcher = (
  variables: Types.SetBundleOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetBundleOptOutMutation, Types.SetBundleOptOutMutationVariables>(
    SetBundleOptOutDocument,
    variables,
    options,
  )

export const SetBidSpecOptOutDocument = `
    mutation SetBidSpecOptOut($orgSlug: String!, $bundlePid: String!, $bidSpecPid: String!, $value: Boolean!, $reason: SpecOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleSpecOptOut(
        optOut: $value
        bidSpecPid: $bidSpecPid
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `

export const useSetBidSpecOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBidSpecOptOutMutation,
    TError,
    Types.SetBidSpecOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBidSpecOptOutMutation,
    TError,
    Types.SetBidSpecOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBidSpecOptOut"],
    mutationFn: (variables?: Types.SetBidSpecOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBidSpecOptOutMutation,
        Types.SetBidSpecOptOutMutationVariables
      >(SetBidSpecOptOutDocument, variables)(),
    ...options,
  })
}

useSetBidSpecOptOutMutation.fetcher = (
  variables: Types.SetBidSpecOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBidSpecOptOutMutation,
    Types.SetBidSpecOptOutMutationVariables
  >(SetBidSpecOptOutDocument, variables, options)

export const SubmitBundleBidsDocument = `
    mutation SubmitBundleBids($orgSlug: String!, $bundlePid: String!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      submitBids(pricingTermsAgreed: true)
    }
  }
}
    `

export const useSubmitBundleBidsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SubmitBundleBidsMutation,
    TError,
    Types.SubmitBundleBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SubmitBundleBidsMutation,
    TError,
    Types.SubmitBundleBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SubmitBundleBids"],
    mutationFn: (variables?: Types.SubmitBundleBidsMutationVariables) =>
      publicFetcher<
        Types.SubmitBundleBidsMutation,
        Types.SubmitBundleBidsMutationVariables
      >(SubmitBundleBidsDocument, variables)(),
    ...options,
  })
}

useSubmitBundleBidsMutation.fetcher = (
  variables: Types.SubmitBundleBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SubmitBundleBidsMutation,
    Types.SubmitBundleBidsMutationVariables
  >(SubmitBundleBidsDocument, variables, options)

export const AddHelioscopeDocument = `
    mutation AddHelioscope($sitePid: String!, $bundlePid: String, $reportPdf: Upload!, $productionCsv: Upload) {
  addHelioscope(
    sitePid: $sitePid
    bundlePid: $bundlePid
    reportPdf: $reportPdf
    productionCsv: $productionCsv
  ) {
    ...Helioscope2Ref
  }
}
    ${Helioscope2RefFragmentDoc}`

export const useAddHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["AddHelioscope"],
    mutationFn: (variables?: Types.AddHelioscopeMutationVariables) =>
      publicFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
        AddHelioscopeDocument,
        variables,
      )(),
    ...options,
  })
}

useAddHelioscopeMutation.fetcher = (
  variables: Types.AddHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
    AddHelioscopeDocument,
    variables,
    options,
  )

export const SetArchiveBidSpecDocument = `
    mutation SetArchiveBidSpec($orgSlug: String!, $bundleParentPid: String!, $specPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        archiveSpec(specPid: $specPid, archive: $value)
      }
    }
  }
}
    `

export const useSetArchiveBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetArchiveBidSpecMutation,
    TError,
    Types.SetArchiveBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchiveBidSpecMutation,
    TError,
    Types.SetArchiveBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchiveBidSpec"],
    mutationFn: (variables?: Types.SetArchiveBidSpecMutationVariables) =>
      publicFetcher<
        Types.SetArchiveBidSpecMutation,
        Types.SetArchiveBidSpecMutationVariables
      >(SetArchiveBidSpecDocument, variables)(),
    ...options,
  })
}

useSetArchiveBidSpecMutation.fetcher = (
  variables: Types.SetArchiveBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchiveBidSpecMutation,
    Types.SetArchiveBidSpecMutationVariables
  >(SetArchiveBidSpecDocument, variables, options)

export const SetPackageSiteNotesDocument = `
    mutation SetPackageSiteNotes($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $notes: String) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setRowNotes(sitePid: $sitePid, notes: $notes)
      }
    }
  }
}
    `

export const useSetPackageSiteNotesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPackageSiteNotesMutation,
    TError,
    Types.SetPackageSiteNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPackageSiteNotesMutation,
    TError,
    Types.SetPackageSiteNotesMutationVariables,
    TContext
  >({
    mutationKey: ["SetPackageSiteNotes"],
    mutationFn: (variables?: Types.SetPackageSiteNotesMutationVariables) =>
      publicFetcher<
        Types.SetPackageSiteNotesMutation,
        Types.SetPackageSiteNotesMutationVariables
      >(SetPackageSiteNotesDocument, variables)(),
    ...options,
  })
}

useSetPackageSiteNotesMutation.fetcher = (
  variables: Types.SetPackageSiteNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPackageSiteNotesMutation,
    Types.SetPackageSiteNotesMutationVariables
  >(SetPackageSiteNotesDocument, variables, options)

export const AddInvestorBidSpecDocument = `
    mutation AddInvestorBidSpec($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $input: BidSpecInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addSpec(sitePid: $sitePid, input: $input) {
          ppa {
            pid
          }
          cs {
            pid
          }
        }
      }
    }
  }
}
    `

export const useAddInvestorBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddInvestorBidSpecMutation,
    TError,
    Types.AddInvestorBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddInvestorBidSpecMutation,
    TError,
    Types.AddInvestorBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["AddInvestorBidSpec"],
    mutationFn: (variables?: Types.AddInvestorBidSpecMutationVariables) =>
      publicFetcher<
        Types.AddInvestorBidSpecMutation,
        Types.AddInvestorBidSpecMutationVariables
      >(AddInvestorBidSpecDocument, variables)(),
    ...options,
  })
}

useAddInvestorBidSpecMutation.fetcher = (
  variables: Types.AddInvestorBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddInvestorBidSpecMutation,
    Types.AddInvestorBidSpecMutationVariables
  >(AddInvestorBidSpecDocument, variables, options)

export const EditInvestorBidSpecDocument = `
    mutation EditInvestorBidSpec($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $specPid: String!, $input: BidSpecInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editSpec(specPid: $specPid, sitePid: $sitePid, input: $input) {
          ppa {
            pid
          }
          cs {
            pid
          }
        }
      }
    }
  }
}
    `

export const useEditInvestorBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditInvestorBidSpecMutation,
    TError,
    Types.EditInvestorBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditInvestorBidSpecMutation,
    TError,
    Types.EditInvestorBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["EditInvestorBidSpec"],
    mutationFn: (variables?: Types.EditInvestorBidSpecMutationVariables) =>
      publicFetcher<
        Types.EditInvestorBidSpecMutation,
        Types.EditInvestorBidSpecMutationVariables
      >(EditInvestorBidSpecDocument, variables)(),
    ...options,
  })
}

useEditInvestorBidSpecMutation.fetcher = (
  variables: Types.EditInvestorBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditInvestorBidSpecMutation,
    Types.EditInvestorBidSpecMutationVariables
  >(EditInvestorBidSpecDocument, variables, options)

export const EnqueueTaskDocument = `
    mutation EnqueueTask($params: TaskParamsInput!) {
  tasks {
    enqueue(params: $params) {
      __typename
      ... on Task {
        id
        name
        status
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useEnqueueTaskMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EnqueueTaskMutation,
    TError,
    Types.EnqueueTaskMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EnqueueTaskMutation,
    TError,
    Types.EnqueueTaskMutationVariables,
    TContext
  >({
    mutationKey: ["EnqueueTask"],
    mutationFn: (variables?: Types.EnqueueTaskMutationVariables) =>
      publicFetcher<Types.EnqueueTaskMutation, Types.EnqueueTaskMutationVariables>(
        EnqueueTaskDocument,
        variables,
      )(),
    ...options,
  })
}

useEnqueueTaskMutation.fetcher = (
  variables: Types.EnqueueTaskMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EnqueueTaskMutation, Types.EnqueueTaskMutationVariables>(
    EnqueueTaskDocument,
    variables,
    options,
  )

export const AddBundleParentEtbDocument = `
    mutation AddBundleParentEtb($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $upload: Upload!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addEtb(sitePid: $sitePid, upload: $upload) {
          ...EtbExtract
        }
      }
    }
  }
}
    ${EtbExtractFragmentDoc}`

export const useAddBundleParentEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleParentEtbMutation,
    TError,
    Types.AddBundleParentEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentEtbMutation,
    TError,
    Types.AddBundleParentEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentEtb"],
    mutationFn: (variables?: Types.AddBundleParentEtbMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentEtbMutation,
        Types.AddBundleParentEtbMutationVariables
      >(AddBundleParentEtbDocument, variables)(),
    ...options,
  })
}

useAddBundleParentEtbMutation.fetcher = (
  variables: Types.AddBundleParentEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentEtbMutation,
    Types.AddBundleParentEtbMutationVariables
  >(AddBundleParentEtbDocument, variables, options)

export const AddBundleEtbDocument = `
    mutation AddBundleEtb($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $upload: Upload!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addEtb(sitePid: $sitePid, upload: $upload) {
        ...EtbExtract
      }
    }
  }
}
    ${EtbExtractFragmentDoc}`

export const useAddBundleEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleEtbMutation,
    TError,
    Types.AddBundleEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleEtbMutation,
    TError,
    Types.AddBundleEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleEtb"],
    mutationFn: (variables?: Types.AddBundleEtbMutationVariables) =>
      publicFetcher<Types.AddBundleEtbMutation, Types.AddBundleEtbMutationVariables>(
        AddBundleEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useAddBundleEtbMutation.fetcher = (
  variables: Types.AddBundleEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddBundleEtbMutation, Types.AddBundleEtbMutationVariables>(
    AddBundleEtbDocument,
    variables,
    options,
  )

export const SetPackageRowStatusDocument = `
    mutation SetPackageRowStatus($bundleParentPid: String!, $sitePid: String!, $status: PackageRowStatus, $closed: Boolean, $closeReason: PackageRowCloseReason, $closeCustomReason: String) {
  setPackageRowStatus(
    bundleParentPid: $bundleParentPid
    sitePid: $sitePid
    status: $status
    closed: $closed
    closeReason: $closeReason
    customCloseReason: $closeCustomReason
  )
}
    `

export const useSetPackageRowStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPackageRowStatusMutation,
    TError,
    Types.SetPackageRowStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPackageRowStatusMutation,
    TError,
    Types.SetPackageRowStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetPackageRowStatus"],
    mutationFn: (variables?: Types.SetPackageRowStatusMutationVariables) =>
      publicFetcher<
        Types.SetPackageRowStatusMutation,
        Types.SetPackageRowStatusMutationVariables
      >(SetPackageRowStatusDocument, variables)(),
    ...options,
  })
}

useSetPackageRowStatusMutation.fetcher = (
  variables: Types.SetPackageRowStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPackageRowStatusMutation,
    Types.SetPackageRowStatusMutationVariables
  >(SetPackageRowStatusDocument, variables, options)

export const AwardInvestorBidDocument = `
    mutation AwardInvestorBid($orgSlug: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    awardInvestorBid(bidPid: $bidPid, value: $value)
  }
}
    `

export const useAwardInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AwardInvestorBidMutation,
    TError,
    Types.AwardInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AwardInvestorBidMutation,
    TError,
    Types.AwardInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["AwardInvestorBid"],
    mutationFn: (variables?: Types.AwardInvestorBidMutationVariables) =>
      publicFetcher<
        Types.AwardInvestorBidMutation,
        Types.AwardInvestorBidMutationVariables
      >(AwardInvestorBidDocument, variables)(),
    ...options,
  })
}

useAwardInvestorBidMutation.fetcher = (
  variables: Types.AwardInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardInvestorBidMutation,
    Types.AwardInvestorBidMutationVariables
  >(AwardInvestorBidDocument, variables, options)

export const RecommendInvestorBidDocument = `
    mutation RecommendInvestorBid($orgSlug: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    recommendInvestorBid(bidPid: $bidPid, value: $value)
  }
}
    `

export const useRecommendInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RecommendInvestorBidMutation,
    TError,
    Types.RecommendInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RecommendInvestorBidMutation,
    TError,
    Types.RecommendInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["RecommendInvestorBid"],
    mutationFn: (variables?: Types.RecommendInvestorBidMutationVariables) =>
      publicFetcher<
        Types.RecommendInvestorBidMutation,
        Types.RecommendInvestorBidMutationVariables
      >(RecommendInvestorBidDocument, variables)(),
    ...options,
  })
}

useRecommendInvestorBidMutation.fetcher = (
  variables: Types.RecommendInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RecommendInvestorBidMutation,
    Types.RecommendInvestorBidMutationVariables
  >(RecommendInvestorBidDocument, variables, options)

export const SetBundleParentHiddenFromFinancierDocument = `
    mutation SetBundleParentHiddenFromFinancier($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setHiddenFromFinancier(hidden: $value)
      }
    }
  }
}
    `

export const useSetBundleParentHiddenFromFinancierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentHiddenFromFinancierMutation,
    TError,
    Types.SetBundleParentHiddenFromFinancierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentHiddenFromFinancierMutation,
    TError,
    Types.SetBundleParentHiddenFromFinancierMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentHiddenFromFinancier"],
    mutationFn: (
      variables?: Types.SetBundleParentHiddenFromFinancierMutationVariables,
    ) =>
      publicFetcher<
        Types.SetBundleParentHiddenFromFinancierMutation,
        Types.SetBundleParentHiddenFromFinancierMutationVariables
      >(SetBundleParentHiddenFromFinancierDocument, variables)(),
    ...options,
  })
}

useSetBundleParentHiddenFromFinancierMutation.fetcher = (
  variables: Types.SetBundleParentHiddenFromFinancierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentHiddenFromFinancierMutation,
    Types.SetBundleParentHiddenFromFinancierMutationVariables
  >(SetBundleParentHiddenFromFinancierDocument, variables, options)

export const SetBundleParentHiddenFromReceiverDocument = `
    mutation SetBundleParentHiddenFromReceiver($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setHiddenFromRecvr(hidden: $value)
      }
    }
  }
}
    `

export const useSetBundleParentHiddenFromReceiverMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentHiddenFromReceiverMutation,
    TError,
    Types.SetBundleParentHiddenFromReceiverMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentHiddenFromReceiverMutation,
    TError,
    Types.SetBundleParentHiddenFromReceiverMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentHiddenFromReceiver"],
    mutationFn: (
      variables?: Types.SetBundleParentHiddenFromReceiverMutationVariables,
    ) =>
      publicFetcher<
        Types.SetBundleParentHiddenFromReceiverMutation,
        Types.SetBundleParentHiddenFromReceiverMutationVariables
      >(SetBundleParentHiddenFromReceiverDocument, variables)(),
    ...options,
  })
}

useSetBundleParentHiddenFromReceiverMutation.fetcher = (
  variables: Types.SetBundleParentHiddenFromReceiverMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentHiddenFromReceiverMutation,
    Types.SetBundleParentHiddenFromReceiverMutationVariables
  >(SetBundleParentHiddenFromReceiverDocument, variables, options)

export const SetBundleParentCanAddNewBidsDocument = `
    mutation SetBundleParentCanAddNewBids($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setCanAddNewBids(can: $value)
      }
    }
  }
}
    `

export const useSetBundleParentCanAddNewBidsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentCanAddNewBidsMutation,
    TError,
    Types.SetBundleParentCanAddNewBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentCanAddNewBidsMutation,
    TError,
    Types.SetBundleParentCanAddNewBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentCanAddNewBids"],
    mutationFn: (variables?: Types.SetBundleParentCanAddNewBidsMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentCanAddNewBidsMutation,
        Types.SetBundleParentCanAddNewBidsMutationVariables
      >(SetBundleParentCanAddNewBidsDocument, variables)(),
    ...options,
  })
}

useSetBundleParentCanAddNewBidsMutation.fetcher = (
  variables: Types.SetBundleParentCanAddNewBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentCanAddNewBidsMutation,
    Types.SetBundleParentCanAddNewBidsMutationVariables
  >(SetBundleParentCanAddNewBidsDocument, variables, options)

export const SetBundleParentCanEditBidsDocument = `
    mutation SetBundleParentCanEditBids($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setCanEditBids(can: $value)
      }
    }
  }
}
    `

export const useSetBundleParentCanEditBidsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentCanEditBidsMutation,
    TError,
    Types.SetBundleParentCanEditBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentCanEditBidsMutation,
    TError,
    Types.SetBundleParentCanEditBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentCanEditBids"],
    mutationFn: (variables?: Types.SetBundleParentCanEditBidsMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentCanEditBidsMutation,
        Types.SetBundleParentCanEditBidsMutationVariables
      >(SetBundleParentCanEditBidsDocument, variables)(),
    ...options,
  })
}

useSetBundleParentCanEditBidsMutation.fetcher = (
  variables: Types.SetBundleParentCanEditBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentCanEditBidsMutation,
    Types.SetBundleParentCanEditBidsMutationVariables
  >(SetBundleParentCanEditBidsDocument, variables, options)

export const SetBundleParentShowAllBidsReportDocument = `
    mutation SetBundleParentShowAllBidsReport($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setShowAllBidsReport(value: $value)
      }
    }
  }
}
    `

export const useSetBundleParentShowAllBidsReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentShowAllBidsReportMutation,
    TError,
    Types.SetBundleParentShowAllBidsReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentShowAllBidsReportMutation,
    TError,
    Types.SetBundleParentShowAllBidsReportMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentShowAllBidsReport"],
    mutationFn: (variables?: Types.SetBundleParentShowAllBidsReportMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentShowAllBidsReportMutation,
        Types.SetBundleParentShowAllBidsReportMutationVariables
      >(SetBundleParentShowAllBidsReportDocument, variables)(),
    ...options,
  })
}

useSetBundleParentShowAllBidsReportMutation.fetcher = (
  variables: Types.SetBundleParentShowAllBidsReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentShowAllBidsReportMutation,
    Types.SetBundleParentShowAllBidsReportMutationVariables
  >(SetBundleParentShowAllBidsReportDocument, variables, options)

export const SetBundleParentOpsOwnerDocument = `
    mutation SetBundleParentOpsOwner($orgSlug: String!, $bundleParentPid: String!, $opsOwnerPid: String) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setOpsOwner(opsOwnerPid: $opsOwnerPid)
      }
    }
  }
}
    `

export const useSetBundleParentOpsOwnerMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentOpsOwnerMutation,
    TError,
    Types.SetBundleParentOpsOwnerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentOpsOwnerMutation,
    TError,
    Types.SetBundleParentOpsOwnerMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentOpsOwner"],
    mutationFn: (variables?: Types.SetBundleParentOpsOwnerMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentOpsOwnerMutation,
        Types.SetBundleParentOpsOwnerMutationVariables
      >(SetBundleParentOpsOwnerDocument, variables)(),
    ...options,
  })
}

useSetBundleParentOpsOwnerMutation.fetcher = (
  variables: Types.SetBundleParentOpsOwnerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentOpsOwnerMutation,
    Types.SetBundleParentOpsOwnerMutationVariables
  >(SetBundleParentOpsOwnerDocument, variables, options)

export const UpdateSiteDetailsDocument = `
    mutation UpdateSiteDetails($sitePid: String!, $input: UpdateDetailsInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  siteDetails(sitePid: $sitePid) {
    updateDetails(input: $input, sourceType: $sourceType, sourceId: $sourceId)
  }
}
    `

export const useUpdateSiteDetailsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateSiteDetailsMutation,
    TError,
    Types.UpdateSiteDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateSiteDetailsMutation,
    TError,
    Types.UpdateSiteDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateSiteDetails"],
    mutationFn: (variables?: Types.UpdateSiteDetailsMutationVariables) =>
      publicFetcher<
        Types.UpdateSiteDetailsMutation,
        Types.UpdateSiteDetailsMutationVariables
      >(UpdateSiteDetailsDocument, variables)(),
    ...options,
  })
}

useUpdateSiteDetailsMutation.fetcher = (
  variables: Types.UpdateSiteDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateSiteDetailsMutation,
    Types.UpdateSiteDetailsMutationVariables
  >(UpdateSiteDetailsDocument, variables, options)

export const CreateCustomBidReportDocument = `
    mutation CreateCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $name: String!, $developerNotes: String!, $buildingOwnerNotes: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        createCustomBidReport(
          name: $name
          developerNotes: $developerNotes
          buildingOwnerNotes: $buildingOwnerNotes
        ) {
          pid
          name
          notes
          buildingOwnerNotes
        }
      }
    }
  }
}
    `

export const useCreateCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomBidReportMutation,
    TError,
    Types.CreateCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomBidReportMutation,
    TError,
    Types.CreateCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomBidReport"],
    mutationFn: (variables?: Types.CreateCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.CreateCustomBidReportMutation,
        Types.CreateCustomBidReportMutationVariables
      >(CreateCustomBidReportDocument, variables)(),
    ...options,
  })
}

useCreateCustomBidReportMutation.fetcher = (
  variables: Types.CreateCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomBidReportMutation,
    Types.CreateCustomBidReportMutationVariables
  >(CreateCustomBidReportDocument, variables, options)

export const EditCustomBidReportDocument = `
    mutation EditCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $name: String!, $developerNotes: String!, $buildingOwnerNotes: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setName(value: $name)
          setDeveloperNotes(value: $developerNotes)
          setBuildingOwnerNotes(value: $buildingOwnerNotes)
        }
      }
    }
  }
}
    `

export const useEditCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditCustomBidReportMutation,
    TError,
    Types.EditCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditCustomBidReportMutation,
    TError,
    Types.EditCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["EditCustomBidReport"],
    mutationFn: (variables?: Types.EditCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.EditCustomBidReportMutation,
        Types.EditCustomBidReportMutationVariables
      >(EditCustomBidReportDocument, variables)(),
    ...options,
  })
}

useEditCustomBidReportMutation.fetcher = (
  variables: Types.EditCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditCustomBidReportMutation,
    Types.EditCustomBidReportMutationVariables
  >(EditCustomBidReportDocument, variables, options)

export const SetCustomBidReportVisibleToDevelopersDocument = `
    mutation SetCustomBidReportVisibleToDevelopers($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setVisibleToDevelopers(value: $value)
        }
      }
    }
  }
}
    `

export const useSetCustomBidReportVisibleToDevelopersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    TError,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    TError,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomBidReportVisibleToDevelopers"],
    mutationFn: (
      variables?: Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    ) =>
      publicFetcher<
        Types.SetCustomBidReportVisibleToDevelopersMutation,
        Types.SetCustomBidReportVisibleToDevelopersMutationVariables
      >(SetCustomBidReportVisibleToDevelopersDocument, variables)(),
    ...options,
  })
}

useSetCustomBidReportVisibleToDevelopersMutation.fetcher = (
  variables: Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables
  >(SetCustomBidReportVisibleToDevelopersDocument, variables, options)

export const SetCustomBidReportVisibleToBuildingOwnersDocument = `
    mutation SetCustomBidReportVisibleToBuildingOwners($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setVisibleToBuildingOwners(value: $value)
        }
      }
    }
  }
}
    `

export const useSetCustomBidReportVisibleToBuildingOwnersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    TError,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    TError,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomBidReportVisibleToBuildingOwners"],
    mutationFn: (
      variables?: Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    ) =>
      publicFetcher<
        Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
        Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables
      >(SetCustomBidReportVisibleToBuildingOwnersDocument, variables)(),
    ...options,
  })
}

useSetCustomBidReportVisibleToBuildingOwnersMutation.fetcher = (
  variables: Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables
  >(SetCustomBidReportVisibleToBuildingOwnersDocument, variables, options)

export const DeleteCustomBidReportDocument = `
    mutation DeleteCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          delete
        }
      }
    }
  }
}
    `

export const useDeleteCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteCustomBidReportMutation,
    TError,
    Types.DeleteCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteCustomBidReportMutation,
    TError,
    Types.DeleteCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteCustomBidReport"],
    mutationFn: (variables?: Types.DeleteCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.DeleteCustomBidReportMutation,
        Types.DeleteCustomBidReportMutationVariables
      >(DeleteCustomBidReportDocument, variables)(),
    ...options,
  })
}

useDeleteCustomBidReportMutation.fetcher = (
  variables: Types.DeleteCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteCustomBidReportMutation,
    Types.DeleteCustomBidReportMutationVariables
  >(DeleteCustomBidReportDocument, variables, options)

export const AddBidToCustomBidReportDocument = `
    mutation AddBidToCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $bidPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          upsertBid(bidPid: $bidPid)
        }
      }
    }
  }
}
    `

export const useAddBidToCustomBidReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBidToCustomBidReportMutation,
    TError,
    Types.AddBidToCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBidToCustomBidReportMutation,
    TError,
    Types.AddBidToCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["AddBidToCustomBidReport"],
    mutationFn: (variables?: Types.AddBidToCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.AddBidToCustomBidReportMutation,
        Types.AddBidToCustomBidReportMutationVariables
      >(AddBidToCustomBidReportDocument, variables)(),
    ...options,
  })
}

useAddBidToCustomBidReportMutation.fetcher = (
  variables: Types.AddBidToCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBidToCustomBidReportMutation,
    Types.AddBidToCustomBidReportMutationVariables
  >(AddBidToCustomBidReportDocument, variables, options)

export const RemoveBidFromCustomBidReportDocument = `
    mutation RemoveBidFromCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $bidPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          removeBid(bidPid: $bidPid)
        }
      }
    }
  }
}
    `

export const useRemoveBidFromCustomBidReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBidFromCustomBidReportMutation,
    TError,
    Types.RemoveBidFromCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBidFromCustomBidReportMutation,
    TError,
    Types.RemoveBidFromCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBidFromCustomBidReport"],
    mutationFn: (variables?: Types.RemoveBidFromCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.RemoveBidFromCustomBidReportMutation,
        Types.RemoveBidFromCustomBidReportMutationVariables
      >(RemoveBidFromCustomBidReportDocument, variables)(),
    ...options,
  })
}

useRemoveBidFromCustomBidReportMutation.fetcher = (
  variables: Types.RemoveBidFromCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBidFromCustomBidReportMutation,
    Types.RemoveBidFromCustomBidReportMutationVariables
  >(RemoveBidFromCustomBidReportDocument, variables, options)

export const UpdateCustomTariffDocument = `
    mutation UpdateCustomTariff($sitePid: String!, $customTariffId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!, $name: String!) {
  customTariffs(sitePid: $sitePid) {
    update(
      customTariffId: $customTariffId
      addRateOps: $addRateOps
      subRateOps: $subRateOps
      propertyOps: $propertyOps
      name: $name
    ) {
      id
    }
  }
}
    `

export const useUpdateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateCustomTariffMutation,
    TError,
    Types.UpdateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateCustomTariffMutation,
    TError,
    Types.UpdateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateCustomTariff"],
    mutationFn: (variables?: Types.UpdateCustomTariffMutationVariables) =>
      publicFetcher<
        Types.UpdateCustomTariffMutation,
        Types.UpdateCustomTariffMutationVariables
      >(UpdateCustomTariffDocument, variables)(),
    ...options,
  })
}

useUpdateCustomTariffMutation.fetcher = (
  variables: Types.UpdateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateCustomTariffMutation,
    Types.UpdateCustomTariffMutationVariables
  >(UpdateCustomTariffDocument, variables, options)

export const CreateCustomTariffDocument = `
    mutation CreateCustomTariff($sitePid: String!, $name: String!, $rateId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  customTariffs(sitePid: $sitePid) {
    create(
      name: $name
      rateId: $rateId
      addRateOps: $addRateOps
      subRateOps: $subRateOps
      propertyOps: $propertyOps
    ) {
      id
    }
  }
}
    `

export const useCreateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariff"],
    mutationFn: (variables?: Types.CreateCustomTariffMutationVariables) =>
      publicFetcher<
        Types.CreateCustomTariffMutation,
        Types.CreateCustomTariffMutationVariables
      >(CreateCustomTariffDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffMutation.fetcher = (
  variables: Types.CreateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomTariffMutation,
    Types.CreateCustomTariffMutationVariables
  >(CreateCustomTariffDocument, variables, options)

export const CreateCustomTariffScratchDocument = `
    mutation CreateCustomTariffScratch($sitePid: String!, $name: String!, $tariffClass: TariffClass!, $utilityPid: String!, $addRateOps: [AddRateInput!]!) {
  customTariffs(sitePid: $sitePid) {
    createFromScratch(
      name: $name
      tariffClass: $tariffClass
      utilityPid: $utilityPid
      addRateOps: $addRateOps
    ) {
      id
    }
  }
}
    `

export const useCreateCustomTariffScratchMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffScratchMutation,
    TError,
    Types.CreateCustomTariffScratchMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffScratchMutation,
    TError,
    Types.CreateCustomTariffScratchMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariffScratch"],
    mutationFn: (variables?: Types.CreateCustomTariffScratchMutationVariables) =>
      publicFetcher<
        Types.CreateCustomTariffScratchMutation,
        Types.CreateCustomTariffScratchMutationVariables
      >(CreateCustomTariffScratchDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffScratchMutation.fetcher = (
  variables: Types.CreateCustomTariffScratchMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomTariffScratchMutation,
    Types.CreateCustomTariffScratchMutationVariables
  >(CreateCustomTariffScratchDocument, variables, options)

export const SetProjectMilestoneDocument = `
    mutation SetProjectMilestone($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $milestoneType: MilestoneType!, $milestoneInput: ProjectMilestoneInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      setMilestone(
        sitePid: $sitePid
        milestoneType: $milestoneType
        milestoneInput: $milestoneInput
      )
    }
  }
}
    `

export const useSetProjectMilestoneMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetProjectMilestoneMutation,
    TError,
    Types.SetProjectMilestoneMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProjectMilestoneMutation,
    TError,
    Types.SetProjectMilestoneMutationVariables,
    TContext
  >({
    mutationKey: ["SetProjectMilestone"],
    mutationFn: (variables?: Types.SetProjectMilestoneMutationVariables) =>
      publicFetcher<
        Types.SetProjectMilestoneMutation,
        Types.SetProjectMilestoneMutationVariables
      >(SetProjectMilestoneDocument, variables)(),
    ...options,
  })
}

useSetProjectMilestoneMutation.fetcher = (
  variables: Types.SetProjectMilestoneMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProjectMilestoneMutation,
    Types.SetProjectMilestoneMutationVariables
  >(SetProjectMilestoneDocument, variables, options)

export const SetIsBehindTheMeterDocument = `
    mutation SetIsBehindTheMeter($sitePid: String!, $isBehindTheMeter: Boolean) {
  setIsBehindTheMeter(sitePid: $sitePid, isBehindTheMeter: $isBehindTheMeter)
}
    `

export const useSetIsBehindTheMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetIsBehindTheMeterMutation,
    TError,
    Types.SetIsBehindTheMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetIsBehindTheMeterMutation,
    TError,
    Types.SetIsBehindTheMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetIsBehindTheMeter"],
    mutationFn: (variables?: Types.SetIsBehindTheMeterMutationVariables) =>
      publicFetcher<
        Types.SetIsBehindTheMeterMutation,
        Types.SetIsBehindTheMeterMutationVariables
      >(SetIsBehindTheMeterDocument, variables)(),
    ...options,
  })
}

useSetIsBehindTheMeterMutation.fetcher = (
  variables: Types.SetIsBehindTheMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetIsBehindTheMeterMutation,
    Types.SetIsBehindTheMeterMutationVariables
  >(SetIsBehindTheMeterDocument, variables, options)

export const FinalizeEnergyPageUploadsDocument = `
    mutation FinalizeEnergyPageUploads($sitePid: String!) {
  finalizeEnergyPageUpload(sitePid: $sitePid)
}
    `

export const useFinalizeEnergyPageUploadsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.FinalizeEnergyPageUploadsMutation,
    TError,
    Types.FinalizeEnergyPageUploadsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinalizeEnergyPageUploadsMutation,
    TError,
    Types.FinalizeEnergyPageUploadsMutationVariables,
    TContext
  >({
    mutationKey: ["FinalizeEnergyPageUploads"],
    mutationFn: (variables?: Types.FinalizeEnergyPageUploadsMutationVariables) =>
      publicFetcher<
        Types.FinalizeEnergyPageUploadsMutation,
        Types.FinalizeEnergyPageUploadsMutationVariables
      >(FinalizeEnergyPageUploadsDocument, variables)(),
    ...options,
  })
}

useFinalizeEnergyPageUploadsMutation.fetcher = (
  variables: Types.FinalizeEnergyPageUploadsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinalizeEnergyPageUploadsMutation,
    Types.FinalizeEnergyPageUploadsMutationVariables
  >(FinalizeEnergyPageUploadsDocument, variables, options)

export const CreateEmailDraftDocument = `
    mutation CreateEmailDraft($draftParams: DraftEmailInput!) {
  admin {
    googleApi {
      draftEmail(draftParams: $draftParams)
    }
  }
}
    `

export const useCreateEmailDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >({
    mutationKey: ["CreateEmailDraft"],
    mutationFn: (variables?: Types.CreateEmailDraftMutationVariables) =>
      publicFetcher<
        Types.CreateEmailDraftMutation,
        Types.CreateEmailDraftMutationVariables
      >(CreateEmailDraftDocument, variables)(),
    ...options,
  })
}

useCreateEmailDraftMutation.fetcher = (
  variables: Types.CreateEmailDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateEmailDraftMutation,
    Types.CreateEmailDraftMutationVariables
  >(CreateEmailDraftDocument, variables, options)

export const AddFinmodelOverrideDocument = `
    mutation AddFinmodelOverride($sitePid: String, $portfolioPid: String, $overrideInput: FinmodelMutationInput!) {
  finmodel {
    addOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      overrideInput: $overrideInput
    ) {
      __typename
      ... on Error {
        message
      }
      ... on FinmodelInputOverride {
        inputName
        inputValue
      }
    }
  }
}
    `

export const useAddFinmodelOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddFinmodelOverrideMutation,
    TError,
    Types.AddFinmodelOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddFinmodelOverrideMutation,
    TError,
    Types.AddFinmodelOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["AddFinmodelOverride"],
    mutationFn: (variables?: Types.AddFinmodelOverrideMutationVariables) =>
      publicFetcher<
        Types.AddFinmodelOverrideMutation,
        Types.AddFinmodelOverrideMutationVariables
      >(AddFinmodelOverrideDocument, variables)(),
    ...options,
  })
}

useAddFinmodelOverrideMutation.fetcher = (
  variables: Types.AddFinmodelOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddFinmodelOverrideMutation,
    Types.AddFinmodelOverrideMutationVariables
  >(AddFinmodelOverrideDocument, variables, options)

export const DeleteFinmodelOverrideDocument = `
    mutation DeleteFinmodelOverride($sitePid: String, $portfolioPid: String, $inputName: String!) {
  finmodel {
    deleteOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      inputName: $inputName
    ) {
      __typename
      ... on Error {
        message
      }
      ... on FinmodelInputOverride {
        inputName
        inputValue
      }
    }
  }
}
    `

export const useDeleteFinmodelOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteFinmodelOverrideMutation,
    TError,
    Types.DeleteFinmodelOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteFinmodelOverrideMutation,
    TError,
    Types.DeleteFinmodelOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteFinmodelOverride"],
    mutationFn: (variables?: Types.DeleteFinmodelOverrideMutationVariables) =>
      publicFetcher<
        Types.DeleteFinmodelOverrideMutation,
        Types.DeleteFinmodelOverrideMutationVariables
      >(DeleteFinmodelOverrideDocument, variables)(),
    ...options,
  })
}

useDeleteFinmodelOverrideMutation.fetcher = (
  variables: Types.DeleteFinmodelOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteFinmodelOverrideMutation,
    Types.DeleteFinmodelOverrideMutationVariables
  >(DeleteFinmodelOverrideDocument, variables, options)

export const ListUtilitiesDocument = `
    query ListUtilities($zipCode: String!, $state: String!) {
  utilitiesForZip(zipCode: $zipCode) {
    __typename
    ... on UtilityList {
      refs {
        id
        pid
        name
        shortName
      }
    }
    ... on Error {
      message
    }
  }
  utilitiesForState(state: $state) {
    __typename
    ... on UtilityList {
      refs {
        id
        pid
        name
        shortName
      }
    }
    ... on Error {
      message
    }
  }
}
    `

export const useListUtilitiesQuery = <
  TData = Types.ListUtilitiesQuery,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ListUtilitiesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ListUtilitiesQuery, TError, TData>({
    queryKey: ["ListUtilities", variables],
    queryFn: publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
      ListUtilitiesDocument,
      variables,
    ),
    ...options,
  })
}

useListUtilitiesQuery.getKey = (variables: Types.ListUtilitiesQueryVariables) => [
  "ListUtilities",
  variables,
]

useListUtilitiesQuery.rootKey = "ListUtilities"

export const useSuspenseListUtilitiesQuery = <
  TData = Types.ListUtilitiesQuery,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ListUtilitiesQuery, TError, TData>({
    queryKey: ["ListUtilitiesSuspense", variables],
    queryFn: publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
      ListUtilitiesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilitiesSuspense", variables]

useSuspenseListUtilitiesQuery.rootKey = "ListUtilities"

export const useInfiniteListUtilitiesQuery = <
  TData = InfiniteData<Types.ListUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ListUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ListUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
            ListUtilitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilities.infinite", variables]

useInfiniteListUtilitiesQuery.rootKey = "ListUtilities.infinite"

export const useSuspenseInfiniteListUtilitiesQuery = <
  TData = InfiniteData<Types.ListUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ListUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ListUtilities.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
            ListUtilitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilities.infiniteSuspense", variables]

useSuspenseInfiniteListUtilitiesQuery.rootKey = "ListUtilities.infinite"

useListUtilitiesQuery.fetcher = (
  variables: Types.ListUtilitiesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
    ListUtilitiesDocument,
    variables,
    options,
  )

export const OrgSettingsDetailBySlugDocument = `
    query OrgSettingsDetailBySlug($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    name
    slug
    irrFlavor
    isDeveloper
    currentUserPermissions
    users {
      ...UserRef
    }
    portfolios {
      pid
      title
    }
    userRoles {
      user {
        ...UserRef
      }
      role
    }
    investorProfile {
      hq
      yearFounded
      website
      managementTeam
      description
      distinguishingFeatures
      investorBacking
      longTermOwnershipApproach
      subscriptionMgmtApproach
      committedFunding
      experiences {
        state
        ftmEnergizedPvKw
        ftmAwardedPvKw
        btmEnergizedPvKw
        btmAwardedPvKw
      }
      buyBoxes {
        state
        ftmMinPortfolioSize
        ftmMinProjectSize
        ftmSeekingProjects
        btmMinPortfolioSize
        btmMinProjectSize
        btmSeekingProjects
        utilityProgramNotes
      }
    }
  }
}
    ${UserRefFragmentDoc}`

export const useOrgSettingsDetailBySlugQuery = <
  TData = Types.OrgSettingsDetailBySlugQuery,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>({
    queryKey: ["OrgSettingsDetailBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSettingsDetailBySlugQuery,
      Types.OrgSettingsDetailBySlugQueryVariables
    >(OrgSettingsDetailBySlugDocument, variables),
    ...options,
  })
}

useOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug", variables]

useOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug"

export const useSuspenseOrgSettingsDetailBySlugQuery = <
  TData = Types.OrgSettingsDetailBySlugQuery,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>({
    queryKey: ["OrgSettingsDetailBySlugSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSettingsDetailBySlugQuery,
      Types.OrgSettingsDetailBySlugQueryVariables
    >(OrgSettingsDetailBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlugSuspense", variables]

useSuspenseOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug"

export const useInfiniteOrgSettingsDetailBySlugQuery = <
  TData = InfiniteData<Types.OrgSettingsDetailBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSettingsDetailBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSettingsDetailBySlugQuery,
            Types.OrgSettingsDetailBySlugQueryVariables
          >(OrgSettingsDetailBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug.infinite", variables]

useInfiniteOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug.infinite"

export const useSuspenseInfiniteOrgSettingsDetailBySlugQuery = <
  TData = InfiniteData<Types.OrgSettingsDetailBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "OrgSettingsDetailBySlug.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSettingsDetailBySlugQuery,
            Types.OrgSettingsDetailBySlugQueryVariables
          >(OrgSettingsDetailBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug.infiniteSuspense", variables]

useSuspenseInfiniteOrgSettingsDetailBySlugQuery.rootKey =
  "OrgSettingsDetailBySlug.infinite"

useOrgSettingsDetailBySlugQuery.fetcher = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OrgSettingsDetailBySlugQuery,
    Types.OrgSettingsDetailBySlugQueryVariables
  >(OrgSettingsDetailBySlugDocument, variables, options)

export const OrgUsersDocument = `
    query OrgUsers($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    name
    currentUserPermissions
    portfolios {
      pid
      title
    }
    orgUsers {
      user {
        pid
        name
        email
      }
      userRoles {
        role
        portfolio {
          pid
          title
        }
      }
    }
    unclaimedInvites {
      dateCreated
      createdBy {
        ...UserRef
      }
      portfolio {
        pid
        title
      }
      targetUser {
        ...UserRef
      }
    }
  }
}
    ${UserRefFragmentDoc}`

export const useOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgUsersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsers", variables],
    queryFn: publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers",
  variables,
]

useOrgUsersQuery.rootKey = "OrgUsers"

export const useSuspenseOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsersSuspense", variables],
    queryFn: publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsersSuspense",
  variables,
]

useSuspenseOrgUsersQuery.rootKey = "OrgUsers"

export const useInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infinite",
  variables,
]

useInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

export const useSuspenseInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infiniteSuspense",
  variables,
]

useSuspenseInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

useOrgUsersQuery.fetcher = (
  variables: Types.OrgUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
    OrgUsersDocument,
    variables,
    options,
  )

export const PortfolioUsersDocument = `
    query PortfolioUsers($portfolioPid: String!) {
  portfolio(pid: $portfolioPid) {
    pid
    title
    users {
      ...UserRef
    }
  }
}
    ${UserRefFragmentDoc}`

export const usePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsers", variables],
    queryFn: publicFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

usePortfolioUsersQuery.getKey = (variables: Types.PortfolioUsersQueryVariables) => [
  "PortfolioUsers",
  variables,
]

usePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useSuspensePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsersSuspense", variables],
    queryFn: publicFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

useSuspensePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsersSuspense", variables]

useSuspensePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infinite", variables]

useInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

export const useSuspenseInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infiniteSuspense", variables]

useSuspenseInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

usePortfolioUsersQuery.fetcher = (
  variables: Types.PortfolioUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
    PortfolioUsersDocument,
    variables,
    options,
  )

export const WelcomeDocument = `
    query Welcome($pid: String!) {
  openInvite(pid: $pid) {
    __typename
    ... on OpenInvite {
      pid
      orgSlug
      portfolio {
        pid
        title
      }
    }
    ... on Error {
      message
    }
  }
}
    `

export const useWelcomeQuery = <TData = Types.WelcomeQuery, TError = unknown>(
  variables: Types.WelcomeQueryVariables,
  options?: Omit<UseQueryOptions<Types.WelcomeQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.WelcomeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.WelcomeQuery, TError, TData>({
    queryKey: ["Welcome", variables],
    queryFn: publicFetcher<Types.WelcomeQuery, Types.WelcomeQueryVariables>(
      WelcomeDocument,
      variables,
    ),
    ...options,
  })
}

useWelcomeQuery.getKey = (variables: Types.WelcomeQueryVariables) => [
  "Welcome",
  variables,
]

useWelcomeQuery.rootKey = "Welcome"

export const useSuspenseWelcomeQuery = <TData = Types.WelcomeQuery, TError = unknown>(
  variables: Types.WelcomeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.WelcomeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.WelcomeQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.WelcomeQuery, TError, TData>({
    queryKey: ["WelcomeSuspense", variables],
    queryFn: publicFetcher<Types.WelcomeQuery, Types.WelcomeQueryVariables>(
      WelcomeDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseWelcomeQuery.getKey = (variables: Types.WelcomeQueryVariables) => [
  "WelcomeSuspense",
  variables,
]

useSuspenseWelcomeQuery.rootKey = "Welcome"

export const useInfiniteWelcomeQuery = <
  TData = InfiniteData<Types.WelcomeQuery>,
  TError = unknown,
>(
  variables: Types.WelcomeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.WelcomeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.WelcomeQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.WelcomeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Welcome.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.WelcomeQuery, Types.WelcomeQueryVariables>(
            WelcomeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteWelcomeQuery.getKey = (variables: Types.WelcomeQueryVariables) => [
  "Welcome.infinite",
  variables,
]

useInfiniteWelcomeQuery.rootKey = "Welcome.infinite"

export const useSuspenseInfiniteWelcomeQuery = <
  TData = InfiniteData<Types.WelcomeQuery>,
  TError = unknown,
>(
  variables: Types.WelcomeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.WelcomeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.WelcomeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.WelcomeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Welcome.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.WelcomeQuery, Types.WelcomeQueryVariables>(
            WelcomeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteWelcomeQuery.getKey = (variables: Types.WelcomeQueryVariables) => [
  "Welcome.infiniteSuspense",
  variables,
]

useSuspenseInfiniteWelcomeQuery.rootKey = "Welcome.infinite"

useWelcomeQuery.fetcher = (
  variables: Types.WelcomeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.WelcomeQuery, Types.WelcomeQueryVariables>(
    WelcomeDocument,
    variables,
    options,
  )

export const OrgSiteLabelsBySlugDocument = `
    query OrgSiteLabelsBySlug($orgSlug: String) {
  orgLabels(slug: $orgSlug) {
    ...SiteLabelRef
  }
}
    ${SiteLabelRefFragmentDoc}`

export const useOrgSiteLabelsBySlugQuery = <
  TData = Types.OrgSiteLabelsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteLabelsBySlug"]
        : ["OrgSiteLabelsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteLabelsBySlugQuery,
      Types.OrgSiteLabelsBySlugQueryVariables
    >(OrgSiteLabelsBySlugDocument, variables),
    ...options,
  })
}

useOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteLabelsBySlug"] : ["OrgSiteLabelsBySlug", variables]

useOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug"

export const useSuspenseOrgSiteLabelsBySlugQuery = <
  TData = Types.OrgSiteLabelsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteLabelsBySlugSuspense"]
        : ["OrgSiteLabelsBySlugSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSiteLabelsBySlugQuery,
      Types.OrgSiteLabelsBySlugQueryVariables
    >(OrgSiteLabelsBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlugSuspense"]
    : ["OrgSiteLabelsBySlugSuspense", variables]

useSuspenseOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug"

export const useInfiniteOrgSiteLabelsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteLabelsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteLabelsBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteLabelsBySlug.infinite"]
            : ["OrgSiteLabelsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteLabelsBySlugQuery,
            Types.OrgSiteLabelsBySlugQueryVariables
          >(OrgSiteLabelsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlug.infinite"]
    : ["OrgSiteLabelsBySlug.infinite", variables]

useInfiniteOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug.infinite"

export const useSuspenseInfiniteOrgSiteLabelsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteLabelsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteLabelsBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteLabelsBySlug.infiniteSuspense"]
            : ["OrgSiteLabelsBySlug.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteLabelsBySlugQuery,
            Types.OrgSiteLabelsBySlugQueryVariables
          >(OrgSiteLabelsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlug.infiniteSuspense"]
    : ["OrgSiteLabelsBySlug.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug.infinite"

useOrgSiteLabelsBySlugQuery.fetcher = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OrgSiteLabelsBySlugQuery,
    Types.OrgSiteLabelsBySlugQueryVariables
  >(OrgSiteLabelsBySlugDocument, variables, options)

export const OrgSiteDocsBySlugDocument = `
    query OrgSiteDocsBySlug($orgSlug: String) {
  orgSiteDocuments(slug: $orgSlug) {
    sitePid
    documents {
      ...Document
    }
  }
}
    ${DocumentFragmentDoc}`

export const useOrgSiteDocsBySlugQuery = <
  TData = Types.OrgSiteDocsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteDocsBySlug"]
        : ["OrgSiteDocsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteDocsBySlugQuery,
      Types.OrgSiteDocsBySlugQueryVariables
    >(OrgSiteDocsBySlugDocument, variables),
    ...options,
  })
}

useOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteDocsBySlug"] : ["OrgSiteDocsBySlug", variables]

useOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug"

export const useSuspenseOrgSiteDocsBySlugQuery = <
  TData = Types.OrgSiteDocsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteDocsBySlugSuspense"]
        : ["OrgSiteDocsBySlugSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSiteDocsBySlugQuery,
      Types.OrgSiteDocsBySlugQueryVariables
    >(OrgSiteDocsBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlugSuspense"]
    : ["OrgSiteDocsBySlugSuspense", variables]

useSuspenseOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug"

export const useInfiniteOrgSiteDocsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteDocsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteDocsBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteDocsBySlug.infinite"]
            : ["OrgSiteDocsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteDocsBySlugQuery,
            Types.OrgSiteDocsBySlugQueryVariables
          >(OrgSiteDocsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlug.infinite"]
    : ["OrgSiteDocsBySlug.infinite", variables]

useInfiniteOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug.infinite"

export const useSuspenseInfiniteOrgSiteDocsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteDocsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteDocsBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteDocsBySlug.infiniteSuspense"]
            : ["OrgSiteDocsBySlug.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteDocsBySlugQuery,
            Types.OrgSiteDocsBySlugQueryVariables
          >(OrgSiteDocsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlug.infiniteSuspense"]
    : ["OrgSiteDocsBySlug.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug.infinite"

useOrgSiteDocsBySlugQuery.fetcher = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteDocsBySlugQuery, Types.OrgSiteDocsBySlugQueryVariables>(
    OrgSiteDocsBySlugDocument,
    variables,
    options,
  )

export const GetVersionDocument = `
    query GetVersion {
  schemaVersion {
    version
  }
}
    `

export const useGetVersionQuery = <TData = Types.GetVersionQuery, TError = unknown>(
  variables?: Types.GetVersionQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetVersionQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetVersionQuery, TError, TData>({
    queryKey: variables === undefined ? ["GetVersion"] : ["GetVersion", variables],
    queryFn: publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
      GetVersionDocument,
      variables,
    ),
    ...options,
  })
}

useGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersion"] : ["GetVersion", variables]

useGetVersionQuery.rootKey = "GetVersion"

export const useSuspenseGetVersionQuery = <
  TData = Types.GetVersionQuery,
  TError = unknown,
>(
  variables?: Types.GetVersionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetVersionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetVersionSuspense"]
        : ["GetVersionSuspense", variables],
    queryFn: publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
      GetVersionDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersionSuspense"] : ["GetVersionSuspense", variables]

useSuspenseGetVersionQuery.rootKey = "GetVersion"

export const useInfiniteGetVersionQuery = <
  TData = InfiniteData<Types.GetVersionQuery>,
  TError = unknown,
>(
  variables: Types.GetVersionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetVersionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetVersion.infinite"]
            : ["GetVersion.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
            GetVersionDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersion.infinite"] : ["GetVersion.infinite", variables]

useInfiniteGetVersionQuery.rootKey = "GetVersion.infinite"

export const useSuspenseInfiniteGetVersionQuery = <
  TData = InfiniteData<Types.GetVersionQuery>,
  TError = unknown,
>(
  variables: Types.GetVersionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetVersionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetVersionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetVersion.infiniteSuspense"]
            : ["GetVersion.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
            GetVersionDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetVersionQuery.getKey = (
  variables?: Types.GetVersionQueryVariables,
) =>
  variables === undefined
    ? ["GetVersion.infiniteSuspense"]
    : ["GetVersion.infiniteSuspense", variables]

useSuspenseInfiniteGetVersionQuery.rootKey = "GetVersion.infinite"

useGetVersionQuery.fetcher = (
  variables?: Types.GetVersionQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
    GetVersionDocument,
    variables,
    options,
  )

export const CurrentUserDocument = `
    query CurrentUser {
  currentUser {
    pid
    isAuthenticated
    isAdmin
    email
    name
    org {
      pid
      slug
      name
      logoUrl
      logomarkUrl
      irrFlavor
      inactivityTimeoutMs
    }
    orgs {
      pid
      name
      slug
    }
  }
}
    `

export const useCurrentUserQuery = <TData = Types.CurrentUserQuery, TError = unknown>(
  variables?: Types.CurrentUserQueryVariables,
  options?: Omit<UseQueryOptions<Types.CurrentUserQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.CurrentUserQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CurrentUserQuery, TError, TData>({
    queryKey: variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables],
    queryFn: publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
      CurrentUserDocument,
      variables,
    ),
    ...options,
  })
}

useCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables]

useCurrentUserQuery.rootKey = "CurrentUser"

export const useSuspenseCurrentUserQuery = <
  TData = Types.CurrentUserQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentUserQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserSuspense"]
        : ["CurrentUserSuspense", variables],
    queryFn: publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
      CurrentUserDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined ? ["CurrentUserSuspense"] : ["CurrentUserSuspense", variables]

useSuspenseCurrentUserQuery.rootKey = "CurrentUser"

export const useInfiniteCurrentUserQuery = <
  TData = InfiniteData<Types.CurrentUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUser.infinite"]
            : ["CurrentUser.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
            CurrentUserDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined
    ? ["CurrentUser.infinite"]
    : ["CurrentUser.infinite", variables]

useInfiniteCurrentUserQuery.rootKey = "CurrentUser.infinite"

export const useSuspenseInfiniteCurrentUserQuery = <
  TData = InfiniteData<Types.CurrentUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CurrentUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUser.infiniteSuspense"]
            : ["CurrentUser.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
            CurrentUserDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentUserQuery.getKey = (
  variables?: Types.CurrentUserQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUser.infiniteSuspense"]
    : ["CurrentUser.infiniteSuspense", variables]

useSuspenseInfiniteCurrentUserQuery.rootKey = "CurrentUser.infinite"

useCurrentUserQuery.fetcher = (
  variables?: Types.CurrentUserQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
    CurrentUserDocument,
    variables,
    options,
  )

export const PropertyHubViewDocument = `
    query PropertyHubView($orgSlug: String!, $filterBy: OrgSiteTableFilter!, $sortBy: [OrgSiteTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    sites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const usePropertyHubViewQuery = <
  TData = Types.PropertyHubViewQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PropertyHubViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PropertyHubViewQuery, TError, TData>({
    queryKey: ["PropertyHubView", variables],
    queryFn: publicFetcher<
      Types.PropertyHubViewQuery,
      Types.PropertyHubViewQueryVariables
    >(PropertyHubViewDocument, variables),
    ...options,
  })
}

usePropertyHubViewQuery.getKey = (variables: Types.PropertyHubViewQueryVariables) => [
  "PropertyHubView",
  variables,
]

usePropertyHubViewQuery.rootKey = "PropertyHubView"

export const useSuspensePropertyHubViewQuery = <
  TData = Types.PropertyHubViewQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PropertyHubViewQuery, TError, TData>({
    queryKey: ["PropertyHubViewSuspense", variables],
    queryFn: publicFetcher<
      Types.PropertyHubViewQuery,
      Types.PropertyHubViewQueryVariables
    >(PropertyHubViewDocument, variables),
    ...options,
  })
}

useSuspensePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubViewSuspense", variables]

useSuspensePropertyHubViewQuery.rootKey = "PropertyHubView"

export const useInfinitePropertyHubViewQuery = <
  TData = InfiniteData<Types.PropertyHubViewQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PropertyHubViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubViewQuery,
            Types.PropertyHubViewQueryVariables
          >(PropertyHubViewDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubView.infinite", variables]

useInfinitePropertyHubViewQuery.rootKey = "PropertyHubView.infinite"

export const useSuspenseInfinitePropertyHubViewQuery = <
  TData = InfiniteData<Types.PropertyHubViewQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PropertyHubViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubViewQuery,
            Types.PropertyHubViewQueryVariables
          >(PropertyHubViewDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubView.infiniteSuspense", variables]

useSuspenseInfinitePropertyHubViewQuery.rootKey = "PropertyHubView.infinite"

usePropertyHubViewQuery.fetcher = (
  variables: Types.PropertyHubViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PropertyHubViewQuery, Types.PropertyHubViewQueryVariables>(
    PropertyHubViewDocument,
    variables,
    options,
  )

export const PropertyHubRowsDocument = `
    query PropertyHubRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    sites {
      rows(keys: $keys) {
        pid
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        ownerName
        customerId
        portfolio
        status
        statusReason
        statusCustomReason
        isFavorite
        flagSummary {
          __typename
          utilityNotFound
          tariffNotFound
          improbableEnergyCosts
          existingSolarLikely
          duplicateAddressFound
        }
        salePlans
        salePlannedYear
        mortgageLender
        sqft
        buildingType
        buildingClass
        numStories
        numUnitsTotal
        yearBuilt
        buildingFootprintSqft
        buildingFootprintRoofSqftError
        buildingSqftRoofSqftError
        buildingFootprintCount
        existingSolarDetectionScore
        inEnergyCommunity
        inLowIncomeCommunity
        roofCondition
        roofReplacedYr
        roofSqft
        usableRoofSqft
        metering
        loadOrigin
        utilityName
        utilityCagr
        utilityCagrFromYear
        utilityCagrToYear
        meterCount
        loadKwh
        loadUsd
        loadUsdPerKwh
        soleTariffName
        bauCo2EmissionsTons
        bauCo2EmissionsTonsPerMwh
        ppaOfftaker
        ppaSignatory
        occupancyType
        tenantLeaseEnds
        leaseType
        tenantInterest
        energyThings {
          canRooftopSolar
          canCarportSolar
          canGroundmountSolar
          canStorage
        }
        financingThings {
          canSelfInvest
          canLoan
          canPpa
          canLease
          canCommunitySolar
        }
        noiRangeMin
        noiRangeMax
        noiSelfMax
        irrSelfMax
        solarLocationSelfMax
        includeStorageSelfMax
        pvSizeKwSelfMax
        storageSizeKwSelfMax
        storageSizeKwhSelfMax
        leDevFeeSelfMax
        gpvSelfMax
        lifetimeCapexSelfMax
        lifetimeUtilitySavingsSelfMax
        co2EmissionsReductionTonnesSelfMax
        noiPpaMax
        solarLocationPpaMax
        includeStoragePpaMax
        pvSizeKwPpaMax
        storageSizeKwPpaMax
        storageSizeKwhPpaMax
        leDevFeePpaMax
        gpvPpaMax
        lifetimeUtilitySavingsPpaMax
        costPerKwhPpaMax
        ppaEscPpaMax
        yearOneOfftakerSavingsPpaMax
        co2EmissionsReductionTonnesPpaMax
        noiLoanMax
        leDevFeeLoanMax
        gpvLoanMax
        noiLeaseMax
        noiCsMax
        pvSizeKwCsMax
        roofMaxPvKw
        groundMaxPvKw
        carportMaxPvKw
        physicalCheck {
          roofIsGood
          carportIsGood
          groundmountIsGood
          existingSolar
          unobstructedFlatRoof
          badRoof
        }
        marketplaceStatus
        marketplaceClosedAt
        marketplaceCloseReason
        marketplaceCloseCustomReason
      }
    }
  }
}
    `

export const usePropertyHubRowsQuery = <
  TData = Types.PropertyHubRowsQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PropertyHubRowsQuery, TError, TData>({
    queryKey: ["PropertyHubRows", variables],
    queryFn: publicFetcher<
      Types.PropertyHubRowsQuery,
      Types.PropertyHubRowsQueryVariables
    >(PropertyHubRowsDocument, variables),
    ...options,
  })
}

usePropertyHubRowsQuery.getKey = (variables: Types.PropertyHubRowsQueryVariables) => [
  "PropertyHubRows",
  variables,
]

usePropertyHubRowsQuery.rootKey = "PropertyHubRows"

export const useSuspensePropertyHubRowsQuery = <
  TData = Types.PropertyHubRowsQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PropertyHubRowsQuery, TError, TData>({
    queryKey: ["PropertyHubRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.PropertyHubRowsQuery,
      Types.PropertyHubRowsQueryVariables
    >(PropertyHubRowsDocument, variables),
    ...options,
  })
}

useSuspensePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRowsSuspense", variables]

useSuspensePropertyHubRowsQuery.rootKey = "PropertyHubRows"

export const useInfinitePropertyHubRowsQuery = <
  TData = InfiniteData<Types.PropertyHubRowsQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PropertyHubRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubRowsQuery,
            Types.PropertyHubRowsQueryVariables
          >(PropertyHubRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRows.infinite", variables]

useInfinitePropertyHubRowsQuery.rootKey = "PropertyHubRows.infinite"

export const useSuspenseInfinitePropertyHubRowsQuery = <
  TData = InfiniteData<Types.PropertyHubRowsQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PropertyHubRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubRowsQuery,
            Types.PropertyHubRowsQueryVariables
          >(PropertyHubRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRows.infiniteSuspense", variables]

useSuspenseInfinitePropertyHubRowsQuery.rootKey = "PropertyHubRows.infinite"

usePropertyHubRowsQuery.fetcher = (
  variables: Types.PropertyHubRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PropertyHubRowsQuery, Types.PropertyHubRowsQueryVariables>(
    PropertyHubRowsDocument,
    variables,
    options,
  )

export const MarketplaceOrgPropertySearchDocument = `
    query MarketplaceOrgPropertySearch($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    slug
    marketplaceSiteSearchTerms {
      propertyName
      address
      pid
      packagePid
    }
  }
}
    `

export const useMarketplaceOrgPropertySearchQuery = <
  TData = Types.MarketplaceOrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceOrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceOrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceOrgPropertySearchQuery, TError, TData>({
    queryKey: ["MarketplaceOrgPropertySearch", variables],
    queryFn: publicFetcher<
      Types.MarketplaceOrgPropertySearchQuery,
      Types.MarketplaceOrgPropertySearchQueryVariables
    >(MarketplaceOrgPropertySearchDocument, variables),
    ...options,
  })
}

useMarketplaceOrgPropertySearchQuery.getKey = (
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
) => ["MarketplaceOrgPropertySearch", variables]

useMarketplaceOrgPropertySearchQuery.rootKey = "MarketplaceOrgPropertySearch"

export const useSuspenseMarketplaceOrgPropertySearchQuery = <
  TData = Types.MarketplaceOrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceOrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceOrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceOrgPropertySearchQuery, TError, TData>({
    queryKey: ["MarketplaceOrgPropertySearchSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceOrgPropertySearchQuery,
      Types.MarketplaceOrgPropertySearchQueryVariables
    >(MarketplaceOrgPropertySearchDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceOrgPropertySearchQuery.getKey = (
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
) => ["MarketplaceOrgPropertySearchSuspense", variables]

useSuspenseMarketplaceOrgPropertySearchQuery.rootKey = "MarketplaceOrgPropertySearch"

export const useInfiniteMarketplaceOrgPropertySearchQuery = <
  TData = InfiniteData<Types.MarketplaceOrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceOrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceOrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceOrgPropertySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceOrgPropertySearch.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceOrgPropertySearchQuery,
            Types.MarketplaceOrgPropertySearchQueryVariables
          >(MarketplaceOrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceOrgPropertySearchQuery.getKey = (
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
) => ["MarketplaceOrgPropertySearch.infinite", variables]

useInfiniteMarketplaceOrgPropertySearchQuery.rootKey =
  "MarketplaceOrgPropertySearch.infinite"

export const useSuspenseInfiniteMarketplaceOrgPropertySearchQuery = <
  TData = InfiniteData<Types.MarketplaceOrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceOrgPropertySearchQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceOrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MarketplaceOrgPropertySearchQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceOrgPropertySearch.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceOrgPropertySearchQuery,
            Types.MarketplaceOrgPropertySearchQueryVariables
          >(MarketplaceOrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceOrgPropertySearchQuery.getKey = (
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
) => ["MarketplaceOrgPropertySearch.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceOrgPropertySearchQuery.rootKey =
  "MarketplaceOrgPropertySearch.infinite"

useMarketplaceOrgPropertySearchQuery.fetcher = (
  variables: Types.MarketplaceOrgPropertySearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceOrgPropertySearchQuery,
    Types.MarketplaceOrgPropertySearchQueryVariables
  >(MarketplaceOrgPropertySearchDocument, variables, options)

export const OrgPropertySearchDocument = `
    query OrgPropertySearch($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    slug
    siteSearchTerms {
      propertyName
      address
      pid
    }
  }
}
    `

export const useOrgPropertySearchQuery = <
  TData = Types.OrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgPropertySearchQuery, TError, TData>({
    queryKey: ["OrgPropertySearch", variables],
    queryFn: publicFetcher<
      Types.OrgPropertySearchQuery,
      Types.OrgPropertySearchQueryVariables
    >(OrgPropertySearchDocument, variables),
    ...options,
  })
}

useOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch", variables]

useOrgPropertySearchQuery.rootKey = "OrgPropertySearch"

export const useSuspenseOrgPropertySearchQuery = <
  TData = Types.OrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgPropertySearchQuery, TError, TData>({
    queryKey: ["OrgPropertySearchSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgPropertySearchQuery,
      Types.OrgPropertySearchQueryVariables
    >(OrgPropertySearchDocument, variables),
    ...options,
  })
}

useSuspenseOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearchSuspense", variables]

useSuspenseOrgPropertySearchQuery.rootKey = "OrgPropertySearch"

export const useInfiniteOrgPropertySearchQuery = <
  TData = InfiniteData<Types.OrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgPropertySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPropertySearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgPropertySearchQuery,
            Types.OrgPropertySearchQueryVariables
          >(OrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch.infinite", variables]

useInfiniteOrgPropertySearchQuery.rootKey = "OrgPropertySearch.infinite"

export const useSuspenseInfiniteOrgPropertySearchQuery = <
  TData = InfiniteData<Types.OrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgPropertySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPropertySearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgPropertySearchQuery,
            Types.OrgPropertySearchQueryVariables
          >(OrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch.infiniteSuspense", variables]

useSuspenseInfiniteOrgPropertySearchQuery.rootKey = "OrgPropertySearch.infinite"

useOrgPropertySearchQuery.fetcher = (
  variables: Types.OrgPropertySearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgPropertySearchQuery, Types.OrgPropertySearchQueryVariables>(
    OrgPropertySearchDocument,
    variables,
    options,
  )

export const OrgHomepageDocument = `
    query OrgHomepage($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    logoUrl
    portfolioPdfReportHref
    mapView {
      pid
      lat
      lon
    }
  }
}
    `

export const useOrgHomepageQuery = <TData = Types.OrgHomepageQuery, TError = unknown>(
  variables: Types.OrgHomepageQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgHomepageQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgHomepageQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgHomepageQuery, TError, TData>({
    queryKey: ["OrgHomepage", variables],
    queryFn: publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
      OrgHomepageDocument,
      variables,
    ),
    ...options,
  })
}

useOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepage",
  variables,
]

useOrgHomepageQuery.rootKey = "OrgHomepage"

export const useSuspenseOrgHomepageQuery = <
  TData = Types.OrgHomepageQuery,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgHomepageQuery, TError, TData>({
    queryKey: ["OrgHomepageSuspense", variables],
    queryFn: publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
      OrgHomepageDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepageSuspense",
  variables,
]

useSuspenseOrgHomepageQuery.rootKey = "OrgHomepage"

export const useInfiniteOrgHomepageQuery = <
  TData = InfiniteData<Types.OrgHomepageQuery>,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgHomepageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgHomepage.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
            OrgHomepageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepage.infinite",
  variables,
]

useInfiniteOrgHomepageQuery.rootKey = "OrgHomepage.infinite"

export const useSuspenseInfiniteOrgHomepageQuery = <
  TData = InfiniteData<Types.OrgHomepageQuery>,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgHomepageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgHomepage.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
            OrgHomepageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgHomepageQuery.getKey = (
  variables: Types.OrgHomepageQueryVariables,
) => ["OrgHomepage.infiniteSuspense", variables]

useSuspenseInfiniteOrgHomepageQuery.rootKey = "OrgHomepage.infinite"

useOrgHomepageQuery.fetcher = (
  variables: Types.OrgHomepageQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
    OrgHomepageDocument,
    variables,
    options,
  )

export const OrgFacetsDocument = `
    query OrgFacets($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    sites {
      view {
        stats {
          portfolios
          coveredStates
          buildingClasses
          buildingTypes
          counties
          cbsaNames
          assetManagers
          utilityNames
          ownerNames
        }
      }
    }
    finproducts {
      id
      type
      name
    }
  }
}
    `

export const useOrgFacetsQuery = <TData = Types.OrgFacetsQuery, TError = unknown>(
  variables: Types.OrgFacetsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgFacetsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgFacetsQuery, TError, TData>({
    queryKey: ["OrgFacets", variables],
    queryFn: publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
      OrgFacetsDocument,
      variables,
    ),
    ...options,
  })
}

useOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacets",
  variables,
]

useOrgFacetsQuery.rootKey = "OrgFacets"

export const useSuspenseOrgFacetsQuery = <
  TData = Types.OrgFacetsQuery,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgFacetsQuery, TError, TData>({
    queryKey: ["OrgFacetsSuspense", variables],
    queryFn: publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
      OrgFacetsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacetsSuspense",
  variables,
]

useSuspenseOrgFacetsQuery.rootKey = "OrgFacets"

export const useInfiniteOrgFacetsQuery = <
  TData = InfiniteData<Types.OrgFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
            OrgFacetsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacets.infinite",
  variables,
]

useInfiniteOrgFacetsQuery.rootKey = "OrgFacets.infinite"

export const useSuspenseInfiniteOrgFacetsQuery = <
  TData = InfiniteData<Types.OrgFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgFacets.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
            OrgFacetsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgFacetsQuery.getKey = (
  variables: Types.OrgFacetsQueryVariables,
) => ["OrgFacets.infiniteSuspense", variables]

useSuspenseInfiniteOrgFacetsQuery.rootKey = "OrgFacets.infinite"

useOrgFacetsQuery.fetcher = (
  variables: Types.OrgFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
    OrgFacetsDocument,
    variables,
    options,
  )

export const OrgSiteTableDocument = `
    query OrgSiteTable($orgSlug: String!, $filterBy: OrgSiteTableFilter, $sortBy: [OrgSiteTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    sites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          annualNoi
          co2EmissionsReductionTonnes
          maxPvSizeKw
        }
      }
    }
  }
}
    `

export const useOrgSiteTableQuery = <TData = Types.OrgSiteTableQuery, TError = unknown>(
  variables: Types.OrgSiteTableQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteTableQuery, TError, TData>({
    queryKey: ["OrgSiteTable", variables],
    queryFn: publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
      OrgSiteTableDocument,
      variables,
    ),
    ...options,
  })
}

useOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTable",
  variables,
]

useOrgSiteTableQuery.rootKey = "OrgSiteTable"

export const useSuspenseOrgSiteTableQuery = <
  TData = Types.OrgSiteTableQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteTableQuery, TError, TData>({
    queryKey: ["OrgSiteTableSuspense", variables],
    queryFn: publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
      OrgSiteTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTableSuspense",
  variables,
]

useSuspenseOrgSiteTableQuery.rootKey = "OrgSiteTable"

export const useInfiniteOrgSiteTableQuery = <
  TData = InfiniteData<Types.OrgSiteTableQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
            OrgSiteTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTable.infinite",
  variables,
]

useInfiniteOrgSiteTableQuery.rootKey = "OrgSiteTable.infinite"

export const useSuspenseInfiniteOrgSiteTableQuery = <
  TData = InfiniteData<Types.OrgSiteTableQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTable.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
            OrgSiteTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteTableQuery.getKey = (
  variables: Types.OrgSiteTableQueryVariables,
) => ["OrgSiteTable.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteTableQuery.rootKey = "OrgSiteTable.infinite"

useOrgSiteTableQuery.fetcher = (
  variables: Types.OrgSiteTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
    OrgSiteTableDocument,
    variables,
    options,
  )

export const PortfolioPdfReportStatsDocument = `
    query PortfolioPdfReportStats($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    mapView {
      pid
      lat
      lon
    }
    sites {
      view(
        filterBy: {status: {value: ACTIVE}}
        sortBy: [{attr: NOI_CS_MAX, order: DESC}]
      ) {
        keys
        count
        stats {
          annualNoi
          maxPvSizeKw
          co2EmissionsReductionTonnes
        }
      }
      highlight: view(
        filterBy: {status: {value: ACTIVE}, irrSelfMax: {gte: 0.1}, noiPpaMax: {gte: 0}, noiCsMax: {gte: 0}}
        sortBy: [{attr: NOI_CS_MAX, order: DESC}]
      ) {
        keys
        count
      }
      cs: view(
        filterBy: {status: {value: ACTIVE}, noiCsMax: {gte: 20000}}
        sortBy: []
      ) {
        count
        stats {
          csAnnualNoi
          csMaxPvSizeKw
        }
      }
      csAfter: view(
        filterBy: {status: {value: ACTIVE}, noiCsMax: {gte: 33333}}
        sortBy: []
      ) {
        count
        stats {
          csAnnualNoi
          csMaxPvSizeKw
        }
      }
      ppa: view(filterBy: {status: {value: ACTIVE}, noiPpaMax: {gte: 0}}, sortBy: []) {
        count
        stats {
          ppaAnnualNoi
          ppaMaxPvSizeKw
          ppaCo2EmissionsReductionTonnes
        }
      }
      cash: view(
        filterBy: {status: {value: ACTIVE}, irrSelfMax: {gte: 0.1}}
        sortBy: []
      ) {
        count
        stats {
          cashAnnualNoi
          cashMaxPvSizeKw
          cashCo2EmissionsReductionTonnes
        }
      }
    }
    bestCsSite {
      pid
      name
      streetAddress
      city
      state
      zipCode
      noiCsMax
    }
  }
}
    `

export const usePortfolioPdfReportStatsQuery = <
  TData = Types.PortfolioPdfReportStatsQuery,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>({
    queryKey: ["PortfolioPdfReportStats", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfReportStatsQuery,
      Types.PortfolioPdfReportStatsQueryVariables
    >(PortfolioPdfReportStatsDocument, variables),
    ...options,
  })
}

usePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats", variables]

usePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats"

export const useSuspensePortfolioPdfReportStatsQuery = <
  TData = Types.PortfolioPdfReportStatsQuery,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>({
    queryKey: ["PortfolioPdfReportStatsSuspense", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfReportStatsQuery,
      Types.PortfolioPdfReportStatsQueryVariables
    >(PortfolioPdfReportStatsDocument, variables),
    ...options,
  })
}

useSuspensePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStatsSuspense", variables]

useSuspensePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats"

export const useInfinitePortfolioPdfReportStatsQuery = <
  TData = InfiniteData<Types.PortfolioPdfReportStatsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioPdfReportStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfReportStatsQuery,
            Types.PortfolioPdfReportStatsQueryVariables
          >(PortfolioPdfReportStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats.infinite", variables]

useInfinitePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats.infinite"

export const useSuspenseInfinitePortfolioPdfReportStatsQuery = <
  TData = InfiniteData<Types.PortfolioPdfReportStatsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "PortfolioPdfReportStats.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfReportStatsQuery,
            Types.PortfolioPdfReportStatsQueryVariables
          >(PortfolioPdfReportStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats.infiniteSuspense", variables]

useSuspenseInfinitePortfolioPdfReportStatsQuery.rootKey =
  "PortfolioPdfReportStats.infinite"

usePortfolioPdfReportStatsQuery.fetcher = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PortfolioPdfReportStatsQuery,
    Types.PortfolioPdfReportStatsQueryVariables
  >(PortfolioPdfReportStatsDocument, variables, options)

export const OrgSiteTableRowsDocument = `
    query OrgSiteTableRows($orgSlug: String!, $propertyPids: [String!]!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    sites {
      rows(keys: $propertyPids) {
        pid
        name
        portfolio
        isFavorite
        streetAddress
        city
        state
        zipCode
        noiRangeMin
        noiRangeMax
        latitude
        longitude
        irrSelfMax
        noiLoanMax
        noiPpaMax
        noiCsMax
        energyThings {
          canRooftopSolar
          canCarportSolar
          canGroundmountSolar
          canStorage
        }
        financingThings {
          canSelfInvest
          canLoan
          canPpa
          canLease
          canCommunitySolar
        }
        footprint {
          latitude
          longitude
          roofShape
          roofShapeGeojson
        }
      }
    }
  }
}
    `

export const useOrgSiteTableRowsQuery = <
  TData = Types.OrgSiteTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteTableRowsQuery, TError, TData>({
    queryKey: ["OrgSiteTableRows", variables],
    queryFn: publicFetcher<
      Types.OrgSiteTableRowsQuery,
      Types.OrgSiteTableRowsQueryVariables
    >(OrgSiteTableRowsDocument, variables),
    ...options,
  })
}

useOrgSiteTableRowsQuery.getKey = (variables: Types.OrgSiteTableRowsQueryVariables) => [
  "OrgSiteTableRows",
  variables,
]

useOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows"

export const useSuspenseOrgSiteTableRowsQuery = <
  TData = Types.OrgSiteTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteTableRowsQuery, TError, TData>({
    queryKey: ["OrgSiteTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSiteTableRowsQuery,
      Types.OrgSiteTableRowsQueryVariables
    >(OrgSiteTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRowsSuspense", variables]

useSuspenseOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows"

export const useInfiniteOrgSiteTableRowsQuery = <
  TData = InfiniteData<Types.OrgSiteTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteTableRowsQuery,
            Types.OrgSiteTableRowsQueryVariables
          >(OrgSiteTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRows.infinite", variables]

useInfiniteOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows.infinite"

export const useSuspenseInfiniteOrgSiteTableRowsQuery = <
  TData = InfiniteData<Types.OrgSiteTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteTableRowsQuery,
            Types.OrgSiteTableRowsQueryVariables
          >(OrgSiteTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRows.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows.infinite"

useOrgSiteTableRowsQuery.fetcher = (
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteTableRowsQuery, Types.OrgSiteTableRowsQueryVariables>(
    OrgSiteTableRowsDocument,
    variables,
    options,
  )

export const RevampSiteDocument = `
    query RevampSite($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    site(pid: $sitePid) {
      ...RevampSiteBasics
      status
      statusDisplayText
      flags {
        type
      }
      feasibility {
        economics
        physical
      }
      labels {
        name
        pid
        themeColor
      }
      biddingConfigResult {
        id
      }
      selectedConfigResult {
        ...Result
      }
      selectedResults {
        ...Result
      }
      highlightedResults {
        ...Result
      }
      activeResults {
        ...Result
      }
      resultPerLocation {
        ...ResultRef
        helioscope {
          ...Helioscope
        }
      }
    }
  }
}
    ${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${UserRefFragmentDoc}
${FootprintFragmentDoc}
${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}
${ResultRefFragmentDoc}`

export const useRevampSiteQuery = <TData = Types.RevampSiteQuery, TError = unknown>(
  variables: Types.RevampSiteQueryVariables,
  options?: Omit<UseQueryOptions<Types.RevampSiteQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteQuery, TError, TData>({
    queryKey: ["RevampSite", variables],
    queryFn: publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
      RevampSiteDocument,
      variables,
    ),
    ...options,
  })
}

useRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSite",
  variables,
]

useRevampSiteQuery.rootKey = "RevampSite"

export const useSuspenseRevampSiteQuery = <
  TData = Types.RevampSiteQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteQuery, TError, TData>({
    queryKey: ["RevampSiteSuspense", variables],
    queryFn: publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
      RevampSiteDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSiteSuspense",
  variables,
]

useSuspenseRevampSiteQuery.rootKey = "RevampSite"

export const useInfiniteRevampSiteQuery = <
  TData = InfiniteData<Types.RevampSiteQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSite.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
            RevampSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSite.infinite",
  variables,
]

useInfiniteRevampSiteQuery.rootKey = "RevampSite.infinite"

export const useSuspenseInfiniteRevampSiteQuery = <
  TData = InfiniteData<Types.RevampSiteQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSite.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
            RevampSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteQuery.getKey = (
  variables: Types.RevampSiteQueryVariables,
) => ["RevampSite.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteQuery.rootKey = "RevampSite.infinite"

useRevampSiteQuery.fetcher = (
  variables: Types.RevampSiteQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
    RevampSiteDocument,
    variables,
    options,
  )

export const ResultDetailDocument = `
    query ResultDetail($orgSlug: String!, $sitePid: String!, $resultPid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    name
    site(pid: $sitePid) {
      pid
      name
      roofMaxPvKw
      footprint {
        ...Footprint
      }
      result(pid: $resultPid) {
        ...Result
      }
    }
  }
}
    ${FootprintFragmentDoc}
${UserRefFragmentDoc}
${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}`

export const useResultDetailQuery = <TData = Types.ResultDetailQuery, TError = unknown>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetail", variables],
    queryFn: publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail",
  variables,
]

useResultDetailQuery.rootKey = "ResultDetail"

export const useSuspenseResultDetailQuery = <
  TData = Types.ResultDetailQuery,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetailSuspense", variables],
    queryFn: publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetailSuspense",
  variables,
]

useSuspenseResultDetailQuery.rootKey = "ResultDetail"

export const useInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail.infinite",
  variables,
]

useInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

export const useSuspenseInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultDetailQuery.getKey = (
  variables: Types.ResultDetailQueryVariables,
) => ["ResultDetail.infiniteSuspense", variables]

useSuspenseInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

useResultDetailQuery.fetcher = (
  variables: Types.ResultDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
    ResultDetailDocument,
    variables,
    options,
  )

export const ResultCashFlowDocument = `
    query ResultCashFlow($orgSlug: String!, $sitePid: String!, $resultPid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      result(pid: $resultPid) {
        result {
          __typename
          ... on RevampFinmodel {
            buildingOwnerXlsx
          }
        }
      }
    }
  }
}
    `

export const useResultCashFlowQuery = <
  TData = Types.ResultCashFlowQuery,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultCashFlowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultCashFlowQuery, TError, TData>({
    queryKey: ["ResultCashFlow", variables],
    queryFn: publicFetcher<
      Types.ResultCashFlowQuery,
      Types.ResultCashFlowQueryVariables
    >(ResultCashFlowDocument, variables),
    ...options,
  })
}

useResultCashFlowQuery.getKey = (variables: Types.ResultCashFlowQueryVariables) => [
  "ResultCashFlow",
  variables,
]

useResultCashFlowQuery.rootKey = "ResultCashFlow"

export const useSuspenseResultCashFlowQuery = <
  TData = Types.ResultCashFlowQuery,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultCashFlowQuery, TError, TData>({
    queryKey: ["ResultCashFlowSuspense", variables],
    queryFn: publicFetcher<
      Types.ResultCashFlowQuery,
      Types.ResultCashFlowQueryVariables
    >(ResultCashFlowDocument, variables),
    ...options,
  })
}

useSuspenseResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlowSuspense", variables]

useSuspenseResultCashFlowQuery.rootKey = "ResultCashFlow"

export const useInfiniteResultCashFlowQuery = <
  TData = InfiniteData<Types.ResultCashFlowQuery>,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultCashFlowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultCashFlow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
            ResultCashFlowDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlow.infinite", variables]

useInfiniteResultCashFlowQuery.rootKey = "ResultCashFlow.infinite"

export const useSuspenseInfiniteResultCashFlowQuery = <
  TData = InfiniteData<Types.ResultCashFlowQuery>,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultCashFlowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultCashFlow.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
            ResultCashFlowDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlow.infiniteSuspense", variables]

useSuspenseInfiniteResultCashFlowQuery.rootKey = "ResultCashFlow.infinite"

useResultCashFlowQuery.fetcher = (
  variables: Types.ResultCashFlowQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
    ResultCashFlowDocument,
    variables,
    options,
  )

export const RevampSiteDocumentsDocument = `
    query RevampSiteDocuments($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    site(pid: $sitePid) {
      pid
      documents {
        canDelete
        contentType
        dateCreated
        fileName
        ownerName
        pid
        tag
        downloadHref
      }
    }
  }
}
    `

export const useRevampSiteDocumentsQuery = <
  TData = Types.RevampSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteDocumentsQuery, TError, TData>({
    queryKey: ["RevampSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.RevampSiteDocumentsQuery,
      Types.RevampSiteDocumentsQueryVariables
    >(RevampSiteDocumentsDocument, variables),
    ...options,
  })
}

useRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments", variables]

useRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments"

export const useSuspenseRevampSiteDocumentsQuery = <
  TData = Types.RevampSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteDocumentsQuery, TError, TData>({
    queryKey: ["RevampSiteDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteDocumentsQuery,
      Types.RevampSiteDocumentsQueryVariables
    >(RevampSiteDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocumentsSuspense", variables]

useSuspenseRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments"

export const useInfiniteRevampSiteDocumentsQuery = <
  TData = InfiniteData<Types.RevampSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteDocumentsQuery,
            Types.RevampSiteDocumentsQueryVariables
          >(RevampSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments.infinite", variables]

useInfiniteRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments.infinite"

export const useSuspenseInfiniteRevampSiteDocumentsQuery = <
  TData = InfiniteData<Types.RevampSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteDocumentsQuery,
            Types.RevampSiteDocumentsQueryVariables
          >(RevampSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments.infinite"

useRevampSiteDocumentsQuery.fetcher = (
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteDocumentsQuery,
    Types.RevampSiteDocumentsQueryVariables
  >(RevampSiteDocumentsDocument, variables, options)

export const RevampSiteBelowTheFoldDocument = `
    query RevampSiteBelowTheFold($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    name
    pid
    site(pid: $sitePid) {
      pid
      recommendedResult {
        ...Result
        ...ResultWithEnergyData
      }
      policies {
        ...PolicyBrief
      }
    }
  }
}
    ${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}
${ResultWithEnergyDataFragmentDoc}
${PolicyBriefFragmentDoc}`

export const useRevampSiteBelowTheFoldQuery = <
  TData = Types.RevampSiteBelowTheFoldQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>({
    queryKey: ["RevampSiteBelowTheFold", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBelowTheFoldQuery,
      Types.RevampSiteBelowTheFoldQueryVariables
    >(RevampSiteBelowTheFoldDocument, variables),
    ...options,
  })
}

useRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold", variables]

useRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold"

export const useSuspenseRevampSiteBelowTheFoldQuery = <
  TData = Types.RevampSiteBelowTheFoldQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>({
    queryKey: ["RevampSiteBelowTheFoldSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBelowTheFoldQuery,
      Types.RevampSiteBelowTheFoldQueryVariables
    >(RevampSiteBelowTheFoldDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFoldSuspense", variables]

useSuspenseRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold"

export const useInfiniteRevampSiteBelowTheFoldQuery = <
  TData = InfiniteData<Types.RevampSiteBelowTheFoldQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBelowTheFold.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBelowTheFoldQuery,
            Types.RevampSiteBelowTheFoldQueryVariables
          >(RevampSiteBelowTheFoldDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold.infinite", variables]

useInfiniteRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold.infinite"

export const useSuspenseInfiniteRevampSiteBelowTheFoldQuery = <
  TData = InfiniteData<Types.RevampSiteBelowTheFoldQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteBelowTheFold.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBelowTheFoldQuery,
            Types.RevampSiteBelowTheFoldQueryVariables
          >(RevampSiteBelowTheFoldDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteBelowTheFoldQuery.rootKey =
  "RevampSiteBelowTheFold.infinite"

useRevampSiteBelowTheFoldQuery.fetcher = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteBelowTheFoldQuery,
    Types.RevampSiteBelowTheFoldQueryVariables
  >(RevampSiteBelowTheFoldDocument, variables, options)

export const RevampSiteBillingMetersDocument = `
    query RevampSiteBillingMeters($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      billingMeters {
        ...BillingMeter
      }
    }
  }
}
    ${BillingMeterFragmentDoc}
${FancyFloatFragmentDoc}
${BillingMonthViewFragmentDoc}
${ProviderFragmentDoc}
${UserRefFragmentDoc}
${BillingMeterProjectedFragmentDoc}
${BillGroupFragmentDoc}
${GroupedLineItemFragmentDoc}
${BillingIntervalViewFragmentDoc}`

export const useRevampSiteBillingMetersQuery = <
  TData = Types.RevampSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteBillingMetersQuery, TError, TData>({
    queryKey: ["RevampSiteBillingMeters", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBillingMetersQuery,
      Types.RevampSiteBillingMetersQueryVariables
    >(RevampSiteBillingMetersDocument, variables),
    ...options,
  })
}

useRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters", variables]

useRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters"

export const useSuspenseRevampSiteBillingMetersQuery = <
  TData = Types.RevampSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteBillingMetersQuery, TError, TData>({
    queryKey: ["RevampSiteBillingMetersSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBillingMetersQuery,
      Types.RevampSiteBillingMetersQueryVariables
    >(RevampSiteBillingMetersDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMetersSuspense", variables]

useSuspenseRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters"

export const useInfiniteRevampSiteBillingMetersQuery = <
  TData = InfiniteData<Types.RevampSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBillingMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBillingMetersQuery,
            Types.RevampSiteBillingMetersQueryVariables
          >(RevampSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters.infinite", variables]

useInfiniteRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters.infinite"

export const useSuspenseInfiniteRevampSiteBillingMetersQuery = <
  TData = InfiniteData<Types.RevampSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteBillingMeters.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBillingMetersQuery,
            Types.RevampSiteBillingMetersQueryVariables
          >(RevampSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteBillingMetersQuery.rootKey =
  "RevampSiteBillingMeters.infinite"

useRevampSiteBillingMetersQuery.fetcher = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteBillingMetersQuery,
    Types.RevampSiteBillingMetersQueryVariables
  >(RevampSiteBillingMetersDocument, variables, options)

export const RevampSiteGridEscalatorDocument = `
    query RevampSiteGridEscalator($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      gridEscalator
    }
  }
}
    `

export const useRevampSiteGridEscalatorQuery = <
  TData = Types.RevampSiteGridEscalatorQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>({
    queryKey: ["RevampSiteGridEscalator", variables],
    queryFn: publicFetcher<
      Types.RevampSiteGridEscalatorQuery,
      Types.RevampSiteGridEscalatorQueryVariables
    >(RevampSiteGridEscalatorDocument, variables),
    ...options,
  })
}

useRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator", variables]

useRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator"

export const useSuspenseRevampSiteGridEscalatorQuery = <
  TData = Types.RevampSiteGridEscalatorQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>({
    queryKey: ["RevampSiteGridEscalatorSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteGridEscalatorQuery,
      Types.RevampSiteGridEscalatorQueryVariables
    >(RevampSiteGridEscalatorDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalatorSuspense", variables]

useSuspenseRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator"

export const useInfiniteRevampSiteGridEscalatorQuery = <
  TData = InfiniteData<Types.RevampSiteGridEscalatorQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteGridEscalator.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteGridEscalatorQuery,
            Types.RevampSiteGridEscalatorQueryVariables
          >(RevampSiteGridEscalatorDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator.infinite", variables]

useInfiniteRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator.infinite"

export const useSuspenseInfiniteRevampSiteGridEscalatorQuery = <
  TData = InfiniteData<Types.RevampSiteGridEscalatorQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteGridEscalator.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteGridEscalatorQuery,
            Types.RevampSiteGridEscalatorQueryVariables
          >(RevampSiteGridEscalatorDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteGridEscalatorQuery.rootKey =
  "RevampSiteGridEscalator.infinite"

useRevampSiteGridEscalatorQuery.fetcher = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteGridEscalatorQuery,
    Types.RevampSiteGridEscalatorQueryVariables
  >(RevampSiteGridEscalatorDocument, variables, options)

export const SiteMetersDocument = `
    query SiteMeters($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      billingMeters {
        ...BillingMeterOption
      }
    }
  }
}
    ${BillingMeterOptionFragmentDoc}`

export const useSiteMetersQuery = <TData = Types.SiteMetersQuery, TError = unknown>(
  variables: Types.SiteMetersQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteMetersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteMetersQuery, TError, TData>({
    queryKey: ["SiteMeters", variables],
    queryFn: publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
      SiteMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMeters",
  variables,
]

useSiteMetersQuery.rootKey = "SiteMeters"

export const useSuspenseSiteMetersQuery = <
  TData = Types.SiteMetersQuery,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteMetersQuery, TError, TData>({
    queryKey: ["SiteMetersSuspense", variables],
    queryFn: publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
      SiteMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMetersSuspense",
  variables,
]

useSuspenseSiteMetersQuery.rootKey = "SiteMeters"

export const useInfiniteSiteMetersQuery = <
  TData = InfiniteData<Types.SiteMetersQuery>,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
            SiteMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMeters.infinite",
  variables,
]

useInfiniteSiteMetersQuery.rootKey = "SiteMeters.infinite"

export const useSuspenseInfiniteSiteMetersQuery = <
  TData = InfiniteData<Types.SiteMetersQuery>,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMeters.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
            SiteMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteMetersQuery.getKey = (
  variables: Types.SiteMetersQueryVariables,
) => ["SiteMeters.infiniteSuspense", variables]

useSuspenseInfiniteSiteMetersQuery.rootKey = "SiteMeters.infinite"

useSiteMetersQuery.fetcher = (
  variables: Types.SiteMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
    SiteMetersDocument,
    variables,
    options,
  )

export const BillingMeterDocumentParseRequestsDocument = `
    query BillingMeterDocumentParseRequests($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      documentParseRequests {
        ...DocumentParseRequest
      }
    }
  }
}
    ${DocumentParseRequestFragmentDoc}`

export const useBillingMeterDocumentParseRequestsQuery = <
  TData = Types.BillingMeterDocumentParseRequestsQuery,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>({
    queryKey: ["BillingMeterDocumentParseRequests", variables],
    queryFn: publicFetcher<
      Types.BillingMeterDocumentParseRequestsQuery,
      Types.BillingMeterDocumentParseRequestsQueryVariables
    >(BillingMeterDocumentParseRequestsDocument, variables),
    ...options,
  })
}

useBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests", variables]

useBillingMeterDocumentParseRequestsQuery.rootKey = "BillingMeterDocumentParseRequests"

export const useSuspenseBillingMeterDocumentParseRequestsQuery = <
  TData = Types.BillingMeterDocumentParseRequestsQuery,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>({
    queryKey: ["BillingMeterDocumentParseRequestsSuspense", variables],
    queryFn: publicFetcher<
      Types.BillingMeterDocumentParseRequestsQuery,
      Types.BillingMeterDocumentParseRequestsQueryVariables
    >(BillingMeterDocumentParseRequestsDocument, variables),
    ...options,
  })
}

useSuspenseBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequestsSuspense", variables]

useSuspenseBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests"

export const useInfiniteBillingMeterDocumentParseRequestsQuery = <
  TData = InfiniteData<Types.BillingMeterDocumentParseRequestsQuery>,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BillingMeterDocumentParseRequests.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BillingMeterDocumentParseRequestsQuery,
            Types.BillingMeterDocumentParseRequestsQueryVariables
          >(BillingMeterDocumentParseRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests.infinite", variables]

useInfiniteBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests.infinite"

export const useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery = <
  TData = InfiniteData<Types.BillingMeterDocumentParseRequestsQuery>,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.BillingMeterDocumentParseRequestsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BillingMeterDocumentParseRequests.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BillingMeterDocumentParseRequestsQuery,
            Types.BillingMeterDocumentParseRequestsQueryVariables
          >(BillingMeterDocumentParseRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests.infiniteSuspense", variables]

useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests.infinite"

useBillingMeterDocumentParseRequestsQuery.fetcher = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BillingMeterDocumentParseRequestsQuery,
    Types.BillingMeterDocumentParseRequestsQueryVariables
  >(BillingMeterDocumentParseRequestsDocument, variables, options)

export const OrgDetailDocument = `
    query OrgDetail($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    slug
    name
    logomarkUrl
  }
}
    `

export const useOrgDetailQuery = <TData = Types.OrgDetailQuery, TError = unknown>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgDetailQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetail", variables],
    queryFn: publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail",
  variables,
]

useOrgDetailQuery.rootKey = "OrgDetail"

export const useSuspenseOrgDetailQuery = <
  TData = Types.OrgDetailQuery,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetailSuspense", variables],
    queryFn: publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetailSuspense",
  variables,
]

useSuspenseOrgDetailQuery.rootKey = "OrgDetail"

export const useInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail.infinite",
  variables,
]

useInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

export const useSuspenseInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgDetailQuery.getKey = (
  variables: Types.OrgDetailQueryVariables,
) => ["OrgDetail.infiniteSuspense", variables]

useSuspenseInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

useOrgDetailQuery.fetcher = (
  variables: Types.OrgDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
    OrgDetailDocument,
    variables,
    options,
  )

export const MultiMeterDetailDocument = `
    query MultiMeterDetail($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    site(pid: $sitePid) {
      ...SiteMeterSet
    }
  }
}
    ${SiteMeterSetFragmentDoc}
${SiteBauMeterFragmentDoc}
${FormattedBillFragmentDoc}
${BillSectionFragmentDoc}
${DecodedRateFragmentDoc}
${CostOrTaxItemFragmentDoc}`

export const useMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetail", variables],
    queryFn: publicFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useMultiMeterDetailQuery.getKey = (variables: Types.MultiMeterDetailQueryVariables) => [
  "MultiMeterDetail",
  variables,
]

useMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useSuspenseMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useSuspenseMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetailSuspense", variables]

useSuspenseMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infinite", variables]

useInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

export const useSuspenseInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infiniteSuspense", variables]

useSuspenseInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

useMultiMeterDetailQuery.fetcher = (
  variables: Types.MultiMeterDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MultiMeterDetailQuery, Types.MultiMeterDetailQueryVariables>(
    MultiMeterDetailDocument,
    variables,
    options,
  )

export const OrgBundlesDocument = `
    query OrgBundles($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    logoUrl
    bundles {
      ...BundleListItem
      rows {
        ...BundleRowDetail
      }
    }
  }
}
    ${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}
${BundleRowDetailFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}`

export const useOrgBundlesQuery = <TData = Types.OrgBundlesQuery, TError = unknown>(
  variables: Types.OrgBundlesQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgBundlesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgBundlesQuery, TError, TData>({
    queryKey: ["OrgBundles", variables],
    queryFn: publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
      OrgBundlesDocument,
      variables,
    ),
    ...options,
  })
}

useOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundles",
  variables,
]

useOrgBundlesQuery.rootKey = "OrgBundles"

export const useSuspenseOrgBundlesQuery = <
  TData = Types.OrgBundlesQuery,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgBundlesQuery, TError, TData>({
    queryKey: ["OrgBundlesSuspense", variables],
    queryFn: publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
      OrgBundlesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundlesSuspense",
  variables,
]

useSuspenseOrgBundlesQuery.rootKey = "OrgBundles"

export const useInfiniteOrgBundlesQuery = <
  TData = InfiniteData<Types.OrgBundlesQuery>,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgBundlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgBundles.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
            OrgBundlesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundles.infinite",
  variables,
]

useInfiniteOrgBundlesQuery.rootKey = "OrgBundles.infinite"

export const useSuspenseInfiniteOrgBundlesQuery = <
  TData = InfiniteData<Types.OrgBundlesQuery>,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgBundlesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgBundlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgBundles.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
            OrgBundlesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgBundlesQuery.getKey = (
  variables: Types.OrgBundlesQueryVariables,
) => ["OrgBundles.infiniteSuspense", variables]

useSuspenseInfiniteOrgBundlesQuery.rootKey = "OrgBundles.infinite"

useOrgBundlesQuery.fetcher = (
  variables: Types.OrgBundlesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
    OrgBundlesDocument,
    variables,
    options,
  )

export const SiteBidsDocument = `
    query SiteBids($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      bidPackages {
        ...BasicBidPackage
        row(pid: $sitePid) {
          status
          closedAt
          closeReason
          closeCustomReason
        }
      }
      selectedConfigResult {
        ...Result
      }
      biddingConfigResult {
        ...Result
      }
    }
  }
}
    ${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}
${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}`

export const useSiteBidsQuery = <TData = Types.SiteBidsQuery, TError = unknown>(
  variables: Types.SiteBidsQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteBidsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsQuery, TError, TData>({
    queryKey: ["SiteBids", variables],
    queryFn: publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
      SiteBidsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids",
  variables,
]

useSiteBidsQuery.rootKey = "SiteBids"

export const useSuspenseSiteBidsQuery = <TData = Types.SiteBidsQuery, TError = unknown>(
  variables: Types.SiteBidsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsQuery, TError, TData>({
    queryKey: ["SiteBidsSuspense", variables],
    queryFn: publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
      SiteBidsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBidsSuspense",
  variables,
]

useSuspenseSiteBidsQuery.rootKey = "SiteBids"

export const useInfiniteSiteBidsQuery = <
  TData = InfiniteData<Types.SiteBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
            SiteBidsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids.infinite",
  variables,
]

useInfiniteSiteBidsQuery.rootKey = "SiteBids.infinite"

export const useSuspenseInfiniteSiteBidsQuery = <
  TData = InfiniteData<Types.SiteBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBids.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
            SiteBidsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSiteBidsQuery.rootKey = "SiteBids.infinite"

useSiteBidsQuery.fetcher = (
  variables: Types.SiteBidsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
    SiteBidsDocument,
    variables,
    options,
  )

export const FinancierBundleRowDocument = `
    query FinancierBundleRow($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleRowForFinancier(
    orgSlug: $orgSlug
    bundlePid: $bundlePid
    sitePid: $sitePid
  ) {
    ...BundleRow
    helioscopes {
      ...Helioscope
    }
  }
  isAwardee(orgSlug: $orgSlug, bundlePid: $bundlePid, sitePid: $sitePid)
}
    ${BundleRowFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${UserRefFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}
${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}`

export const useFinancierBundleRowQuery = <
  TData = Types.FinancierBundleRowQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinancierBundleRowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinancierBundleRowQuery, TError, TData>({
    queryKey: ["FinancierBundleRow", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowQuery,
      Types.FinancierBundleRowQueryVariables
    >(FinancierBundleRowDocument, variables),
    ...options,
  })
}

useFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRow", variables]

useFinancierBundleRowQuery.rootKey = "FinancierBundleRow"

export const useSuspenseFinancierBundleRowQuery = <
  TData = Types.FinancierBundleRowQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierBundleRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierBundleRowQuery, TError, TData>({
    queryKey: ["FinancierBundleRowSuspense", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowQuery,
      Types.FinancierBundleRowQueryVariables
    >(FinancierBundleRowDocument, variables),
    ...options,
  })
}

useSuspenseFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRowSuspense", variables]

useSuspenseFinancierBundleRowQuery.rootKey = "FinancierBundleRow"

export const useInfiniteFinancierBundleRowQuery = <
  TData = InfiniteData<Types.FinancierBundleRowQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierBundleRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierBundleRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinancierBundleRow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowQuery,
            Types.FinancierBundleRowQueryVariables
          >(FinancierBundleRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRow.infinite", variables]

useInfiniteFinancierBundleRowQuery.rootKey = "FinancierBundleRow.infinite"

export const useSuspenseInfiniteFinancierBundleRowQuery = <
  TData = InfiniteData<Types.FinancierBundleRowQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinancierBundleRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinancierBundleRow.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowQuery,
            Types.FinancierBundleRowQueryVariables
          >(FinancierBundleRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRow.infiniteSuspense", variables]

useSuspenseInfiniteFinancierBundleRowQuery.rootKey = "FinancierBundleRow.infinite"

useFinancierBundleRowQuery.fetcher = (
  variables: Types.FinancierBundleRowQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinancierBundleRowQuery, Types.FinancierBundleRowQueryVariables>(
    FinancierBundleRowDocument,
    variables,
    options,
  )

export const FinancierMultiMeterDetailDocument = `
    query FinancierMultiMeterDetail($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleRowForFinancier(
    orgSlug: $orgSlug
    bundlePid: $bundlePid
    sitePid: $sitePid
  ) {
    site {
      ...SiteMeterSet
    }
  }
}
    ${SiteMeterSetFragmentDoc}
${SiteBauMeterFragmentDoc}
${FormattedBillFragmentDoc}
${BillSectionFragmentDoc}
${DecodedRateFragmentDoc}
${CostOrTaxItemFragmentDoc}`

export const useFinancierMultiMeterDetailQuery = <
  TData = Types.FinancierMultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.FinancierMultiMeterDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierMultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinancierMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinancierMultiMeterDetailQuery, TError, TData>({
    queryKey: ["FinancierMultiMeterDetail", variables],
    queryFn: publicFetcher<
      Types.FinancierMultiMeterDetailQuery,
      Types.FinancierMultiMeterDetailQueryVariables
    >(FinancierMultiMeterDetailDocument, variables),
    ...options,
  })
}

useFinancierMultiMeterDetailQuery.getKey = (
  variables: Types.FinancierMultiMeterDetailQueryVariables,
) => ["FinancierMultiMeterDetail", variables]

useFinancierMultiMeterDetailQuery.rootKey = "FinancierMultiMeterDetail"

export const useSuspenseFinancierMultiMeterDetailQuery = <
  TData = Types.FinancierMultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.FinancierMultiMeterDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierMultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierMultiMeterDetailQuery, TError, TData>({
    queryKey: ["FinancierMultiMeterDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.FinancierMultiMeterDetailQuery,
      Types.FinancierMultiMeterDetailQueryVariables
    >(FinancierMultiMeterDetailDocument, variables),
    ...options,
  })
}

useSuspenseFinancierMultiMeterDetailQuery.getKey = (
  variables: Types.FinancierMultiMeterDetailQueryVariables,
) => ["FinancierMultiMeterDetailSuspense", variables]

useSuspenseFinancierMultiMeterDetailQuery.rootKey = "FinancierMultiMeterDetail"

export const useInfiniteFinancierMultiMeterDetailQuery = <
  TData = InfiniteData<Types.FinancierMultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinancierMultiMeterDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierMultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierMultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinancierMultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierMultiMeterDetailQuery,
            Types.FinancierMultiMeterDetailQueryVariables
          >(FinancierMultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierMultiMeterDetailQuery.getKey = (
  variables: Types.FinancierMultiMeterDetailQueryVariables,
) => ["FinancierMultiMeterDetail.infinite", variables]

useInfiniteFinancierMultiMeterDetailQuery.rootKey = "FinancierMultiMeterDetail.infinite"

export const useSuspenseInfiniteFinancierMultiMeterDetailQuery = <
  TData = InfiniteData<Types.FinancierMultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinancierMultiMeterDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.FinancierMultiMeterDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierMultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinancierMultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierMultiMeterDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierMultiMeterDetailQuery,
            Types.FinancierMultiMeterDetailQueryVariables
          >(FinancierMultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierMultiMeterDetailQuery.getKey = (
  variables: Types.FinancierMultiMeterDetailQueryVariables,
) => ["FinancierMultiMeterDetail.infiniteSuspense", variables]

useSuspenseInfiniteFinancierMultiMeterDetailQuery.rootKey =
  "FinancierMultiMeterDetail.infinite"

useFinancierMultiMeterDetailQuery.fetcher = (
  variables: Types.FinancierMultiMeterDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinancierMultiMeterDetailQuery,
    Types.FinancierMultiMeterDetailQueryVariables
  >(FinancierMultiMeterDetailDocument, variables, options)

export const FinancierBundleRowDocumentsDocument = `
    query FinancierBundleRowDocuments($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleRowForFinancier(
    orgSlug: $orgSlug
    bundlePid: $bundlePid
    sitePid: $sitePid
  ) {
    site {
      pid
      documents {
        ...Document
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useFinancierBundleRowDocumentsQuery = <
  TData = Types.FinancierBundleRowDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierBundleRowDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinancierBundleRowDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleRowDocuments", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowDocumentsQuery,
      Types.FinancierBundleRowDocumentsQueryVariables
    >(FinancierBundleRowDocumentsDocument, variables),
    ...options,
  })
}

useFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocuments", variables]

useFinancierBundleRowDocumentsQuery.rootKey = "FinancierBundleRowDocuments"

export const useSuspenseFinancierBundleRowDocumentsQuery = <
  TData = Types.FinancierBundleRowDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierBundleRowDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierBundleRowDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleRowDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowDocumentsQuery,
      Types.FinancierBundleRowDocumentsQueryVariables
    >(FinancierBundleRowDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocumentsSuspense", variables]

useSuspenseFinancierBundleRowDocumentsQuery.rootKey = "FinancierBundleRowDocuments"

export const useInfiniteFinancierBundleRowDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleRowDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierBundleRowDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierBundleRowDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleRowDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowDocumentsQuery,
            Types.FinancierBundleRowDocumentsQueryVariables
          >(FinancierBundleRowDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocuments.infinite", variables]

useInfiniteFinancierBundleRowDocumentsQuery.rootKey =
  "FinancierBundleRowDocuments.infinite"

export const useSuspenseInfiniteFinancierBundleRowDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleRowDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.FinancierBundleRowDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleRowDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowDocumentsQuery,
            Types.FinancierBundleRowDocumentsQueryVariables
          >(FinancierBundleRowDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocuments.infiniteSuspense", variables]

useSuspenseInfiniteFinancierBundleRowDocumentsQuery.rootKey =
  "FinancierBundleRowDocuments.infinite"

useFinancierBundleRowDocumentsQuery.fetcher = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinancierBundleRowDocumentsQuery,
    Types.FinancierBundleRowDocumentsQueryVariables
  >(FinancierBundleRowDocumentsDocument, variables, options)

export const SidebarDataDocument = `
    query SidebarData($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    sites {
      view {
        count
      }
    }
    shine {
      stats {
        numProperties
      }
    }
    isDeveloper
  }
}
    `

export const useSidebarDataQuery = <TData = Types.SidebarDataQuery, TError = unknown>(
  variables: Types.SidebarDataQueryVariables,
  options?: Omit<UseQueryOptions<Types.SidebarDataQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SidebarDataQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SidebarDataQuery, TError, TData>({
    queryKey: ["SidebarData", variables],
    queryFn: publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
      SidebarDataDocument,
      variables,
    ),
    ...options,
  })
}

useSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarData",
  variables,
]

useSidebarDataQuery.rootKey = "SidebarData"

export const useSuspenseSidebarDataQuery = <
  TData = Types.SidebarDataQuery,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SidebarDataQuery, TError, TData>({
    queryKey: ["SidebarDataSuspense", variables],
    queryFn: publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
      SidebarDataDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarDataSuspense",
  variables,
]

useSuspenseSidebarDataQuery.rootKey = "SidebarData"

export const useInfiniteSidebarDataQuery = <
  TData = InfiniteData<Types.SidebarDataQuery>,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SidebarDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SidebarData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
            SidebarDataDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarData.infinite",
  variables,
]

useInfiniteSidebarDataQuery.rootKey = "SidebarData.infinite"

export const useSuspenseInfiniteSidebarDataQuery = <
  TData = InfiniteData<Types.SidebarDataQuery>,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SidebarDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SidebarData.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
            SidebarDataDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSidebarDataQuery.getKey = (
  variables: Types.SidebarDataQueryVariables,
) => ["SidebarData.infiniteSuspense", variables]

useSuspenseInfiniteSidebarDataQuery.rootKey = "SidebarData.infinite"

useSidebarDataQuery.fetcher = (
  variables: Types.SidebarDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
    SidebarDataDocument,
    variables,
    options,
  )

export const ThirdPartySuppliersDocument = `
    query ThirdPartySuppliers {
  thirdPartySuppliers {
    id
    pid
    name
  }
}
    `

export const useThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliers"]
        : ["ThirdPartySuppliers", variables],
    queryFn: publicFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined ? ["ThirdPartySuppliers"] : ["ThirdPartySuppliers", variables]

useThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useSuspenseThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliersSuspense"]
        : ["ThirdPartySuppliersSuspense", variables],
    queryFn: publicFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useSuspenseThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliersSuspense"]
    : ["ThirdPartySuppliersSuspense", variables]

useSuspenseThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infinite"]
            : ["ThirdPartySuppliers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infinite"]
    : ["ThirdPartySuppliers.infinite", variables]

useInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

export const useSuspenseInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infiniteSuspense"]
            : ["ThirdPartySuppliers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infiniteSuspense"]
    : ["ThirdPartySuppliers.infiniteSuspense", variables]

useSuspenseInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

useThirdPartySuppliersQuery.fetcher = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ThirdPartySuppliersQuery,
    Types.ThirdPartySuppliersQueryVariables
  >(ThirdPartySuppliersDocument, variables, options)

export const ShineSiteDetailDocument = `
    query ShineSiteDetail($orgSlug: String!, $propertyPid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $propertyPid) {
      pid
      name
      fullAddress
      footprint {
        ...Footprint
      }
      shine {
        status
        stats {
          ...ShineStats
        }
      }
    }
  }
}
    ${FootprintFragmentDoc}
${UserRefFragmentDoc}
${ShineStatsFragmentDoc}`

export const useShineSiteDetailQuery = <
  TData = Types.ShineSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ShineSiteDetailQuery, TError, TData>({
    queryKey: ["ShineSiteDetail", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailQuery,
      Types.ShineSiteDetailQueryVariables
    >(ShineSiteDetailDocument, variables),
    ...options,
  })
}

useShineSiteDetailQuery.getKey = (variables: Types.ShineSiteDetailQueryVariables) => [
  "ShineSiteDetail",
  variables,
]

useShineSiteDetailQuery.rootKey = "ShineSiteDetail"

export const useSuspenseShineSiteDetailQuery = <
  TData = Types.ShineSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ShineSiteDetailQuery, TError, TData>({
    queryKey: ["ShineSiteDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailQuery,
      Types.ShineSiteDetailQueryVariables
    >(ShineSiteDetailDocument, variables),
    ...options,
  })
}

useSuspenseShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetailSuspense", variables]

useSuspenseShineSiteDetailQuery.rootKey = "ShineSiteDetail"

export const useInfiniteShineSiteDetailQuery = <
  TData = InfiniteData<Types.ShineSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ShineSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailQuery,
            Types.ShineSiteDetailQueryVariables
          >(ShineSiteDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetail.infinite", variables]

useInfiniteShineSiteDetailQuery.rootKey = "ShineSiteDetail.infinite"

export const useSuspenseInfiniteShineSiteDetailQuery = <
  TData = InfiniteData<Types.ShineSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ShineSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailQuery,
            Types.ShineSiteDetailQueryVariables
          >(ShineSiteDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetail.infiniteSuspense", variables]

useSuspenseInfiniteShineSiteDetailQuery.rootKey = "ShineSiteDetail.infinite"

useShineSiteDetailQuery.fetcher = (
  variables: Types.ShineSiteDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ShineSiteDetailQuery, Types.ShineSiteDetailQueryVariables>(
    ShineSiteDetailDocument,
    variables,
    options,
  )

export const OrgShineDocument = `
    query OrgShine($orgSlug: String!, $duration: ShineDuration!) {
  orgBySlug(slug: $orgSlug) {
    name
    slug
    shine {
      status
      stats {
        ...ShineStats
      }
      units(duration: $duration) {
        ...ShineUnit
      }
      siteRefs {
        pid
        lat
        lon
        monthlyGrossRevenue
        status
      }
    }
  }
}
    ${ShineStatsFragmentDoc}
${ShineUnitFragmentDoc}`

export const useOrgShineQuery = <TData = Types.OrgShineQuery, TError = unknown>(
  variables: Types.OrgShineQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgShineQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgShineQuery, TError, TData>({
    queryKey: ["OrgShine", variables],
    queryFn: publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
      OrgShineDocument,
      variables,
    ),
    ...options,
  })
}

useOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine",
  variables,
]

useOrgShineQuery.rootKey = "OrgShine"

export const useSuspenseOrgShineQuery = <TData = Types.OrgShineQuery, TError = unknown>(
  variables: Types.OrgShineQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgShineQuery, TError, TData>({
    queryKey: ["OrgShineSuspense", variables],
    queryFn: publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
      OrgShineDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShineSuspense",
  variables,
]

useSuspenseOrgShineQuery.rootKey = "OrgShine"

export const useInfiniteOrgShineQuery = <
  TData = InfiniteData<Types.OrgShineQuery>,
  TError = unknown,
>(
  variables: Types.OrgShineQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgShineQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgShine.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
            OrgShineDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine.infinite",
  variables,
]

useInfiniteOrgShineQuery.rootKey = "OrgShine.infinite"

export const useSuspenseInfiniteOrgShineQuery = <
  TData = InfiniteData<Types.OrgShineQuery>,
  TError = unknown,
>(
  variables: Types.OrgShineQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgShineQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgShineQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgShine.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
            OrgShineDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine.infiniteSuspense",
  variables,
]

useSuspenseInfiniteOrgShineQuery.rootKey = "OrgShine.infinite"

useOrgShineQuery.fetcher = (
  variables: Types.OrgShineQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
    OrgShineDocument,
    variables,
    options,
  )

export const ShineSiteDetailUnitsDocument = `
    query ShineSiteDetailUnits($orgSlug: String!, $propertyPid: String!, $duration: ShineDuration!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $propertyPid) {
      pid
      shine {
        units(duration: $duration) {
          ...ShineUnit
        }
      }
    }
  }
}
    ${ShineUnitFragmentDoc}`

export const useShineSiteDetailUnitsQuery = <
  TData = Types.ShineSiteDetailUnitsQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>({
    queryKey: ["ShineSiteDetailUnits", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailUnitsQuery,
      Types.ShineSiteDetailUnitsQueryVariables
    >(ShineSiteDetailUnitsDocument, variables),
    ...options,
  })
}

useShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits", variables]

useShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits"

export const useSuspenseShineSiteDetailUnitsQuery = <
  TData = Types.ShineSiteDetailUnitsQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>({
    queryKey: ["ShineSiteDetailUnitsSuspense", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailUnitsQuery,
      Types.ShineSiteDetailUnitsQueryVariables
    >(ShineSiteDetailUnitsDocument, variables),
    ...options,
  })
}

useSuspenseShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnitsSuspense", variables]

useSuspenseShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits"

export const useInfiniteShineSiteDetailUnitsQuery = <
  TData = InfiniteData<Types.ShineSiteDetailUnitsQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetailUnits.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailUnitsQuery,
            Types.ShineSiteDetailUnitsQueryVariables
          >(ShineSiteDetailUnitsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits.infinite", variables]

useInfiniteShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits.infinite"

export const useSuspenseInfiniteShineSiteDetailUnitsQuery = <
  TData = InfiniteData<Types.ShineSiteDetailUnitsQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ShineSiteDetailUnits.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailUnitsQuery,
            Types.ShineSiteDetailUnitsQueryVariables
          >(ShineSiteDetailUnitsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits.infiniteSuspense", variables]

useSuspenseInfiniteShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits.infinite"

useShineSiteDetailUnitsQuery.fetcher = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ShineSiteDetailUnitsQuery,
    Types.ShineSiteDetailUnitsQueryVariables
  >(ShineSiteDetailUnitsDocument, variables, options)

export const SiteSolarPanelVisualDocument = `
    query SiteSolarPanelVisual($orgSlug: String!, $propertyPid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $propertyPid) {
      visuals {
        ...SiteVisuals
      }
    }
  }
}
    ${SiteVisualsFragmentDoc}`

export const useSiteSolarPanelVisualQuery = <
  TData = Types.SiteSolarPanelVisualQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteSolarPanelVisualQuery, TError, TData>({
    queryKey: ["SiteSolarPanelVisual", variables],
    queryFn: publicFetcher<
      Types.SiteSolarPanelVisualQuery,
      Types.SiteSolarPanelVisualQueryVariables
    >(SiteSolarPanelVisualDocument, variables),
    ...options,
  })
}

useSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual", variables]

useSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual"

export const useSuspenseSiteSolarPanelVisualQuery = <
  TData = Types.SiteSolarPanelVisualQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteSolarPanelVisualQuery, TError, TData>({
    queryKey: ["SiteSolarPanelVisualSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteSolarPanelVisualQuery,
      Types.SiteSolarPanelVisualQueryVariables
    >(SiteSolarPanelVisualDocument, variables),
    ...options,
  })
}

useSuspenseSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisualSuspense", variables]

useSuspenseSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual"

export const useInfiniteSiteSolarPanelVisualQuery = <
  TData = InfiniteData<Types.SiteSolarPanelVisualQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteSolarPanelVisualQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteSolarPanelVisual.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarPanelVisualQuery,
            Types.SiteSolarPanelVisualQueryVariables
          >(SiteSolarPanelVisualDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual.infinite", variables]

useInfiniteSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual.infinite"

export const useSuspenseInfiniteSiteSolarPanelVisualQuery = <
  TData = InfiniteData<Types.SiteSolarPanelVisualQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteSolarPanelVisualQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteSolarPanelVisual.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarPanelVisualQuery,
            Types.SiteSolarPanelVisualQueryVariables
          >(SiteSolarPanelVisualDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual.infiniteSuspense", variables]

useSuspenseInfiniteSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual.infinite"

useSiteSolarPanelVisualQuery.fetcher = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteSolarPanelVisualQuery,
    Types.SiteSolarPanelVisualQueryVariables
  >(SiteSolarPanelVisualDocument, variables, options)

export const CsvImportsListDocument = `
    query CsvImportsList($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    csvImports {
      all {
        pid
        fileName
        createdAt
        createdBy {
          email
        }
        portfolio {
          pid
          title
        }
        rowStats {
          total
          imported
        }
      }
    }
  }
}
    `

export const useCsvImportsListQuery = <
  TData = Types.CsvImportsListQuery,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsvImportsListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsvImportsListQuery, TError, TData>({
    queryKey: ["CsvImportsList", variables],
    queryFn: publicFetcher<
      Types.CsvImportsListQuery,
      Types.CsvImportsListQueryVariables
    >(CsvImportsListDocument, variables),
    ...options,
  })
}

useCsvImportsListQuery.getKey = (variables: Types.CsvImportsListQueryVariables) => [
  "CsvImportsList",
  variables,
]

useCsvImportsListQuery.rootKey = "CsvImportsList"

export const useSuspenseCsvImportsListQuery = <
  TData = Types.CsvImportsListQuery,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsvImportsListQuery, TError, TData>({
    queryKey: ["CsvImportsListSuspense", variables],
    queryFn: publicFetcher<
      Types.CsvImportsListQuery,
      Types.CsvImportsListQueryVariables
    >(CsvImportsListDocument, variables),
    ...options,
  })
}

useSuspenseCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsListSuspense", variables]

useSuspenseCsvImportsListQuery.rootKey = "CsvImportsList"

export const useInfiniteCsvImportsListQuery = <
  TData = InfiniteData<Types.CsvImportsListQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsvImportsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportsList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
            CsvImportsListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsList.infinite", variables]

useInfiniteCsvImportsListQuery.rootKey = "CsvImportsList.infinite"

export const useSuspenseInfiniteCsvImportsListQuery = <
  TData = InfiniteData<Types.CsvImportsListQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsvImportsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportsList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
            CsvImportsListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsList.infiniteSuspense", variables]

useSuspenseInfiniteCsvImportsListQuery.rootKey = "CsvImportsList.infinite"

useCsvImportsListQuery.fetcher = (
  variables: Types.CsvImportsListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
    CsvImportsListDocument,
    variables,
    options,
  )

export const SiteEvDocument = `
    query SiteEV($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $sitePid) {
      evCalc {
        ...BasicEVCalc
      }
      fullAddress
      yearBuilt
      numParkingSpaces
      buildingClass
      numUnitsTotal
    }
  }
}
    ${BasicEvCalcFragmentDoc}`

export const useSiteEvQuery = <TData = Types.SiteEvQuery, TError = unknown>(
  variables: Types.SiteEvQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteEvQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEvQuery, TError, TData>({
    queryKey: ["SiteEV", variables],
    queryFn: publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
      SiteEvDocument,
      variables,
    ),
    ...options,
  })
}

useSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => ["SiteEV", variables]

useSiteEvQuery.rootKey = "SiteEV"

export const useSuspenseSiteEvQuery = <TData = Types.SiteEvQuery, TError = unknown>(
  variables: Types.SiteEvQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEvQuery, TError, TData>({
    queryKey: ["SiteEVSuspense", variables],
    queryFn: publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
      SiteEvDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEVSuspense",
  variables,
]

useSuspenseSiteEvQuery.rootKey = "SiteEV"

export const useInfiniteSiteEvQuery = <
  TData = InfiniteData<Types.SiteEvQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEV.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(SiteEvDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEV.infinite",
  variables,
]

useInfiniteSiteEvQuery.rootKey = "SiteEV.infinite"

export const useSuspenseInfiniteSiteEvQuery = <
  TData = InfiniteData<Types.SiteEvQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEV.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(SiteEvDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEV.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSiteEvQuery.rootKey = "SiteEV.infinite"

useSiteEvQuery.fetcher = (
  variables: Types.SiteEvQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
    SiteEvDocument,
    variables,
    options,
  )

export const SiteEvIncentivesDocument = `
    query SiteEvIncentives($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      evCalc {
        incentives {
          programName
          state
          url
          eligibleAreas
          incentiveAmount
          programConstraints
          incentiveCap
          calculation
        }
      }
    }
  }
}
    `

export const useSiteEvIncentivesQuery = <
  TData = Types.SiteEvIncentivesQuery,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEvIncentivesQuery, TError, TData>({
    queryKey: ["SiteEvIncentives", variables],
    queryFn: publicFetcher<
      Types.SiteEvIncentivesQuery,
      Types.SiteEvIncentivesQueryVariables
    >(SiteEvIncentivesDocument, variables),
    ...options,
  })
}

useSiteEvIncentivesQuery.getKey = (variables: Types.SiteEvIncentivesQueryVariables) => [
  "SiteEvIncentives",
  variables,
]

useSiteEvIncentivesQuery.rootKey = "SiteEvIncentives"

export const useSuspenseSiteEvIncentivesQuery = <
  TData = Types.SiteEvIncentivesQuery,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEvIncentivesQuery, TError, TData>({
    queryKey: ["SiteEvIncentivesSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteEvIncentivesQuery,
      Types.SiteEvIncentivesQueryVariables
    >(SiteEvIncentivesDocument, variables),
    ...options,
  })
}

useSuspenseSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentivesSuspense", variables]

useSuspenseSiteEvIncentivesQuery.rootKey = "SiteEvIncentives"

export const useInfiniteSiteEvIncentivesQuery = <
  TData = InfiniteData<Types.SiteEvIncentivesQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEvIncentivesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEvIncentives.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteEvIncentivesQuery,
            Types.SiteEvIncentivesQueryVariables
          >(SiteEvIncentivesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentives.infinite", variables]

useInfiniteSiteEvIncentivesQuery.rootKey = "SiteEvIncentives.infinite"

export const useSuspenseInfiniteSiteEvIncentivesQuery = <
  TData = InfiniteData<Types.SiteEvIncentivesQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEvIncentivesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEvIncentives.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteEvIncentivesQuery,
            Types.SiteEvIncentivesQueryVariables
          >(SiteEvIncentivesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentives.infiniteSuspense", variables]

useSuspenseInfiniteSiteEvIncentivesQuery.rootKey = "SiteEvIncentives.infinite"

useSiteEvIncentivesQuery.fetcher = (
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEvIncentivesQuery, Types.SiteEvIncentivesQueryVariables>(
    SiteEvIncentivesDocument,
    variables,
    options,
  )

export const SiteRecalculatingDocument = `
    query SiteRecalculating($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      isRecalculating
    }
  }
}
    `

export const useSiteRecalculatingQuery = <
  TData = Types.SiteRecalculatingQuery,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteRecalculatingQuery, TError, TData>({
    queryKey: ["SiteRecalculating", variables],
    queryFn: publicFetcher<
      Types.SiteRecalculatingQuery,
      Types.SiteRecalculatingQueryVariables
    >(SiteRecalculatingDocument, variables),
    ...options,
  })
}

useSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating", variables]

useSiteRecalculatingQuery.rootKey = "SiteRecalculating"

export const useSuspenseSiteRecalculatingQuery = <
  TData = Types.SiteRecalculatingQuery,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteRecalculatingQuery, TError, TData>({
    queryKey: ["SiteRecalculatingSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteRecalculatingQuery,
      Types.SiteRecalculatingQueryVariables
    >(SiteRecalculatingDocument, variables),
    ...options,
  })
}

useSuspenseSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculatingSuspense", variables]

useSuspenseSiteRecalculatingQuery.rootKey = "SiteRecalculating"

export const useInfiniteSiteRecalculatingQuery = <
  TData = InfiniteData<Types.SiteRecalculatingQuery>,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteRecalculatingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteRecalculating.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteRecalculatingQuery,
            Types.SiteRecalculatingQueryVariables
          >(SiteRecalculatingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating.infinite", variables]

useInfiniteSiteRecalculatingQuery.rootKey = "SiteRecalculating.infinite"

export const useSuspenseInfiniteSiteRecalculatingQuery = <
  TData = InfiniteData<Types.SiteRecalculatingQuery>,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteRecalculatingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteRecalculating.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteRecalculatingQuery,
            Types.SiteRecalculatingQueryVariables
          >(SiteRecalculatingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating.infiniteSuspense", variables]

useSuspenseInfiniteSiteRecalculatingQuery.rootKey = "SiteRecalculating.infinite"

useSiteRecalculatingQuery.fetcher = (
  variables: Types.SiteRecalculatingQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteRecalculatingQuery, Types.SiteRecalculatingQueryVariables>(
    SiteRecalculatingDocument,
    variables,
    options,
  )

export const ScenarioListDocument = `
    query ScenarioList($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      fullAddress
      scenarios {
        name
        pid
        solarLocation
        loadCategory
        includeStorage
      }
    }
  }
}
    `

export const useScenarioListQuery = <TData = Types.ScenarioListQuery, TError = unknown>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ScenarioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioList", variables],
    queryFn: publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList",
  variables,
]

useScenarioListQuery.rootKey = "ScenarioList"

export const useSuspenseScenarioListQuery = <
  TData = Types.ScenarioListQuery,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioListSuspense", variables],
    queryFn: publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioListSuspense",
  variables,
]

useSuspenseScenarioListQuery.rootKey = "ScenarioList"

export const useInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList.infinite",
  variables,
]

useInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

export const useSuspenseInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteScenarioListQuery.getKey = (
  variables: Types.ScenarioListQueryVariables,
) => ["ScenarioList.infiniteSuspense", variables]

useSuspenseInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

useScenarioListQuery.fetcher = (
  variables: Types.ScenarioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
    ScenarioListDocument,
    variables,
    options,
  )

export const SiteFromPortfolioBuildingDataDocument = `
    query SiteFromPortfolioBuildingData($orgSlug: String!, $portfolioPid: String!, $buildingDataId: Int!) {
  orgBySlug(slug: $orgSlug) {
    siteFromPortfolioBuildingData(
      portfolioPid: $portfolioPid
      buildingDataId: $buildingDataId
    ) {
      __typename
      ... on SiteRef {
        pid
      }
    }
  }
}
    `

export const useSiteFromPortfolioBuildingDataQuery = <
  TData = Types.SiteFromPortfolioBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>({
    queryKey: ["SiteFromPortfolioBuildingData", variables],
    queryFn: publicFetcher<
      Types.SiteFromPortfolioBuildingDataQuery,
      Types.SiteFromPortfolioBuildingDataQueryVariables
    >(SiteFromPortfolioBuildingDataDocument, variables),
    ...options,
  })
}

useSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData", variables]

useSiteFromPortfolioBuildingDataQuery.rootKey = "SiteFromPortfolioBuildingData"

export const useSuspenseSiteFromPortfolioBuildingDataQuery = <
  TData = Types.SiteFromPortfolioBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>({
    queryKey: ["SiteFromPortfolioBuildingDataSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteFromPortfolioBuildingDataQuery,
      Types.SiteFromPortfolioBuildingDataQueryVariables
    >(SiteFromPortfolioBuildingDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingDataSuspense", variables]

useSuspenseSiteFromPortfolioBuildingDataQuery.rootKey = "SiteFromPortfolioBuildingData"

export const useInfiniteSiteFromPortfolioBuildingDataQuery = <
  TData = InfiniteData<Types.SiteFromPortfolioBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteFromPortfolioBuildingData.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFromPortfolioBuildingDataQuery,
            Types.SiteFromPortfolioBuildingDataQueryVariables
          >(SiteFromPortfolioBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData.infinite", variables]

useInfiniteSiteFromPortfolioBuildingDataQuery.rootKey =
  "SiteFromPortfolioBuildingData.infinite"

export const useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery = <
  TData = InfiniteData<Types.SiteFromPortfolioBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.SiteFromPortfolioBuildingDataQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteFromPortfolioBuildingData.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFromPortfolioBuildingDataQuery,
            Types.SiteFromPortfolioBuildingDataQueryVariables
          >(SiteFromPortfolioBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData.infiniteSuspense", variables]

useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery.rootKey =
  "SiteFromPortfolioBuildingData.infinite"

useSiteFromPortfolioBuildingDataQuery.fetcher = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteFromPortfolioBuildingDataQuery,
    Types.SiteFromPortfolioBuildingDataQueryVariables
  >(SiteFromPortfolioBuildingDataDocument, variables, options)

export const UtilityTariffsDocument = `
    query UtilityTariffs($utilityPid: String!) {
  tariffs2(utilityPid: $utilityPid) {
    ...PublicTariff
  }
}
    ${PublicTariffFragmentDoc}`

export const useUtilityTariffsQuery = <
  TData = Types.UtilityTariffsQuery,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilityTariffsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityTariffsQuery, TError, TData>({
    queryKey: ["UtilityTariffs", variables],
    queryFn: publicFetcher<
      Types.UtilityTariffsQuery,
      Types.UtilityTariffsQueryVariables
    >(UtilityTariffsDocument, variables),
    ...options,
  })
}

useUtilityTariffsQuery.getKey = (variables: Types.UtilityTariffsQueryVariables) => [
  "UtilityTariffs",
  variables,
]

useUtilityTariffsQuery.rootKey = "UtilityTariffs"

export const useSuspenseUtilityTariffsQuery = <
  TData = Types.UtilityTariffsQuery,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityTariffsQuery, TError, TData>({
    queryKey: ["UtilityTariffsSuspense", variables],
    queryFn: publicFetcher<
      Types.UtilityTariffsQuery,
      Types.UtilityTariffsQueryVariables
    >(UtilityTariffsDocument, variables),
    ...options,
  })
}

useSuspenseUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffsSuspense", variables]

useSuspenseUtilityTariffsQuery.rootKey = "UtilityTariffs"

export const useInfiniteUtilityTariffsQuery = <
  TData = InfiniteData<Types.UtilityTariffsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
            UtilityTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffs.infinite", variables]

useInfiniteUtilityTariffsQuery.rootKey = "UtilityTariffs.infinite"

export const useSuspenseInfiniteUtilityTariffsQuery = <
  TData = InfiniteData<Types.UtilityTariffsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityTariffs.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
            UtilityTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffs.infiniteSuspense", variables]

useSuspenseInfiniteUtilityTariffsQuery.rootKey = "UtilityTariffs.infinite"

useUtilityTariffsQuery.fetcher = (
  variables: Types.UtilityTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
    UtilityTariffsDocument,
    variables,
    options,
  )

export const CustomTariffsDocument = `
    query CustomTariffs($sitePid: String!, $utilityPid: String!) {
  customTariffs(sitePid: $sitePid, utilityPid: $utilityPid) {
    ...PublicTariff
  }
}
    ${PublicTariffFragmentDoc}`

export const useCustomTariffsQuery = <
  TData = Types.CustomTariffsQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffsQuery, TError, TData>({
    queryKey: ["CustomTariffs", variables],
    queryFn: publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
      CustomTariffsDocument,
      variables,
    ),
    ...options,
  })
}

useCustomTariffsQuery.getKey = (variables: Types.CustomTariffsQueryVariables) => [
  "CustomTariffs",
  variables,
]

useCustomTariffsQuery.rootKey = "CustomTariffs"

export const useSuspenseCustomTariffsQuery = <
  TData = Types.CustomTariffsQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffsQuery, TError, TData>({
    queryKey: ["CustomTariffsSuspense", variables],
    queryFn: publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
      CustomTariffsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffsSuspense", variables]

useSuspenseCustomTariffsQuery.rootKey = "CustomTariffs"

export const useInfiniteCustomTariffsQuery = <
  TData = InfiniteData<Types.CustomTariffsQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
            CustomTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffs.infinite", variables]

useInfiniteCustomTariffsQuery.rootKey = "CustomTariffs.infinite"

export const useSuspenseInfiniteCustomTariffsQuery = <
  TData = InfiniteData<Types.CustomTariffsQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
            CustomTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffs.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffsQuery.rootKey = "CustomTariffs.infinite"

useCustomTariffsQuery.fetcher = (
  variables: Types.CustomTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
    CustomTariffsDocument,
    variables,
    options,
  )

export const CustomTariffs2Document = `
    query CustomTariffs2($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      customTariffs {
        name
        preOpDetails {
          rates {
            ...Rate
          }
        }
        postOpDetails {
          rates {
            ...Rate
          }
        }
      }
    }
  }
}
    ${RateFragmentDoc}
${RateBandFragmentDoc}
${SeasonFragmentDoc}`

export const useCustomTariffs2Query = <
  TData = Types.CustomTariffs2Query,
  TError = unknown,
>(
  variables: Types.CustomTariffs2QueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffs2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffs2Query, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffs2Query, TError, TData>({
    queryKey: ["CustomTariffs2", variables],
    queryFn: publicFetcher<
      Types.CustomTariffs2Query,
      Types.CustomTariffs2QueryVariables
    >(CustomTariffs2Document, variables),
    ...options,
  })
}

useCustomTariffs2Query.getKey = (variables: Types.CustomTariffs2QueryVariables) => [
  "CustomTariffs2",
  variables,
]

useCustomTariffs2Query.rootKey = "CustomTariffs2"

export const useSuspenseCustomTariffs2Query = <
  TData = Types.CustomTariffs2Query,
  TError = unknown,
>(
  variables: Types.CustomTariffs2QueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffs2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffs2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffs2Query, TError, TData>({
    queryKey: ["CustomTariffs2Suspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffs2Query,
      Types.CustomTariffs2QueryVariables
    >(CustomTariffs2Document, variables),
    ...options,
  })
}

useSuspenseCustomTariffs2Query.getKey = (
  variables: Types.CustomTariffs2QueryVariables,
) => ["CustomTariffs2Suspense", variables]

useSuspenseCustomTariffs2Query.rootKey = "CustomTariffs2"

export const useInfiniteCustomTariffs2Query = <
  TData = InfiniteData<Types.CustomTariffs2Query>,
  TError = unknown,
>(
  variables: Types.CustomTariffs2QueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffs2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffs2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffs2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs2.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffs2Query, Types.CustomTariffs2QueryVariables>(
            CustomTariffs2Document,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffs2Query.getKey = (
  variables: Types.CustomTariffs2QueryVariables,
) => ["CustomTariffs2.infinite", variables]

useInfiniteCustomTariffs2Query.rootKey = "CustomTariffs2.infinite"

export const useSuspenseInfiniteCustomTariffs2Query = <
  TData = InfiniteData<Types.CustomTariffs2Query>,
  TError = unknown,
>(
  variables: Types.CustomTariffs2QueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffs2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffs2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffs2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs2.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffs2Query, Types.CustomTariffs2QueryVariables>(
            CustomTariffs2Document,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffs2Query.getKey = (
  variables: Types.CustomTariffs2QueryVariables,
) => ["CustomTariffs2.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffs2Query.rootKey = "CustomTariffs2.infinite"

useCustomTariffs2Query.fetcher = (
  variables: Types.CustomTariffs2QueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffs2Query, Types.CustomTariffs2QueryVariables>(
    CustomTariffs2Document,
    variables,
    options,
  )

export const CustomTariffDetailDocument = `
    query CustomTariffDetail($orgSlug: String!, $propertyPid: String!, $tariffId: Int!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $propertyPid) {
      customTariff(tariffId: $tariffId) {
        id
        name
        code
        customerClass
        ops {
          ...TariffOp
        }
        preOpDetails {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
        }
        postOpDetails {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
        }
      }
    }
  }
}
    ${TariffOpFragmentDoc}
${CustomRateFragmentDoc}
${RateFragmentDoc}
${RateBandFragmentDoc}
${SeasonFragmentDoc}`

export const useCustomTariffDetailQuery = <
  TData = Types.CustomTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffDetailQuery, TError, TData>({
    queryKey: ["CustomTariffDetail", variables],
    queryFn: publicFetcher<
      Types.CustomTariffDetailQuery,
      Types.CustomTariffDetailQueryVariables
    >(CustomTariffDetailDocument, variables),
    ...options,
  })
}

useCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail", variables]

useCustomTariffDetailQuery.rootKey = "CustomTariffDetail"

export const useSuspenseCustomTariffDetailQuery = <
  TData = Types.CustomTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffDetailQuery, TError, TData>({
    queryKey: ["CustomTariffDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffDetailQuery,
      Types.CustomTariffDetailQueryVariables
    >(CustomTariffDetailDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetailSuspense", variables]

useSuspenseCustomTariffDetailQuery.rootKey = "CustomTariffDetail"

export const useInfiniteCustomTariffDetailQuery = <
  TData = InfiniteData<Types.CustomTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffDetailQuery,
            Types.CustomTariffDetailQueryVariables
          >(CustomTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail.infinite", variables]

useInfiniteCustomTariffDetailQuery.rootKey = "CustomTariffDetail.infinite"

export const useSuspenseInfiniteCustomTariffDetailQuery = <
  TData = InfiniteData<Types.CustomTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffDetailQuery,
            Types.CustomTariffDetailQueryVariables
          >(CustomTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffDetailQuery.rootKey = "CustomTariffDetail.infinite"

useCustomTariffDetailQuery.fetcher = (
  variables: Types.CustomTariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffDetailQuery, Types.CustomTariffDetailQueryVariables>(
    CustomTariffDetailDocument,
    variables,
    options,
  )

export const CustomTariffPreviewDocument = `
    query CustomTariffPreview($orgSlug: String!, $propertyPid: String!, $tariffId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $propertyPid) {
      customTariff(tariffId: $tariffId) {
        id
        name
        previewDetails(
          addRateOps: $addRateOps
          subRateOps: $subRateOps
          propertyOps: $propertyOps
        ) {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
          properties {
            keyName
            dataType
            propertyValue
            propertyValues
            formulaDetail
          }
        }
      }
    }
  }
}
    ${RateFragmentDoc}
${RateBandFragmentDoc}
${SeasonFragmentDoc}`

export const useCustomTariffPreviewQuery = <
  TData = Types.CustomTariffPreviewQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffPreviewQuery, TError, TData>({
    queryKey: ["CustomTariffPreview", variables],
    queryFn: publicFetcher<
      Types.CustomTariffPreviewQuery,
      Types.CustomTariffPreviewQueryVariables
    >(CustomTariffPreviewDocument, variables),
    ...options,
  })
}

useCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview", variables]

useCustomTariffPreviewQuery.rootKey = "CustomTariffPreview"

export const useSuspenseCustomTariffPreviewQuery = <
  TData = Types.CustomTariffPreviewQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffPreviewQuery, TError, TData>({
    queryKey: ["CustomTariffPreviewSuspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffPreviewQuery,
      Types.CustomTariffPreviewQueryVariables
    >(CustomTariffPreviewDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreviewSuspense", variables]

useSuspenseCustomTariffPreviewQuery.rootKey = "CustomTariffPreview"

export const useInfiniteCustomTariffPreviewQuery = <
  TData = InfiniteData<Types.CustomTariffPreviewQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffPreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffPreviewQuery,
            Types.CustomTariffPreviewQueryVariables
          >(CustomTariffPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview.infinite", variables]

useInfiniteCustomTariffPreviewQuery.rootKey = "CustomTariffPreview.infinite"

export const useSuspenseInfiniteCustomTariffPreviewQuery = <
  TData = InfiniteData<Types.CustomTariffPreviewQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CustomTariffPreview.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffPreviewQuery,
            Types.CustomTariffPreviewQueryVariables
          >(CustomTariffPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffPreviewQuery.rootKey = "CustomTariffPreview.infinite"

useCustomTariffPreviewQuery.fetcher = (
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CustomTariffPreviewQuery,
    Types.CustomTariffPreviewQueryVariables
  >(CustomTariffPreviewDocument, variables, options)

export const CustomTariffBillCalcDocument = `
    query CustomTariffBillCalc($propertyPid: String!, $customTariffId: Int!, $bauMeterPid: String!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  customTariff(sitePid: $propertyPid, customTariffId: $customTariffId) {
    billCalc(
      bauMeterPid: $bauMeterPid
      addRateOps: $addRateOps
      subRateOps: $subRateOps
      propertyOps: $propertyOps
    ) {
      start
      end
      summary {
        chargeType
        costUsd
      }
      details {
        group
        items {
          cost
          usage
          name
          rate
          unit
        }
      }
    }
  }
}
    `

export const useCustomTariffBillCalcQuery = <
  TData = Types.CustomTariffBillCalcQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffBillCalcQuery, TError, TData>({
    queryKey: ["CustomTariffBillCalc", variables],
    queryFn: publicFetcher<
      Types.CustomTariffBillCalcQuery,
      Types.CustomTariffBillCalcQueryVariables
    >(CustomTariffBillCalcDocument, variables),
    ...options,
  })
}

useCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc", variables]

useCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc"

export const useSuspenseCustomTariffBillCalcQuery = <
  TData = Types.CustomTariffBillCalcQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffBillCalcQuery, TError, TData>({
    queryKey: ["CustomTariffBillCalcSuspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffBillCalcQuery,
      Types.CustomTariffBillCalcQueryVariables
    >(CustomTariffBillCalcDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalcSuspense", variables]

useSuspenseCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc"

export const useInfiniteCustomTariffBillCalcQuery = <
  TData = InfiniteData<Types.CustomTariffBillCalcQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffBillCalcQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffBillCalc.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffBillCalcQuery,
            Types.CustomTariffBillCalcQueryVariables
          >(CustomTariffBillCalcDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc.infinite", variables]

useInfiniteCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc.infinite"

export const useSuspenseInfiniteCustomTariffBillCalcQuery = <
  TData = InfiniteData<Types.CustomTariffBillCalcQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffBillCalcQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CustomTariffBillCalc.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffBillCalcQuery,
            Types.CustomTariffBillCalcQueryVariables
          >(CustomTariffBillCalcDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc.infinite"

useCustomTariffBillCalcQuery.fetcher = (
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CustomTariffBillCalcQuery,
    Types.CustomTariffBillCalcQueryVariables
  >(CustomTariffBillCalcDocument, variables, options)

export const BundleParentsDocument = `
    query BundleParents($orgSlug: String!) {
  bundleParents(orgSlug: $orgSlug) {
    ...BasicBundleParent
  }
}
    ${BasicBundleParentFragmentDoc}
${UserRefFragmentDoc}`

export const useBundleParentsQuery = <
  TData = Types.BundleParentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentsQuery, TError, TData>({
    queryKey: ["BundleParents", variables],
    queryFn: publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
      BundleParentsDocument,
      variables,
    ),
    ...options,
  })
}

useBundleParentsQuery.getKey = (variables: Types.BundleParentsQueryVariables) => [
  "BundleParents",
  variables,
]

useBundleParentsQuery.rootKey = "BundleParents"

export const useSuspenseBundleParentsQuery = <
  TData = Types.BundleParentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentsQuery, TError, TData>({
    queryKey: ["BundleParentsSuspense", variables],
    queryFn: publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
      BundleParentsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParentsSuspense", variables]

useSuspenseBundleParentsQuery.rootKey = "BundleParents"

export const useInfiniteBundleParentsQuery = <
  TData = InfiniteData<Types.BundleParentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParents.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
            BundleParentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParents.infinite", variables]

useInfiniteBundleParentsQuery.rootKey = "BundleParents.infinite"

export const useSuspenseInfiniteBundleParentsQuery = <
  TData = InfiniteData<Types.BundleParentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParents.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
            BundleParentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParents.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentsQuery.rootKey = "BundleParents.infinite"

useBundleParentsQuery.fetcher = (
  variables: Types.BundleParentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
    BundleParentsDocument,
    variables,
    options,
  )

export const BundleParentDetailDocument = `
    query BundleParentDetail($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    ...BundleParent
    bundles {
      ...BundleListItem
      rows {
        ...BundleRowDetail
      }
    }
  }
}
    ${BundleParentFragmentDoc}
${BasicBundleParentFragmentDoc}
${UserRefFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${SystemLiteFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${BundleRowDetailFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${RevampCsFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}`

export const useBundleParentDetailQuery = <
  TData = Types.BundleParentDetailQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentDetailQuery, TError, TData>({
    queryKey: ["BundleParentDetail", variables],
    queryFn: publicFetcher<
      Types.BundleParentDetailQuery,
      Types.BundleParentDetailQueryVariables
    >(BundleParentDetailDocument, variables),
    ...options,
  })
}

useBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail", variables]

useBundleParentDetailQuery.rootKey = "BundleParentDetail"

export const useSuspenseBundleParentDetailQuery = <
  TData = Types.BundleParentDetailQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentDetailQuery, TError, TData>({
    queryKey: ["BundleParentDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.BundleParentDetailQuery,
      Types.BundleParentDetailQueryVariables
    >(BundleParentDetailDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetailSuspense", variables]

useSuspenseBundleParentDetailQuery.rootKey = "BundleParentDetail"

export const useInfiniteBundleParentDetailQuery = <
  TData = InfiniteData<Types.BundleParentDetailQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDetailQuery,
            Types.BundleParentDetailQueryVariables
          >(BundleParentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail.infinite", variables]

useInfiniteBundleParentDetailQuery.rootKey = "BundleParentDetail.infinite"

export const useSuspenseInfiniteBundleParentDetailQuery = <
  TData = InfiniteData<Types.BundleParentDetailQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDetailQuery,
            Types.BundleParentDetailQueryVariables
          >(BundleParentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentDetailQuery.rootKey = "BundleParentDetail.infinite"

useBundleParentDetailQuery.fetcher = (
  variables: Types.BundleParentDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentDetailQuery, Types.BundleParentDetailQueryVariables>(
    BundleParentDetailDocument,
    variables,
    options,
  )

export const BundleParentReportsDocument = `
    query BundleParentReports($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    type
    customReports {
      ...CustomBidReport
    }
  }
}
    ${CustomBidReportFragmentDoc}`

export const useBundleParentReportsQuery = <
  TData = Types.BundleParentReportsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentReportsQuery, TError, TData>({
    queryKey: ["BundleParentReports", variables],
    queryFn: publicFetcher<
      Types.BundleParentReportsQuery,
      Types.BundleParentReportsQueryVariables
    >(BundleParentReportsDocument, variables),
    ...options,
  })
}

useBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports", variables]

useBundleParentReportsQuery.rootKey = "BundleParentReports"

export const useSuspenseBundleParentReportsQuery = <
  TData = Types.BundleParentReportsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentReportsQuery, TError, TData>({
    queryKey: ["BundleParentReportsSuspense", variables],
    queryFn: publicFetcher<
      Types.BundleParentReportsQuery,
      Types.BundleParentReportsQueryVariables
    >(BundleParentReportsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReportsSuspense", variables]

useSuspenseBundleParentReportsQuery.rootKey = "BundleParentReports"

export const useInfiniteBundleParentReportsQuery = <
  TData = InfiniteData<Types.BundleParentReportsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentReportsQuery,
            Types.BundleParentReportsQueryVariables
          >(BundleParentReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports.infinite", variables]

useInfiniteBundleParentReportsQuery.rootKey = "BundleParentReports.infinite"

export const useSuspenseInfiniteBundleParentReportsQuery = <
  TData = InfiniteData<Types.BundleParentReportsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BundleParentReports.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentReportsQuery,
            Types.BundleParentReportsQueryVariables
          >(BundleParentReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentReportsQuery.rootKey = "BundleParentReports.infinite"

useBundleParentReportsQuery.fetcher = (
  variables: Types.BundleParentReportsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentReportsQuery,
    Types.BundleParentReportsQueryVariables
  >(BundleParentReportsDocument, variables, options)

export const BidReportsDocument = `
    query BidReports {
  bidReports {
    pid
    name
    notes
    buildingOwnerNotes
    parent {
      pid
    }
    bids {
      ppa {
        pid
      }
      cs {
        pid
      }
    }
  }
}
    `

export const useBidReportsQuery = <TData = Types.BidReportsQuery, TError = unknown>(
  variables?: Types.BidReportsQueryVariables,
  options?: Omit<UseQueryOptions<Types.BidReportsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidReportsQuery, TError, TData>({
    queryKey: variables === undefined ? ["BidReports"] : ["BidReports", variables],
    queryFn: publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
      BidReportsDocument,
      variables,
    ),
    ...options,
  })
}

useBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReports"] : ["BidReports", variables]

useBidReportsQuery.rootKey = "BidReports"

export const useSuspenseBidReportsQuery = <
  TData = Types.BidReportsQuery,
  TError = unknown,
>(
  variables?: Types.BidReportsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidReportsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidReportsSuspense"]
        : ["BidReportsSuspense", variables],
    queryFn: publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
      BidReportsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReportsSuspense"] : ["BidReportsSuspense", variables]

useSuspenseBidReportsQuery.rootKey = "BidReports"

export const useInfiniteBidReportsQuery = <
  TData = InfiniteData<Types.BidReportsQuery>,
  TError = unknown,
>(
  variables: Types.BidReportsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidReports.infinite"]
            : ["BidReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
            BidReportsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReports.infinite"] : ["BidReports.infinite", variables]

useInfiniteBidReportsQuery.rootKey = "BidReports.infinite"

export const useSuspenseInfiniteBidReportsQuery = <
  TData = InfiniteData<Types.BidReportsQuery>,
  TError = unknown,
>(
  variables: Types.BidReportsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidReports.infiniteSuspense"]
            : ["BidReports.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
            BidReportsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidReportsQuery.getKey = (
  variables?: Types.BidReportsQueryVariables,
) =>
  variables === undefined
    ? ["BidReports.infiniteSuspense"]
    : ["BidReports.infiniteSuspense", variables]

useSuspenseInfiniteBidReportsQuery.rootKey = "BidReports.infinite"

useBidReportsQuery.fetcher = (
  variables?: Types.BidReportsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
    BidReportsDocument,
    variables,
    options,
  )

export const BundleParentBasicsDocument = `
    query BundleParentBasics($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    ...BasicBundleParent
    publicUrl
    totalPvSizeKwDc
    bundles {
      pid
      financier {
        pid
        name
        orgSlug
        orgUsers {
          user {
            ...UserRef
          }
          userRoles {
            role
          }
        }
      }
    }
  }
}
    ${BasicBundleParentFragmentDoc}
${UserRefFragmentDoc}`

export const useBundleParentBasicsQuery = <
  TData = Types.BundleParentBasicsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentBasicsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentBasicsQuery, TError, TData>({
    queryKey: ["BundleParentBasics", variables],
    queryFn: publicFetcher<
      Types.BundleParentBasicsQuery,
      Types.BundleParentBasicsQueryVariables
    >(BundleParentBasicsDocument, variables),
    ...options,
  })
}

useBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasics", variables]

useBundleParentBasicsQuery.rootKey = "BundleParentBasics"

export const useSuspenseBundleParentBasicsQuery = <
  TData = Types.BundleParentBasicsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentBasicsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentBasicsQuery, TError, TData>({
    queryKey: ["BundleParentBasicsSuspense", variables],
    queryFn: publicFetcher<
      Types.BundleParentBasicsQuery,
      Types.BundleParentBasicsQueryVariables
    >(BundleParentBasicsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasicsSuspense", variables]

useSuspenseBundleParentBasicsQuery.rootKey = "BundleParentBasics"

export const useInfiniteBundleParentBasicsQuery = <
  TData = InfiniteData<Types.BundleParentBasicsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentBasicsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentBasicsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentBasics.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentBasicsQuery,
            Types.BundleParentBasicsQueryVariables
          >(BundleParentBasicsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasics.infinite", variables]

useInfiniteBundleParentBasicsQuery.rootKey = "BundleParentBasics.infinite"

export const useSuspenseInfiniteBundleParentBasicsQuery = <
  TData = InfiniteData<Types.BundleParentBasicsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentBasicsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentBasicsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentBasics.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentBasicsQuery,
            Types.BundleParentBasicsQueryVariables
          >(BundleParentBasicsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasics.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentBasicsQuery.rootKey = "BundleParentBasics.infinite"

useBundleParentBasicsQuery.fetcher = (
  variables: Types.BundleParentBasicsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentBasicsQuery, Types.BundleParentBasicsQueryVariables>(
    BundleParentBasicsDocument,
    variables,
    options,
  )

export const EvOverridesDocument = `
    query EVOverrides($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      evOverrides {
        current {
          chargerKw
          chargerLocation
          cmsUsdPerChargerPerYr
          demandUsdPerKw
          discountRate
          evGrowthPct
          highEnergyUsdPerKwh
          incentivePct
          installType
          lowEnergyUsdPerKwh
          maxUtilization
          milesDrivenPerYear
          milesPerKwh
          numChargers
          omUsdPerChargerPerYr
          onPeakChargingPct
          onSiteChargingPct
          projectStartDate
          rateManagementUsdPerChargerPerYr
          salesTaxPct
          termYears
          transactionFeePct
          usdPerKwhMarkup
          yr0NumTenantEvs
          incentiveCapNumChargers
          maxEvAdoptionPct
          installCostUsd
        }
        defaults {
          chargerKw
          chargerLocation
          cmsUsdPerChargerPerYr
          demandUsdPerKw
          discountRate
          evGrowthPct
          highEnergyUsdPerKwh
          incentivePct
          installType
          lowEnergyUsdPerKwh
          maxUtilization
          milesDrivenPerYear
          milesPerKwh
          numChargers
          omUsdPerChargerPerYr
          onPeakChargingPct
          onSiteChargingPct
          projectStartDate
          rateManagementUsdPerChargerPerYr
          salesTaxPct
          termYears
          transactionFeePct
          usdPerKwhMarkup
          yr0NumTenantEvs
          incentiveCapNumChargers
          maxEvAdoptionPct
          installCostUsd
        }
      }
    }
  }
}
    `

export const useEvOverridesQuery = <TData = Types.EvOverridesQuery, TError = unknown>(
  variables: Types.EvOverridesQueryVariables,
  options?: Omit<UseQueryOptions<Types.EvOverridesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EvOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EvOverridesQuery, TError, TData>({
    queryKey: ["EVOverrides", variables],
    queryFn: publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
      EvOverridesDocument,
      variables,
    ),
    ...options,
  })
}

useEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverrides",
  variables,
]

useEvOverridesQuery.rootKey = "EVOverrides"

export const useSuspenseEvOverridesQuery = <
  TData = Types.EvOverridesQuery,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EvOverridesQuery, TError, TData>({
    queryKey: ["EVOverridesSuspense", variables],
    queryFn: publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
      EvOverridesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverridesSuspense",
  variables,
]

useSuspenseEvOverridesQuery.rootKey = "EVOverrides"

export const useInfiniteEvOverridesQuery = <
  TData = InfiniteData<Types.EvOverridesQuery>,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EvOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EVOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
            EvOverridesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverrides.infinite",
  variables,
]

useInfiniteEvOverridesQuery.rootKey = "EVOverrides.infinite"

export const useSuspenseInfiniteEvOverridesQuery = <
  TData = InfiniteData<Types.EvOverridesQuery>,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EvOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EVOverrides.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
            EvOverridesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEvOverridesQuery.getKey = (
  variables: Types.EvOverridesQueryVariables,
) => ["EVOverrides.infiniteSuspense", variables]

useSuspenseInfiniteEvOverridesQuery.rootKey = "EVOverrides.infinite"

useEvOverridesQuery.fetcher = (
  variables: Types.EvOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
    EvOverridesDocument,
    variables,
    options,
  )

export const InvestorBundleListDocument = `
    query InvestorBundleList($orgSlug: String!) {
  bundlesForFinancier2(slug: $orgSlug) {
    ...BundleListItem
  }
}
    ${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}`

export const useInvestorBundleListQuery = <
  TData = Types.InvestorBundleListQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.InvestorBundleListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleListQuery, TError, TData>({
    queryKey: ["InvestorBundleList", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleListQuery,
      Types.InvestorBundleListQueryVariables
    >(InvestorBundleListDocument, variables),
    ...options,
  })
}

useInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList", variables]

useInvestorBundleListQuery.rootKey = "InvestorBundleList"

export const useSuspenseInvestorBundleListQuery = <
  TData = Types.InvestorBundleListQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleListQuery, TError, TData>({
    queryKey: ["InvestorBundleListSuspense", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleListQuery,
      Types.InvestorBundleListQueryVariables
    >(InvestorBundleListDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleListSuspense", variables]

useSuspenseInvestorBundleListQuery.rootKey = "InvestorBundleList"

export const useInfiniteInvestorBundleListQuery = <
  TData = InfiniteData<Types.InvestorBundleListQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleListQuery,
            Types.InvestorBundleListQueryVariables
          >(InvestorBundleListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList.infinite", variables]

useInfiniteInvestorBundleListQuery.rootKey = "InvestorBundleList.infinite"

export const useSuspenseInfiniteInvestorBundleListQuery = <
  TData = InfiniteData<Types.InvestorBundleListQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleListQuery,
            Types.InvestorBundleListQueryVariables
          >(InvestorBundleListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList.infiniteSuspense", variables]

useSuspenseInfiniteInvestorBundleListQuery.rootKey = "InvestorBundleList.infinite"

useInvestorBundleListQuery.fetcher = (
  variables: Types.InvestorBundleListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InvestorBundleListQuery, Types.InvestorBundleListQueryVariables>(
    InvestorBundleListDocument,
    variables,
    options,
  )

export const InvestorBundleLiteDocument = `
    query InvestorBundleLite($orgSlug: String!, $bundlePid: String!) {
  bundleDetail(slug: $orgSlug, bundlePid: $bundlePid) {
    ...BundleListItem
  }
}
    ${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}`

export const useInvestorBundleLiteQuery = <
  TData = Types.InvestorBundleLiteQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleLiteQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleLiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.InvestorBundleLiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleLiteQuery, TError, TData>({
    queryKey: ["InvestorBundleLite", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleLiteQuery,
      Types.InvestorBundleLiteQueryVariables
    >(InvestorBundleLiteDocument, variables),
    ...options,
  })
}

useInvestorBundleLiteQuery.getKey = (
  variables: Types.InvestorBundleLiteQueryVariables,
) => ["InvestorBundleLite", variables]

useInvestorBundleLiteQuery.rootKey = "InvestorBundleLite"

export const useSuspenseInvestorBundleLiteQuery = <
  TData = Types.InvestorBundleLiteQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleLiteQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleLiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleLiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleLiteQuery, TError, TData>({
    queryKey: ["InvestorBundleLiteSuspense", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleLiteQuery,
      Types.InvestorBundleLiteQueryVariables
    >(InvestorBundleLiteDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleLiteQuery.getKey = (
  variables: Types.InvestorBundleLiteQueryVariables,
) => ["InvestorBundleLiteSuspense", variables]

useSuspenseInvestorBundleLiteQuery.rootKey = "InvestorBundleLite"

export const useInfiniteInvestorBundleLiteQuery = <
  TData = InfiniteData<Types.InvestorBundleLiteQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleLiteQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleLiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleLiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleLiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleLite.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleLiteQuery,
            Types.InvestorBundleLiteQueryVariables
          >(InvestorBundleLiteDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleLiteQuery.getKey = (
  variables: Types.InvestorBundleLiteQueryVariables,
) => ["InvestorBundleLite.infinite", variables]

useInfiniteInvestorBundleLiteQuery.rootKey = "InvestorBundleLite.infinite"

export const useSuspenseInfiniteInvestorBundleLiteQuery = <
  TData = InfiniteData<Types.InvestorBundleLiteQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleLiteQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleLiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleLiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleLiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleLite.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleLiteQuery,
            Types.InvestorBundleLiteQueryVariables
          >(InvestorBundleLiteDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleLiteQuery.getKey = (
  variables: Types.InvestorBundleLiteQueryVariables,
) => ["InvestorBundleLite.infiniteSuspense", variables]

useSuspenseInfiniteInvestorBundleLiteQuery.rootKey = "InvestorBundleLite.infinite"

useInvestorBundleLiteQuery.fetcher = (
  variables: Types.InvestorBundleLiteQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InvestorBundleLiteQuery, Types.InvestorBundleLiteQueryVariables>(
    InvestorBundleLiteDocument,
    variables,
    options,
  )

export const InvestorBundleDetailDocument = `
    query InvestorBundleDetail($orgSlug: String!, $bundlePid: String!) {
  bundleDetail(slug: $orgSlug, bundlePid: $bundlePid) {
    ...BundleDetail
  }
}
    ${BundleDetailFragmentDoc}
${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${BidReportFragmentDoc}
${CsSiteBidReportFragmentDoc}
${BoxplotStatsFragmentDoc}
${PpaSiteBidReportFragmentDoc}`

export const useInvestorBundleDetailQuery = <
  TData = Types.InvestorBundleDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleDetailQuery,
      Types.InvestorBundleDetailQueryVariables
    >(InvestorBundleDetailDocument, variables),
    ...options,
  })
}

useInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail", variables]

useInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail"

export const useSuspenseInvestorBundleDetailQuery = <
  TData = Types.InvestorBundleDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleDetailQuery,
      Types.InvestorBundleDetailQueryVariables
    >(InvestorBundleDetailDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetailSuspense", variables]

useSuspenseInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail"

export const useInfiniteInvestorBundleDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleDetailQuery,
            Types.InvestorBundleDetailQueryVariables
          >(InvestorBundleDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail.infinite", variables]

useInfiniteInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail.infinite"

export const useSuspenseInfiniteInvestorBundleDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InvestorBundleDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleDetailQuery,
            Types.InvestorBundleDetailQueryVariables
          >(InvestorBundleDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail.infiniteSuspense", variables]

useSuspenseInfiniteInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail.infinite"

useInvestorBundleDetailQuery.fetcher = (
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InvestorBundleDetailQuery,
    Types.InvestorBundleDetailQueryVariables
  >(InvestorBundleDetailDocument, variables, options)

export const InvestorBundleRowDetailDocument = `
    query InvestorBundleRowDetail($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleRowDetail(orgSlug: $orgSlug, bundlePid: $bundlePid, sitePid: $sitePid) {
    ...BundleRowDetail
  }
}
    ${BundleRowDetailFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}`

export const useInvestorBundleRowDetailQuery = <
  TData = Types.InvestorBundleRowDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleRowDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleRowDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleRowDetailQuery,
      Types.InvestorBundleRowDetailQueryVariables
    >(InvestorBundleRowDetailDocument, variables),
    ...options,
  })
}

useInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail", variables]

useInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail"

export const useSuspenseInvestorBundleRowDetailQuery = <
  TData = Types.InvestorBundleRowDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleRowDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleRowDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleRowDetailQuery,
      Types.InvestorBundleRowDetailQueryVariables
    >(InvestorBundleRowDetailDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetailSuspense", variables]

useSuspenseInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail"

export const useInfiniteInvestorBundleRowDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleRowDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleRowDetailQuery,
            Types.InvestorBundleRowDetailQueryVariables
          >(InvestorBundleRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail.infinite", variables]

useInfiniteInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail.infinite"

export const useSuspenseInfiniteInvestorBundleRowDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InvestorBundleRowDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleRowDetailQuery,
            Types.InvestorBundleRowDetailQueryVariables
          >(InvestorBundleRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail.infiniteSuspense", variables]

useSuspenseInfiniteInvestorBundleRowDetailQuery.rootKey =
  "InvestorBundleRowDetail.infinite"

useInvestorBundleRowDetailQuery.fetcher = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InvestorBundleRowDetailQuery,
    Types.InvestorBundleRowDetailQueryVariables
  >(InvestorBundleRowDetailDocument, variables, options)

export const GetTaskDocument = `
    query GetTask($taskId: Int!) {
  tasks {
    one(taskId: $taskId) {
      __typename
      ... on Task {
        id
        name
        status
      }
    }
  }
}
    `

export const useGetTaskQuery = <TData = Types.GetTaskQuery, TError = unknown>(
  variables: Types.GetTaskQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetTaskQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetTaskQuery, TError, TData>({
    queryKey: ["GetTask", variables],
    queryFn: publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
      GetTaskDocument,
      variables,
    ),
    ...options,
  })
}

useGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTask",
  variables,
]

useGetTaskQuery.rootKey = "GetTask"

export const useSuspenseGetTaskQuery = <TData = Types.GetTaskQuery, TError = unknown>(
  variables: Types.GetTaskQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.GetTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetTaskQuery, TError, TData>({
    queryKey: ["GetTaskSuspense", variables],
    queryFn: publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
      GetTaskDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTaskSuspense",
  variables,
]

useSuspenseGetTaskQuery.rootKey = "GetTask"

export const useInfiniteGetTaskQuery = <
  TData = InfiniteData<Types.GetTaskQuery>,
  TError = unknown,
>(
  variables: Types.GetTaskQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.GetTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetTaskQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetTask.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
            GetTaskDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTask.infinite",
  variables,
]

useInfiniteGetTaskQuery.rootKey = "GetTask.infinite"

export const useSuspenseInfiniteGetTaskQuery = <
  TData = InfiniteData<Types.GetTaskQuery>,
  TError = unknown,
>(
  variables: Types.GetTaskQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetTaskQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetTaskQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetTask.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
            GetTaskDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTask.infiniteSuspense",
  variables,
]

useSuspenseInfiniteGetTaskQuery.rootKey = "GetTask.infinite"

useGetTaskQuery.fetcher = (
  variables: Types.GetTaskQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
    GetTaskDocument,
    variables,
    options,
  )

export const CsvImportDetailDocument = `
    query CsvImportDetail($orgSlug: String!, $csvImportPid: String!) {
  orgBySlug(slug: $orgSlug) {
    csvImports {
      one(pid: $csvImportPid) {
        pid
        rawCsvWithPidsHref
        columnIds
        columnMappings {
          assetManagerColumnId
          buildingTypeColumnId
          cityColumnId
          customerIdColumnId
          latitudeColumnId
          longitudeColumnId
          nameColumnId
          numStoriesColumnId
          numUnits1BdColumnId
          numUnits2BdColumnId
          numUnits3BdColumnId
          numUnits4BdColumnId
          numUnitsStudioColumnId
          numUnitsTotalColumnId
          sqftColumnId
          stateColumnId
          streetAddressColumnId
          yearBuiltColumnId
          zipCodeColumnId
        }
        buildingTypes
        buildingTypeMappings {
          office
          multifamily
          hotel
          hospital
          outpatient
          retailStandalone
          retailStripMall
          quickServiceRestaurant
          fullServiceRestaurant
          primarySchool
          secondarySchool
          singleFamilyDetached
          warehouse
          dataCenter
          coldStorage
          industrial
          parkingLot
          supermarket
        }
        guessedSchema {
          pid
          origin
        }
        labels {
          pid
          themeColor
          name
        }
        rows {
          id
          droppedAt
          buildingData {
            id
            propertyName
            fullAddress
            streetAddress
            city
            state
            zipCode
            buildingType
            sqft
            roofSqft
            numStories
            latitude
            longitude
            buildingFootprintGeojson
            yearBuilt
            numUnitsTotal
            numUnitsStudio
            numUnits1Bd
            numUnits2Bd
            numUnits3Bd
            numUnits4Bd
          }
          amendments {
            assetManager
            buildingType
            customerId
            latitude
            longitude
            name
            numStories
            roofSqft
            sqft
            yearBuilt
            mfUnits {
              numUnitsTotal
              numUnitsStudio
              numUnits1Bd
              numUnits2Bd
              numUnits3Bd
              numUnits4Bd
            }
          }
          site {
            pid
          }
          dirty {
            ...DirtyRowData
          }
          errors {
            type
            message
          }
          warnings {
            type
            message
          }
        }
      }
    }
  }
}
    ${DirtyRowDataFragmentDoc}`

export const useCsvImportDetailQuery = <
  TData = Types.CsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsvImportDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsvImportDetailQuery, TError, TData>({
    queryKey: ["CsvImportDetail", variables],
    queryFn: publicFetcher<
      Types.CsvImportDetailQuery,
      Types.CsvImportDetailQueryVariables
    >(CsvImportDetailDocument, variables),
    ...options,
  })
}

useCsvImportDetailQuery.getKey = (variables: Types.CsvImportDetailQueryVariables) => [
  "CsvImportDetail",
  variables,
]

useCsvImportDetailQuery.rootKey = "CsvImportDetail"

export const useSuspenseCsvImportDetailQuery = <
  TData = Types.CsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsvImportDetailQuery, TError, TData>({
    queryKey: ["CsvImportDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.CsvImportDetailQuery,
      Types.CsvImportDetailQueryVariables
    >(CsvImportDetailDocument, variables),
    ...options,
  })
}

useSuspenseCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetailSuspense", variables]

useSuspenseCsvImportDetailQuery.rootKey = "CsvImportDetail"

export const useInfiniteCsvImportDetailQuery = <
  TData = InfiniteData<Types.CsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsvImportDetailQuery,
            Types.CsvImportDetailQueryVariables
          >(CsvImportDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetail.infinite", variables]

useInfiniteCsvImportDetailQuery.rootKey = "CsvImportDetail.infinite"

export const useSuspenseInfiniteCsvImportDetailQuery = <
  TData = InfiniteData<Types.CsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsvImportDetailQuery,
            Types.CsvImportDetailQueryVariables
          >(CsvImportDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetail.infiniteSuspense", variables]

useSuspenseInfiniteCsvImportDetailQuery.rootKey = "CsvImportDetail.infinite"

useCsvImportDetailQuery.fetcher = (
  variables: Types.CsvImportDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsvImportDetailQuery, Types.CsvImportDetailQueryVariables>(
    CsvImportDetailDocument,
    variables,
    options,
  )

export const MarketplacePackagesDocument = `
    query MarketplacePackages($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    bidPackages {
      ...BasicBidPackage
    }
  }
}
    ${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}`

export const useMarketplacePackagesQuery = <
  TData = Types.MarketplacePackagesQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackagesQuery, TError, TData>({
    queryKey: ["MarketplacePackages", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackagesQuery,
      Types.MarketplacePackagesQueryVariables
    >(MarketplacePackagesDocument, variables),
    ...options,
  })
}

useMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages", variables]

useMarketplacePackagesQuery.rootKey = "MarketplacePackages"

export const useSuspenseMarketplacePackagesQuery = <
  TData = Types.MarketplacePackagesQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackagesQuery, TError, TData>({
    queryKey: ["MarketplacePackagesSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackagesQuery,
      Types.MarketplacePackagesQueryVariables
    >(MarketplacePackagesDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackagesSuspense", variables]

useSuspenseMarketplacePackagesQuery.rootKey = "MarketplacePackages"

export const useInfiniteMarketplacePackagesQuery = <
  TData = InfiniteData<Types.MarketplacePackagesQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackagesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackages.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackagesQuery,
            Types.MarketplacePackagesQueryVariables
          >(MarketplacePackagesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages.infinite", variables]

useInfiniteMarketplacePackagesQuery.rootKey = "MarketplacePackages.infinite"

export const useSuspenseInfiniteMarketplacePackagesQuery = <
  TData = InfiniteData<Types.MarketplacePackagesQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplacePackagesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackages.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackagesQuery,
            Types.MarketplacePackagesQueryVariables
          >(MarketplacePackagesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackagesQuery.rootKey = "MarketplacePackages.infinite"

useMarketplacePackagesQuery.fetcher = (
  variables: Types.MarketplacePackagesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackagesQuery,
    Types.MarketplacePackagesQueryVariables
  >(MarketplacePackagesDocument, variables, options)

export const MarketplaceSpecsViewDocument = `
    query MarketplaceSpecsView($orgSlug: String!, $filterBy: BidSpecsTableFilter!, $sortBy: [BidSpecsTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSpecs {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useMarketplaceSpecsViewQuery = <
  TData = Types.MarketplaceSpecsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSpecsViewQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsViewQuery,
      Types.MarketplaceSpecsViewQueryVariables
    >(MarketplaceSpecsViewDocument, variables),
    ...options,
  })
}

useMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView", variables]

useMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView"

export const useSuspenseMarketplaceSpecsViewQuery = <
  TData = Types.MarketplaceSpecsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSpecsViewQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsViewQuery,
      Types.MarketplaceSpecsViewQueryVariables
    >(MarketplaceSpecsViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsViewSuspense", variables]

useSuspenseMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView"

export const useInfiniteMarketplaceSpecsViewQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSpecsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsViewQuery,
            Types.MarketplaceSpecsViewQueryVariables
          >(MarketplaceSpecsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView.infinite", variables]

useInfiniteMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView.infinite"

export const useSuspenseInfiniteMarketplaceSpecsViewQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSpecsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSpecsView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsViewQuery,
            Types.MarketplaceSpecsViewQueryVariables
          >(MarketplaceSpecsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView.infinite"

useMarketplaceSpecsViewQuery.fetcher = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSpecsViewQuery,
    Types.MarketplaceSpecsViewQueryVariables
  >(MarketplaceSpecsViewDocument, variables, options)

export const MarketplaceSpecsRowsDocument = `
    query MarketplaceSpecsRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSpecs {
      rows(keys: $keys) {
        pid
        sitePid
        bidName
        bidPackagePid
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        packageRowStatus
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        csSystem {
          ...SystemLite
        }
        ppaSystem {
          ...System
        }
        csRoofRentEscalator
        ppaRoofRentEscalator
        ppaPriceEscalator
        csRoofRentY1Usd
        ppaRoofRentY1Usd
        ppaPriceY1Usd
        ppaTermYears
        csTermYears
      }
    }
  }
}
    ${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${SystemFragmentDoc}`

export const useMarketplaceSpecsRowsQuery = <
  TData = Types.MarketplaceSpecsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsRowsQuery,
      Types.MarketplaceSpecsRowsQueryVariables
    >(MarketplaceSpecsRowsDocument, variables),
    ...options,
  })
}

useMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows", variables]

useMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows"

export const useSuspenseMarketplaceSpecsRowsQuery = <
  TData = Types.MarketplaceSpecsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsRowsQuery,
      Types.MarketplaceSpecsRowsQueryVariables
    >(MarketplaceSpecsRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRowsSuspense", variables]

useSuspenseMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows"

export const useInfiniteMarketplaceSpecsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsRowsQuery,
            Types.MarketplaceSpecsRowsQueryVariables
          >(MarketplaceSpecsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows.infinite", variables]

useInfiniteMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows.infinite"

export const useSuspenseInfiniteMarketplaceSpecsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSpecsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsRowsQuery,
            Types.MarketplaceSpecsRowsQueryVariables
          >(MarketplaceSpecsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows.infinite"

useMarketplaceSpecsRowsQuery.fetcher = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSpecsRowsQuery,
    Types.MarketplaceSpecsRowsQueryVariables
  >(MarketplaceSpecsRowsDocument, variables, options)

export const MarketplaceSitesViewDocument = `
    query MarketplaceSitesView($orgSlug: String!, $filterBy: BidPackageSitesTableFilter!, $sortBy: [BidPackageSitesTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useMarketplaceSitesViewQuery = <
  TData = Types.MarketplaceSitesViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSitesViewQuery, TError, TData>({
    queryKey: ["MarketplaceSitesView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesViewQuery,
      Types.MarketplaceSitesViewQueryVariables
    >(MarketplaceSitesViewDocument, variables),
    ...options,
  })
}

useMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView", variables]

useMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView"

export const useSuspenseMarketplaceSitesViewQuery = <
  TData = Types.MarketplaceSitesViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSitesViewQuery, TError, TData>({
    queryKey: ["MarketplaceSitesViewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesViewQuery,
      Types.MarketplaceSitesViewQueryVariables
    >(MarketplaceSitesViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesViewSuspense", variables]

useSuspenseMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView"

export const useInfiniteMarketplaceSitesViewQuery = <
  TData = InfiniteData<Types.MarketplaceSitesViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSitesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesViewQuery,
            Types.MarketplaceSitesViewQueryVariables
          >(MarketplaceSitesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView.infinite", variables]

useInfiniteMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView.infinite"

export const useSuspenseInfiniteMarketplaceSitesViewQuery = <
  TData = InfiniteData<Types.MarketplaceSitesViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSitesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSitesView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesViewQuery,
            Types.MarketplaceSitesViewQueryVariables
          >(MarketplaceSitesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView.infinite"

useMarketplaceSitesViewQuery.fetcher = (
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSitesViewQuery,
    Types.MarketplaceSitesViewQueryVariables
  >(MarketplaceSitesViewDocument, variables, options)

export const MarketplaceSitesFacetsDocument = `
    query MarketplaceSitesFacets($orgSlug: String!, $filterBy: BidPackageSitesTableFilter!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSites {
      view(filterBy: $filterBy) {
        stats {
          awardedFinanciers
        }
      }
    }
  }
}
    `

export const useMarketplaceSitesFacetsQuery = <
  TData = Types.MarketplaceSitesFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSitesFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSitesFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSitesFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesFacets", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesFacetsQuery,
      Types.MarketplaceSitesFacetsQueryVariables
    >(MarketplaceSitesFacetsDocument, variables),
    ...options,
  })
}

useMarketplaceSitesFacetsQuery.getKey = (
  variables: Types.MarketplaceSitesFacetsQueryVariables,
) => ["MarketplaceSitesFacets", variables]

useMarketplaceSitesFacetsQuery.rootKey = "MarketplaceSitesFacets"

export const useSuspenseMarketplaceSitesFacetsQuery = <
  TData = Types.MarketplaceSitesFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSitesFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSitesFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSitesFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesFacetsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesFacetsQuery,
      Types.MarketplaceSitesFacetsQueryVariables
    >(MarketplaceSitesFacetsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSitesFacetsQuery.getKey = (
  variables: Types.MarketplaceSitesFacetsQueryVariables,
) => ["MarketplaceSitesFacetsSuspense", variables]

useSuspenseMarketplaceSitesFacetsQuery.rootKey = "MarketplaceSitesFacets"

export const useInfiniteMarketplaceSitesFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSitesFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSitesFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSitesFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesFacetsQuery,
            Types.MarketplaceSitesFacetsQueryVariables
          >(MarketplaceSitesFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSitesFacetsQuery.getKey = (
  variables: Types.MarketplaceSitesFacetsQueryVariables,
) => ["MarketplaceSitesFacets.infinite", variables]

useInfiniteMarketplaceSitesFacetsQuery.rootKey = "MarketplaceSitesFacets.infinite"

export const useSuspenseInfiniteMarketplaceSitesFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSitesFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSitesFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSitesFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSitesFacets.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesFacetsQuery,
            Types.MarketplaceSitesFacetsQueryVariables
          >(MarketplaceSitesFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSitesFacetsQuery.getKey = (
  variables: Types.MarketplaceSitesFacetsQueryVariables,
) => ["MarketplaceSitesFacets.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSitesFacetsQuery.rootKey =
  "MarketplaceSitesFacets.infinite"

useMarketplaceSitesFacetsQuery.fetcher = (
  variables: Types.MarketplaceSitesFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSitesFacetsQuery,
    Types.MarketplaceSitesFacetsQueryVariables
  >(MarketplaceSitesFacetsDocument, variables, options)

export const MarketplaceSitesRowsDocument = `
    query MarketplaceSitesRows($orgSlug: String!, $keys: [Int!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSites {
      rows(keys: $keys) {
        id
        sitePid
        bidPackagePid
        bidPackageName
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        salePlans
        buildingLegalName
        propertyFundName
        leaseSignatory
        counselApproval
        leaseApprovers
        externalLeaseApprovers
        needsTenantApproval
        roofRightsHolder
        propertyLenders
        propertyInsurer
        constructionEmail
        propertySiteVisitContacts
        leasePropertyLegalDescription
        leaseLegalOwnerName
        signatureBlocks
        isFannieMaeLender
        isFmGlobalInsurer
        financierCount
        bidCount
        ppaNoiMin
        ppaNoiAvg
        ppaNoiMax
        csNoiMin
        csNoiAvg
        csNoiMax
        targetUtility {
          pid
          name
        }
        awardedDeveloper
        awardedBidPvSizeKwDc
        awardedBidPvSizeKwAc
        awardedBidPvTypes
        csEstimatedAwardDate
        estimatedNtp
        estimatedCod
        csNpv
        roofRentY1Usd
        roofRentEscalator
        noiLifetimeUsd
        financingType
      }
    }
  }
}
    `

export const useMarketplaceSitesRowsQuery = <
  TData = Types.MarketplaceSitesRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSitesRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesRowsQuery,
      Types.MarketplaceSitesRowsQueryVariables
    >(MarketplaceSitesRowsDocument, variables),
    ...options,
  })
}

useMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows", variables]

useMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows"

export const useSuspenseMarketplaceSitesRowsQuery = <
  TData = Types.MarketplaceSitesRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSitesRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesRowsQuery,
      Types.MarketplaceSitesRowsQueryVariables
    >(MarketplaceSitesRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRowsSuspense", variables]

useSuspenseMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows"

export const useInfiniteMarketplaceSitesRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSitesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesRowsQuery,
            Types.MarketplaceSitesRowsQueryVariables
          >(MarketplaceSitesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows.infinite", variables]

useInfiniteMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows.infinite"

export const useSuspenseInfiniteMarketplaceSitesRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSitesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSitesRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesRowsQuery,
            Types.MarketplaceSitesRowsQueryVariables
          >(MarketplaceSitesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows.infinite"

useMarketplaceSitesRowsQuery.fetcher = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSitesRowsQuery,
    Types.MarketplaceSitesRowsQueryVariables
  >(MarketplaceSitesRowsDocument, variables, options)

export const MarketplaceBidsViewDocument = `
    query MarketplaceBidsView($orgSlug: String!, $filterBy: InvestorBidsTableFilter!, $sortBy: [InvestorBidsTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useMarketplaceBidsViewQuery = <
  TData = Types.MarketplaceBidsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsViewQuery, TError, TData>({
    queryKey: ["MarketplaceBidsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsViewQuery,
      Types.MarketplaceBidsViewQueryVariables
    >(MarketplaceBidsViewDocument, variables),
    ...options,
  })
}

useMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView", variables]

useMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView"

export const useSuspenseMarketplaceBidsViewQuery = <
  TData = Types.MarketplaceBidsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsViewQuery, TError, TData>({
    queryKey: ["MarketplaceBidsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsViewQuery,
      Types.MarketplaceBidsViewQueryVariables
    >(MarketplaceBidsViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsViewSuspense", variables]

useSuspenseMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView"

export const useInfiniteMarketplaceBidsViewQuery = <
  TData = InfiniteData<Types.MarketplaceBidsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsViewQuery,
            Types.MarketplaceBidsViewQueryVariables
          >(MarketplaceBidsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView.infinite", variables]

useInfiniteMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView.infinite"

export const useSuspenseInfiniteMarketplaceBidsViewQuery = <
  TData = InfiniteData<Types.MarketplaceBidsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidsView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsViewQuery,
            Types.MarketplaceBidsViewQueryVariables
          >(MarketplaceBidsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView.infinite"

useMarketplaceBidsViewQuery.fetcher = (
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsViewQuery,
    Types.MarketplaceBidsViewQueryVariables
  >(MarketplaceBidsViewDocument, variables, options)

export const MarketplaceBidsFacetsDocument = `
    query MarketplaceBidsFacets($orgSlug: String!, $filterBy: InvestorBidsTableFilter!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      view(filterBy: $filterBy) {
        stats {
          financiers
          siteNames
          siteStates
          siteStreetAddresses
        }
      }
    }
  }
}
    `

export const useMarketplaceBidsFacetsQuery = <
  TData = Types.MarketplaceBidsFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsFacets", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsFacetsQuery,
      Types.MarketplaceBidsFacetsQueryVariables
    >(MarketplaceBidsFacetsDocument, variables),
    ...options,
  })
}

useMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets", variables]

useMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets"

export const useSuspenseMarketplaceBidsFacetsQuery = <
  TData = Types.MarketplaceBidsFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsFacetsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsFacetsQuery,
      Types.MarketplaceBidsFacetsQueryVariables
    >(MarketplaceBidsFacetsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacetsSuspense", variables]

useSuspenseMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets"

export const useInfiniteMarketplaceBidsFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsFacetsQuery,
            Types.MarketplaceBidsFacetsQueryVariables
          >(MarketplaceBidsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets.infinite", variables]

useInfiniteMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets.infinite"

export const useSuspenseInfiniteMarketplaceBidsFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidsFacets.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsFacetsQuery,
            Types.MarketplaceBidsFacetsQueryVariables
          >(MarketplaceBidsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets.infinite"

useMarketplaceBidsFacetsQuery.fetcher = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsFacetsQuery,
    Types.MarketplaceBidsFacetsQueryVariables
  >(MarketplaceBidsFacetsDocument, variables, options)

export const MarketplaceBidsRowsDocument = `
    query MarketplaceBidsRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      rows(keys: $keys) {
        pid
        bidName
        sitePid
        bundleRowPid
        bundleParentPid
        name
        streetAddress
        city
        state
        zipCode
        latitude
        longitude
        county
        cbsaName
        assetManager
        packageRowIsClosed
        packageRowStatus
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        isAwardedBid
        isRecommendedBid
        bidPackagePid
        bidPackageName
        biddingOpenAt
        biddingClosedAt
        termYears
        type
        recOwner
        csEstimatedAwardDate
        estimatedCod
        estimatedNtp
        tariffProvision
        itcPercentageAssumption
        interconnectionCostUsdPerWatt
        interconnectionCostUtilityMinUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostNonUtilityUsd
        buildingOwnerExtraPaymentsUsd
        interconnectionNotes
        interconnectionRepriceProcessNotes
        extraPaymentsNotes
        incentivesNotes
        otherNotes
        csRoofRentEscalator
        csRoofRentY1Usd
        csBuildingOwnerNoiLifetimeUsd
        csRoofRentNpv
        csSystem {
          id
          ...SystemLite
        }
        ppaRoofRentEscalator
        ppaRoofRentY1Usd
        ppaPriceEscalator
        ppaPriceY1Usd
        ppaSystem {
          id
          ...System
        }
        financierPid
        financierName
        financierLogoUrl
        financierLogomarkUrl
        ppaBuildingOwnerNoiLifetimeUsd
        ppaOfftakerLifetimeSavingsUsd
        ppaYearOneOfftakerSavingsUsd
        ppaYearOneRoofRentalFee
      }
    }
  }
}
    ${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${SystemFragmentDoc}`

export const useMarketplaceBidsRowsQuery = <
  TData = Types.MarketplaceBidsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsRowsQuery,
      Types.MarketplaceBidsRowsQueryVariables
    >(MarketplaceBidsRowsDocument, variables),
    ...options,
  })
}

useMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows", variables]

useMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows"

export const useSuspenseMarketplaceBidsRowsQuery = <
  TData = Types.MarketplaceBidsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsRowsQuery,
      Types.MarketplaceBidsRowsQueryVariables
    >(MarketplaceBidsRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRowsSuspense", variables]

useSuspenseMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows"

export const useInfiniteMarketplaceBidsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsRowsQuery,
            Types.MarketplaceBidsRowsQueryVariables
          >(MarketplaceBidsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows.infinite", variables]

useInfiniteMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows.infinite"

export const useSuspenseInfiniteMarketplaceBidsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsRowsQuery,
            Types.MarketplaceBidsRowsQueryVariables
          >(MarketplaceBidsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows.infinite"

useMarketplaceBidsRowsQuery.fetcher = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsRowsQuery,
    Types.MarketplaceBidsRowsQueryVariables
  >(MarketplaceBidsRowsDocument, variables, options)

export const MarketplacePackageDetailDocument = `
    query MarketplacePackageDetail($orgSlug: String!, $bundlePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      ...BidPackageDetail
    }
  }
}
    ${BidPackageDetailFragmentDoc}
${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}`

export const useMarketplacePackageDetailQuery = <
  TData = Types.MarketplacePackageDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageDetailQuery,
      Types.MarketplacePackageDetailQueryVariables
    >(MarketplacePackageDetailDocument, variables),
    ...options,
  })
}

useMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail", variables]

useMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail"

export const useSuspenseMarketplacePackageDetailQuery = <
  TData = Types.MarketplacePackageDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageDetailQuery,
      Types.MarketplacePackageDetailQueryVariables
    >(MarketplacePackageDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetailSuspense", variables]

useSuspenseMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail"

export const useInfiniteMarketplacePackageDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackageDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageDetailQuery,
            Types.MarketplacePackageDetailQueryVariables
          >(MarketplacePackageDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail.infinite", variables]

useInfiniteMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail.infinite"

export const useSuspenseInfiniteMarketplacePackageDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplacePackageDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageDetailQuery,
            Types.MarketplacePackageDetailQueryVariables
          >(MarketplacePackageDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackageDetailQuery.rootKey =
  "MarketplacePackageDetail.infinite"

useMarketplacePackageDetailQuery.fetcher = (
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageDetailQuery,
    Types.MarketplacePackageDetailQueryVariables
  >(MarketplacePackageDetailDocument, variables, options)

export const MarketplacePackageBidReviewDocument = `
    query MarketplacePackageBidReview($orgSlug: String!, $bundlePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      bidReview {
        ...BidReview
      }
    }
  }
}
    ${BidReviewFragmentDoc}
${BidReportFragmentDoc}
${CsSiteBidReportFragmentDoc}
${InvestorProfileFragmentDoc}
${BoxplotStatsFragmentDoc}
${PpaSiteBidReportFragmentDoc}`

export const useMarketplacePackageBidReviewQuery = <
  TData = Types.MarketplacePackageBidReviewQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>({
    queryKey: ["MarketplacePackageBidReview", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageBidReviewQuery,
      Types.MarketplacePackageBidReviewQueryVariables
    >(MarketplacePackageBidReviewDocument, variables),
    ...options,
  })
}

useMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview", variables]

useMarketplacePackageBidReviewQuery.rootKey = "MarketplacePackageBidReview"

export const useSuspenseMarketplacePackageBidReviewQuery = <
  TData = Types.MarketplacePackageBidReviewQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>({
    queryKey: ["MarketplacePackageBidReviewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageBidReviewQuery,
      Types.MarketplacePackageBidReviewQueryVariables
    >(MarketplacePackageBidReviewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReviewSuspense", variables]

useSuspenseMarketplacePackageBidReviewQuery.rootKey = "MarketplacePackageBidReview"

export const useInfiniteMarketplacePackageBidReviewQuery = <
  TData = InfiniteData<Types.MarketplacePackageBidReviewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageBidReview.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageBidReviewQuery,
            Types.MarketplacePackageBidReviewQueryVariables
          >(MarketplacePackageBidReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview.infinite", variables]

useInfiniteMarketplacePackageBidReviewQuery.rootKey =
  "MarketplacePackageBidReview.infinite"

export const useSuspenseInfiniteMarketplacePackageBidReviewQuery = <
  TData = InfiniteData<Types.MarketplacePackageBidReviewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MarketplacePackageBidReviewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageBidReview.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageBidReviewQuery,
            Types.MarketplacePackageBidReviewQueryVariables
          >(MarketplacePackageBidReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackageBidReviewQuery.rootKey =
  "MarketplacePackageBidReview.infinite"

useMarketplacePackageBidReviewQuery.fetcher = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageBidReviewQuery,
    Types.MarketplacePackageBidReviewQueryVariables
  >(MarketplacePackageBidReviewDocument, variables, options)

export const MarketplacePackageRowDetailDocument = `
    query MarketplacePackageRowDetail($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      ...BidPackageDetail
      row(pid: $sitePid) {
        status
        closedAt
        closeReason
        closeCustomReason
        site {
          pid
          name
          isFavorite
          streetAddress
          city
          state
          zipCode
          county
          fullAddress
          targetUtility {
            name
          }
          doeReferenceType
          yearBuilt
          sqft
          numStories
          buildingClass
          roofSqft
          siteDetails {
            roofReplacedYr
            roofCondition
          }
          roofMaxPvKw
          meterSet {
            loadKwh
            totalCost
          }
        }
      }
    }
  }
}
    ${BidPackageDetailFragmentDoc}
${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}`

export const useMarketplacePackageRowDetailQuery = <
  TData = Types.MarketplacePackageRowDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageRowDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageRowDetailQuery,
      Types.MarketplacePackageRowDetailQueryVariables
    >(MarketplacePackageRowDetailDocument, variables),
    ...options,
  })
}

useMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail", variables]

useMarketplacePackageRowDetailQuery.rootKey = "MarketplacePackageRowDetail"

export const useSuspenseMarketplacePackageRowDetailQuery = <
  TData = Types.MarketplacePackageRowDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageRowDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageRowDetailQuery,
      Types.MarketplacePackageRowDetailQueryVariables
    >(MarketplacePackageRowDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetailSuspense", variables]

useSuspenseMarketplacePackageRowDetailQuery.rootKey = "MarketplacePackageRowDetail"

export const useInfiniteMarketplacePackageRowDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageRowDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageRowDetailQuery,
            Types.MarketplacePackageRowDetailQueryVariables
          >(MarketplacePackageRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail.infinite", variables]

useInfiniteMarketplacePackageRowDetailQuery.rootKey =
  "MarketplacePackageRowDetail.infinite"

export const useSuspenseInfiniteMarketplacePackageRowDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MarketplacePackageRowDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageRowDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageRowDetailQuery,
            Types.MarketplacePackageRowDetailQueryVariables
          >(MarketplacePackageRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackageRowDetailQuery.rootKey =
  "MarketplacePackageRowDetail.infinite"

useMarketplacePackageRowDetailQuery.fetcher = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageRowDetailQuery,
    Types.MarketplacePackageRowDetailQueryVariables
  >(MarketplacePackageRowDetailDocument, variables, options)

export const MarketplaceBidDetailDocument = `
    query MarketplaceBidDetail($orgSlug: String!, $bidPid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bid(pid: $bidPid) {
      ...InvestorBid
      ppa {
        site {
          ...BidRevampSite
        }
        financier {
          pid
          name
          orgSlug
          logoUrl
          logomarkUrl
        }
      }
      cs {
        site {
          ...BidRevampSite
        }
        financier {
          pid
          name
          orgSlug
          logoUrl
          logomarkUrl
        }
      }
    }
  }
}
    ${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${BidRevampSiteFragmentDoc}`

export const useMarketplaceBidDetailQuery = <
  TData = Types.MarketplaceBidDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidDetailQuery, TError, TData>({
    queryKey: ["MarketplaceBidDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidDetailQuery,
      Types.MarketplaceBidDetailQueryVariables
    >(MarketplaceBidDetailDocument, variables),
    ...options,
  })
}

useMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail", variables]

useMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail"

export const useSuspenseMarketplaceBidDetailQuery = <
  TData = Types.MarketplaceBidDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidDetailQuery, TError, TData>({
    queryKey: ["MarketplaceBidDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidDetailQuery,
      Types.MarketplaceBidDetailQueryVariables
    >(MarketplaceBidDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetailSuspense", variables]

useSuspenseMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail"

export const useInfiniteMarketplaceBidDetailQuery = <
  TData = InfiniteData<Types.MarketplaceBidDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidDetailQuery,
            Types.MarketplaceBidDetailQueryVariables
          >(MarketplaceBidDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail.infinite", variables]

useInfiniteMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail.infinite"

export const useSuspenseInfiniteMarketplaceBidDetailQuery = <
  TData = InfiniteData<Types.MarketplaceBidDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidDetailQuery,
            Types.MarketplaceBidDetailQueryVariables
          >(MarketplaceBidDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail.infinite"

useMarketplaceBidDetailQuery.fetcher = (
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidDetailQuery,
    Types.MarketplaceBidDetailQueryVariables
  >(MarketplaceBidDetailDocument, variables, options)

export const CurrentUserGoogleAccessDocument = `
    query CurrentUserGoogleAccess {
  currentUser {
    pid
    hasGoogleAccessToken
  }
}
    `

export const useCurrentUserGoogleAccessQuery = <
  TData = Types.CurrentUserGoogleAccessQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserGoogleAccess"]
        : ["CurrentUserGoogleAccess", variables],
    queryFn: publicFetcher<
      Types.CurrentUserGoogleAccessQuery,
      Types.CurrentUserGoogleAccessQueryVariables
    >(CurrentUserGoogleAccessDocument, variables),
    ...options,
  })
}

useCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess"]
    : ["CurrentUserGoogleAccess", variables]

useCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess"

export const useSuspenseCurrentUserGoogleAccessQuery = <
  TData = Types.CurrentUserGoogleAccessQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserGoogleAccessSuspense"]
        : ["CurrentUserGoogleAccessSuspense", variables],
    queryFn: publicFetcher<
      Types.CurrentUserGoogleAccessQuery,
      Types.CurrentUserGoogleAccessQueryVariables
    >(CurrentUserGoogleAccessDocument, variables),
    ...options,
  })
}

useSuspenseCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccessSuspense"]
    : ["CurrentUserGoogleAccessSuspense", variables]

useSuspenseCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess"

export const useInfiniteCurrentUserGoogleAccessQuery = <
  TData = InfiniteData<Types.CurrentUserGoogleAccessQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserGoogleAccessQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUserGoogleAccess.infinite"]
            : ["CurrentUserGoogleAccess.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentUserGoogleAccessQuery,
            Types.CurrentUserGoogleAccessQueryVariables
          >(CurrentUserGoogleAccessDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess.infinite"]
    : ["CurrentUserGoogleAccess.infinite", variables]

useInfiniteCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess.infinite"

export const useSuspenseInfiniteCurrentUserGoogleAccessQuery = <
  TData = InfiniteData<Types.CurrentUserGoogleAccessQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserGoogleAccessQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUserGoogleAccess.infiniteSuspense"]
            : ["CurrentUserGoogleAccess.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentUserGoogleAccessQuery,
            Types.CurrentUserGoogleAccessQueryVariables
          >(CurrentUserGoogleAccessDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess.infiniteSuspense"]
    : ["CurrentUserGoogleAccess.infiniteSuspense", variables]

useSuspenseInfiniteCurrentUserGoogleAccessQuery.rootKey =
  "CurrentUserGoogleAccess.infinite"

useCurrentUserGoogleAccessQuery.fetcher = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CurrentUserGoogleAccessQuery,
    Types.CurrentUserGoogleAccessQueryVariables
  >(CurrentUserGoogleAccessDocument, variables, options)

export const AwardedProjectsTableViewDocument = `
    query AwardedProjectsTableView($orgSlug: String!, $filterBy: AwardedProjectTableFilter, $sortBy: [AwardedProjectTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    pid
    awardedProjects {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          buildingOwners
        }
      }
    }
  }
}
    `

export const useAwardedProjectsTableViewQuery = <
  TData = Types.AwardedProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AwardedProjectsTableViewQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableViewQuery,
      Types.AwardedProjectsTableViewQueryVariables
    >(AwardedProjectsTableViewDocument, variables),
    ...options,
  })
}

useAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView", variables]

useAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView"

export const useSuspenseAwardedProjectsTableViewQuery = <
  TData = Types.AwardedProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AwardedProjectsTableViewQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableViewQuery,
      Types.AwardedProjectsTableViewQueryVariables
    >(AwardedProjectsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableViewSuspense", variables]

useSuspenseAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView"

export const useInfiniteAwardedProjectsTableViewQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AwardedProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableViewQuery,
            Types.AwardedProjectsTableViewQueryVariables
          >(AwardedProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView.infinite", variables]

useInfiniteAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView.infinite"

export const useSuspenseInfiniteAwardedProjectsTableViewQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AwardedProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AwardedProjectsTableView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableViewQuery,
            Types.AwardedProjectsTableViewQueryVariables
          >(AwardedProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView.infiniteSuspense", variables]

useSuspenseInfiniteAwardedProjectsTableViewQuery.rootKey =
  "AwardedProjectsTableView.infinite"

useAwardedProjectsTableViewQuery.fetcher = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardedProjectsTableViewQuery,
    Types.AwardedProjectsTableViewQueryVariables
  >(AwardedProjectsTableViewDocument, variables, options)

export const AwardedProjectsTableRowsDocument = `
    query AwardedProjectsTableRows($orgSlug: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    awardedProjects {
      rows(keys: $keys) {
        id
        orgName
        sitePid
        name
        streetAddress
        city
        state
        zipCode
        bundlePid
        bidPackagePid
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        estimatedCod
        estimatedNtp
        lumenRevenue
        csEstimatedAwardDate
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        rofoSignedDate
        rofoSignedEstimated
        rofoSignedActual
        preBidMeetingDate
        preBidMeetingEstimated
        preBidMeetingActual
        biddingOpenedDate
        biddingOpenedEstimated
        biddingOpenedActual
        biddingClosedDate
        biddingClosedEstimated
        biddingClosedActual
        bidsPresentedDate
        bidsPresentedEstimated
        bidsPresentedActual
        ixSubmittedDate
        ixSubmittedEstimated
        ixSubmittedActual
        ixApprovedDate
        ixApprovedEstimated
        ixApprovedActual
        csProgramSubmittedDate
        csProgramSubmittedEstimated
        csProgramSubmittedActual
        csProgramApprovedDate
        csProgramApprovedEstimated
        csProgramApprovedActual
        awardedToDeveloperDate
        awardedToDeveloperEstimated
        awardedToDeveloperActual
        leaseSignedDate
        leaseSignedEstimated
        leaseSignedActual
        ntpDate
        ntpEstimated
        ntpActual
        codDate
        codEstimated
        codActual
      }
    }
  }
}
    `

export const useAwardedProjectsTableRowsQuery = <
  TData = Types.AwardedProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableRowsQuery,
      Types.AwardedProjectsTableRowsQueryVariables
    >(AwardedProjectsTableRowsDocument, variables),
    ...options,
  })
}

useAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows", variables]

useAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows"

export const useSuspenseAwardedProjectsTableRowsQuery = <
  TData = Types.AwardedProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableRowsQuery,
      Types.AwardedProjectsTableRowsQueryVariables
    >(AwardedProjectsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRowsSuspense", variables]

useSuspenseAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows"

export const useInfiniteAwardedProjectsTableRowsQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableRowsQuery,
            Types.AwardedProjectsTableRowsQueryVariables
          >(AwardedProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows.infinite", variables]

useInfiniteAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows.infinite"

export const useSuspenseInfiniteAwardedProjectsTableRowsQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AwardedProjectsTableRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableRowsQuery,
            Types.AwardedProjectsTableRowsQueryVariables
          >(AwardedProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteAwardedProjectsTableRowsQuery.rootKey =
  "AwardedProjectsTableRows.infinite"

useAwardedProjectsTableRowsQuery.fetcher = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardedProjectsTableRowsQuery,
    Types.AwardedProjectsTableRowsQueryVariables
  >(AwardedProjectsTableRowsDocument, variables, options)

export const FinancierProjectsTableViewDocument = `
    query FinancierProjectsTableView($orgSlug: String!, $filterBy: FinancierProjectTableFilter, $sortBy: [FinancierProjectTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    pid
    financierProjects {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          buildingOwners
        }
      }
    }
  }
}
    `

export const useFinancierProjectsTableViewQuery = <
  TData = Types.FinancierProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinancierProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinancierProjectsTableViewQuery, TError, TData>({
    queryKey: ["FinancierProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.FinancierProjectsTableViewQuery,
      Types.FinancierProjectsTableViewQueryVariables
    >(FinancierProjectsTableViewDocument, variables),
    ...options,
  })
}

useFinancierProjectsTableViewQuery.getKey = (
  variables: Types.FinancierProjectsTableViewQueryVariables,
) => ["FinancierProjectsTableView", variables]

useFinancierProjectsTableViewQuery.rootKey = "FinancierProjectsTableView"

export const useSuspenseFinancierProjectsTableViewQuery = <
  TData = Types.FinancierProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierProjectsTableViewQuery, TError, TData>({
    queryKey: ["FinancierProjectsTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.FinancierProjectsTableViewQuery,
      Types.FinancierProjectsTableViewQueryVariables
    >(FinancierProjectsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseFinancierProjectsTableViewQuery.getKey = (
  variables: Types.FinancierProjectsTableViewQueryVariables,
) => ["FinancierProjectsTableViewSuspense", variables]

useSuspenseFinancierProjectsTableViewQuery.rootKey = "FinancierProjectsTableView"

export const useInfiniteFinancierProjectsTableViewQuery = <
  TData = InfiniteData<Types.FinancierProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinancierProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierProjectsTableViewQuery,
            Types.FinancierProjectsTableViewQueryVariables
          >(FinancierProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierProjectsTableViewQuery.getKey = (
  variables: Types.FinancierProjectsTableViewQueryVariables,
) => ["FinancierProjectsTableView.infinite", variables]

useInfiniteFinancierProjectsTableViewQuery.rootKey =
  "FinancierProjectsTableView.infinite"

export const useSuspenseInfiniteFinancierProjectsTableViewQuery = <
  TData = InfiniteData<Types.FinancierProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.FinancierProjectsTableViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinancierProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierProjectsTableView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierProjectsTableViewQuery,
            Types.FinancierProjectsTableViewQueryVariables
          >(FinancierProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierProjectsTableViewQuery.getKey = (
  variables: Types.FinancierProjectsTableViewQueryVariables,
) => ["FinancierProjectsTableView.infiniteSuspense", variables]

useSuspenseInfiniteFinancierProjectsTableViewQuery.rootKey =
  "FinancierProjectsTableView.infinite"

useFinancierProjectsTableViewQuery.fetcher = (
  variables: Types.FinancierProjectsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinancierProjectsTableViewQuery,
    Types.FinancierProjectsTableViewQueryVariables
  >(FinancierProjectsTableViewDocument, variables, options)

export const FinancierProjectsTableRowsDocument = `
    query FinancierProjectsTableRows($orgSlug: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    financierProjects {
      rows(keys: $keys) {
        id
        sitePid
        bundlePid
        orgName
        name
        streetAddress
        city
        state
        zipCode
        bundleName
        bundleType
        bundleBiddingOpenAt
        bundleBiddingClosedAt
        bundleRowState
        bundleRowIsClosed
        bundleRowCloseReason
        bundleRowCloseCustomReason
        bidStatuses
        optedOut
      }
    }
  }
}
    `

export const useFinancierProjectsTableRowsQuery = <
  TData = Types.FinancierProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinancierProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinancierProjectsTableRowsQuery, TError, TData>({
    queryKey: ["FinancierProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.FinancierProjectsTableRowsQuery,
      Types.FinancierProjectsTableRowsQueryVariables
    >(FinancierProjectsTableRowsDocument, variables),
    ...options,
  })
}

useFinancierProjectsTableRowsQuery.getKey = (
  variables: Types.FinancierProjectsTableRowsQueryVariables,
) => ["FinancierProjectsTableRows", variables]

useFinancierProjectsTableRowsQuery.rootKey = "FinancierProjectsTableRows"

export const useSuspenseFinancierProjectsTableRowsQuery = <
  TData = Types.FinancierProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierProjectsTableRowsQuery, TError, TData>({
    queryKey: ["FinancierProjectsTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.FinancierProjectsTableRowsQuery,
      Types.FinancierProjectsTableRowsQueryVariables
    >(FinancierProjectsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseFinancierProjectsTableRowsQuery.getKey = (
  variables: Types.FinancierProjectsTableRowsQueryVariables,
) => ["FinancierProjectsTableRowsSuspense", variables]

useSuspenseFinancierProjectsTableRowsQuery.rootKey = "FinancierProjectsTableRows"

export const useInfiniteFinancierProjectsTableRowsQuery = <
  TData = InfiniteData<Types.FinancierProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinancierProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierProjectsTableRowsQuery,
            Types.FinancierProjectsTableRowsQueryVariables
          >(FinancierProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierProjectsTableRowsQuery.getKey = (
  variables: Types.FinancierProjectsTableRowsQueryVariables,
) => ["FinancierProjectsTableRows.infinite", variables]

useInfiniteFinancierProjectsTableRowsQuery.rootKey =
  "FinancierProjectsTableRows.infinite"

export const useSuspenseInfiniteFinancierProjectsTableRowsQuery = <
  TData = InfiniteData<Types.FinancierProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierProjectsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.FinancierProjectsTableRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinancierProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierProjectsTableRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierProjectsTableRowsQuery,
            Types.FinancierProjectsTableRowsQueryVariables
          >(FinancierProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierProjectsTableRowsQuery.getKey = (
  variables: Types.FinancierProjectsTableRowsQueryVariables,
) => ["FinancierProjectsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteFinancierProjectsTableRowsQuery.rootKey =
  "FinancierProjectsTableRows.infinite"

useFinancierProjectsTableRowsQuery.fetcher = (
  variables: Types.FinancierProjectsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinancierProjectsTableRowsQuery,
    Types.FinancierProjectsTableRowsQueryVariables
  >(FinancierProjectsTableRowsDocument, variables, options)

export const SiteBidsTableViewDocument = `
    query SiteBidsTableView($orgSlug: String!, $sitePid: String!, $filterBy: SiteBidsTableFilter, $sortBy: [SiteBidsTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    siteBids(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useSiteBidsTableViewQuery = <
  TData = Types.SiteBidsTableViewQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsTableViewQuery, TError, TData>({
    queryKey: ["SiteBidsTableView", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableViewQuery,
      Types.SiteBidsTableViewQueryVariables
    >(SiteBidsTableViewDocument, variables),
    ...options,
  })
}

useSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView", variables]

useSiteBidsTableViewQuery.rootKey = "SiteBidsTableView"

export const useSuspenseSiteBidsTableViewQuery = <
  TData = Types.SiteBidsTableViewQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsTableViewQuery, TError, TData>({
    queryKey: ["SiteBidsTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableViewQuery,
      Types.SiteBidsTableViewQueryVariables
    >(SiteBidsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableViewSuspense", variables]

useSuspenseSiteBidsTableViewQuery.rootKey = "SiteBidsTableView"

export const useInfiniteSiteBidsTableViewQuery = <
  TData = InfiniteData<Types.SiteBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableViewQuery,
            Types.SiteBidsTableViewQueryVariables
          >(SiteBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView.infinite", variables]

useInfiniteSiteBidsTableViewQuery.rootKey = "SiteBidsTableView.infinite"

export const useSuspenseInfiniteSiteBidsTableViewQuery = <
  TData = InfiniteData<Types.SiteBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableViewQuery,
            Types.SiteBidsTableViewQueryVariables
          >(SiteBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView.infiniteSuspense", variables]

useSuspenseInfiniteSiteBidsTableViewQuery.rootKey = "SiteBidsTableView.infinite"

useSiteBidsTableViewQuery.fetcher = (
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsTableViewQuery, Types.SiteBidsTableViewQueryVariables>(
    SiteBidsTableViewDocument,
    variables,
    options,
  )

export const SiteBidsTableRowsDocument = `
    query SiteBidsTableRows($orgSlug: String!, $sitePid: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    siteBids(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        bidPackagePid
        bundlePid
        bidPackageName
        name
        isAwarded
        isCustom
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        estimatedCod
        estimatedNtp
        csEstimatedAwardDate
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        termYears
        roofRentY1Usd
        roofRentEscalator
        buildingOwnerExtraPaymentsUsd
        extraPaymentsNotes
        buildingOwnerNoiLifetimeUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostUtilityMinUsd
        interconnectionCostNonUtilityUsd
        interconnectionCostUsdPerWatt
        interconnectionNotes
        itcPercentageAssumption
        recOwner
        incentivesNotes
        otherNotes
      }
    }
  }
}
    `

export const useSiteBidsTableRowsQuery = <
  TData = Types.SiteBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsTableRowsQuery, TError, TData>({
    queryKey: ["SiteBidsTableRows", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableRowsQuery,
      Types.SiteBidsTableRowsQueryVariables
    >(SiteBidsTableRowsDocument, variables),
    ...options,
  })
}

useSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows", variables]

useSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows"

export const useSuspenseSiteBidsTableRowsQuery = <
  TData = Types.SiteBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsTableRowsQuery, TError, TData>({
    queryKey: ["SiteBidsTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableRowsQuery,
      Types.SiteBidsTableRowsQueryVariables
    >(SiteBidsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRowsSuspense", variables]

useSuspenseSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows"

export const useInfiniteSiteBidsTableRowsQuery = <
  TData = InfiniteData<Types.SiteBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableRowsQuery,
            Types.SiteBidsTableRowsQueryVariables
          >(SiteBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows.infinite", variables]

useInfiniteSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows.infinite"

export const useSuspenseInfiniteSiteBidsTableRowsQuery = <
  TData = InfiniteData<Types.SiteBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableRowsQuery,
            Types.SiteBidsTableRowsQueryVariables
          >(SiteBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows.infinite"

useSiteBidsTableRowsQuery.fetcher = (
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsTableRowsQuery, Types.SiteBidsTableRowsQueryVariables>(
    SiteBidsTableRowsDocument,
    variables,
    options,
  )

export const SiteFieldChangeLogDocument = `
    query SiteFieldChangeLog($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    name
    pid
    site(pid: $sitePid) {
      pid
      fieldHistory {
        ...SiteFieldChangeLog
      }
      siteDetails {
        fieldHistory {
          ...SiteDetailsFieldChangeLog
        }
      }
    }
  }
}
    ${SiteFieldChangeLogFragmentDoc}
${FieldChangeLogHistoryFragmentDoc}
${ChangeLogEntryFragmentDoc}
${SiteDetailsFieldChangeLogFragmentDoc}`

export const useSiteFieldChangeLogQuery = <
  TData = Types.SiteFieldChangeLogQuery,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteFieldChangeLogQuery, TError, TData>({
    queryKey: ["SiteFieldChangeLog", variables],
    queryFn: publicFetcher<
      Types.SiteFieldChangeLogQuery,
      Types.SiteFieldChangeLogQueryVariables
    >(SiteFieldChangeLogDocument, variables),
    ...options,
  })
}

useSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog", variables]

useSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog"

export const useSuspenseSiteFieldChangeLogQuery = <
  TData = Types.SiteFieldChangeLogQuery,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteFieldChangeLogQuery, TError, TData>({
    queryKey: ["SiteFieldChangeLogSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteFieldChangeLogQuery,
      Types.SiteFieldChangeLogQueryVariables
    >(SiteFieldChangeLogDocument, variables),
    ...options,
  })
}

useSuspenseSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLogSuspense", variables]

useSuspenseSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog"

export const useInfiniteSiteFieldChangeLogQuery = <
  TData = InfiniteData<Types.SiteFieldChangeLogQuery>,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteFieldChangeLogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFieldChangeLog.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFieldChangeLogQuery,
            Types.SiteFieldChangeLogQueryVariables
          >(SiteFieldChangeLogDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog.infinite", variables]

useInfiniteSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog.infinite"

export const useSuspenseInfiniteSiteFieldChangeLogQuery = <
  TData = InfiniteData<Types.SiteFieldChangeLogQuery>,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteFieldChangeLogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFieldChangeLog.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFieldChangeLogQuery,
            Types.SiteFieldChangeLogQueryVariables
          >(SiteFieldChangeLogDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog.infiniteSuspense", variables]

useSuspenseInfiniteSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog.infinite"

useSiteFieldChangeLogQuery.fetcher = (
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteFieldChangeLogQuery, Types.SiteFieldChangeLogQueryVariables>(
    SiteFieldChangeLogDocument,
    variables,
    options,
  )

export const AdminUsersDocument = `
    query AdminUsers {
  admin {
    activeAdmins {
      ...UserRef
    }
  }
}
    ${UserRefFragmentDoc}`

export const useAdminUsersQuery = <TData = Types.AdminUsersQuery, TError = unknown>(
  variables?: Types.AdminUsersQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminUsersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminUsersQuery, TError, TData>({
    queryKey: variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables],
    queryFn: publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
      AdminUsersDocument,
      variables,
    ),
    ...options,
  })
}

useAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables]

useAdminUsersQuery.rootKey = "AdminUsers"

export const useSuspenseAdminUsersQuery = <
  TData = Types.AdminUsersQuery,
  TError = unknown,
>(
  variables?: Types.AdminUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUsersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminUsersSuspense"]
        : ["AdminUsersSuspense", variables],
    queryFn: publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
      AdminUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsersSuspense"] : ["AdminUsersSuspense", variables]

useSuspenseAdminUsersQuery.rootKey = "AdminUsers"

export const useInfiniteAdminUsersQuery = <
  TData = InfiniteData<Types.AdminUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUsers.infinite"]
            : ["AdminUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
            AdminUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsers.infinite"] : ["AdminUsers.infinite", variables]

useInfiniteAdminUsersQuery.rootKey = "AdminUsers.infinite"

export const useSuspenseInfiniteAdminUsersQuery = <
  TData = InfiniteData<Types.AdminUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUsers.infiniteSuspense"]
            : ["AdminUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
            AdminUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUsersQuery.getKey = (
  variables?: Types.AdminUsersQueryVariables,
) =>
  variables === undefined
    ? ["AdminUsers.infiniteSuspense"]
    : ["AdminUsers.infiniteSuspense", variables]

useSuspenseInfiniteAdminUsersQuery.rootKey = "AdminUsers.infinite"

useAdminUsersQuery.fetcher = (
  variables?: Types.AdminUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
    AdminUsersDocument,
    variables,
    options,
  )

export const CollectingBidsNoticeDocument = `
    query CollectingBidsNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    biddingClosedAt
  }
}
    `

export const useCollectingBidsNoticeQuery = <
  TData = Types.CollectingBidsNoticeQuery,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CollectingBidsNoticeQuery, TError, TData>({
    queryKey: ["CollectingBidsNotice", variables],
    queryFn: publicFetcher<
      Types.CollectingBidsNoticeQuery,
      Types.CollectingBidsNoticeQueryVariables
    >(CollectingBidsNoticeDocument, variables),
    ...options,
  })
}

useCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice", variables]

useCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice"

export const useSuspenseCollectingBidsNoticeQuery = <
  TData = Types.CollectingBidsNoticeQuery,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CollectingBidsNoticeQuery, TError, TData>({
    queryKey: ["CollectingBidsNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.CollectingBidsNoticeQuery,
      Types.CollectingBidsNoticeQueryVariables
    >(CollectingBidsNoticeDocument, variables),
    ...options,
  })
}

useSuspenseCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNoticeSuspense", variables]

useSuspenseCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice"

export const useInfiniteCollectingBidsNoticeQuery = <
  TData = InfiniteData<Types.CollectingBidsNoticeQuery>,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CollectingBidsNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CollectingBidsNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CollectingBidsNoticeQuery,
            Types.CollectingBidsNoticeQueryVariables
          >(CollectingBidsNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice.infinite", variables]

useInfiniteCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice.infinite"

export const useSuspenseInfiniteCollectingBidsNoticeQuery = <
  TData = InfiniteData<Types.CollectingBidsNoticeQuery>,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CollectingBidsNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CollectingBidsNotice.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CollectingBidsNoticeQuery,
            Types.CollectingBidsNoticeQueryVariables
          >(CollectingBidsNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice.infiniteSuspense", variables]

useSuspenseInfiniteCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice.infinite"

useCollectingBidsNoticeQuery.fetcher = (
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CollectingBidsNoticeQuery,
    Types.CollectingBidsNoticeQueryVariables
  >(CollectingBidsNoticeDocument, variables, options)

export const LeaseExecutionDetailsRequestDocument = `
    query LeaseExecutionDetailsRequest($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    rows {
      id
      site {
        pid
        name
      }
      portfolio {
        pid
        title
      }
      awardedFinancier {
        name
      }
    }
  }
}
    `

export const useLeaseExecutionDetailsRequestQuery = <
  TData = Types.LeaseExecutionDetailsRequestQuery,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>({
    queryKey: ["LeaseExecutionDetailsRequest", variables],
    queryFn: publicFetcher<
      Types.LeaseExecutionDetailsRequestQuery,
      Types.LeaseExecutionDetailsRequestQueryVariables
    >(LeaseExecutionDetailsRequestDocument, variables),
    ...options,
  })
}

useLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest", variables]

useLeaseExecutionDetailsRequestQuery.rootKey = "LeaseExecutionDetailsRequest"

export const useSuspenseLeaseExecutionDetailsRequestQuery = <
  TData = Types.LeaseExecutionDetailsRequestQuery,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>({
    queryKey: ["LeaseExecutionDetailsRequestSuspense", variables],
    queryFn: publicFetcher<
      Types.LeaseExecutionDetailsRequestQuery,
      Types.LeaseExecutionDetailsRequestQueryVariables
    >(LeaseExecutionDetailsRequestDocument, variables),
    ...options,
  })
}

useSuspenseLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequestSuspense", variables]

useSuspenseLeaseExecutionDetailsRequestQuery.rootKey = "LeaseExecutionDetailsRequest"

export const useInfiniteLeaseExecutionDetailsRequestQuery = <
  TData = InfiniteData<Types.LeaseExecutionDetailsRequestQuery>,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "LeaseExecutionDetailsRequest.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.LeaseExecutionDetailsRequestQuery,
            Types.LeaseExecutionDetailsRequestQueryVariables
          >(LeaseExecutionDetailsRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest.infinite", variables]

useInfiniteLeaseExecutionDetailsRequestQuery.rootKey =
  "LeaseExecutionDetailsRequest.infinite"

export const useSuspenseInfiniteLeaseExecutionDetailsRequestQuery = <
  TData = InfiniteData<Types.LeaseExecutionDetailsRequestQuery>,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.LeaseExecutionDetailsRequestQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "LeaseExecutionDetailsRequest.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.LeaseExecutionDetailsRequestQuery,
            Types.LeaseExecutionDetailsRequestQueryVariables
          >(LeaseExecutionDetailsRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest.infiniteSuspense", variables]

useSuspenseInfiniteLeaseExecutionDetailsRequestQuery.rootKey =
  "LeaseExecutionDetailsRequest.infinite"

useLeaseExecutionDetailsRequestQuery.fetcher = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.LeaseExecutionDetailsRequestQuery,
    Types.LeaseExecutionDetailsRequestQueryVariables
  >(LeaseExecutionDetailsRequestDocument, variables, options)

export const DeveloperDiligenceRequestDocument = `
    query DeveloperDiligenceRequest($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    rows {
      id
      site {
        pid
        name
      }
      awardedFinancier {
        name
      }
    }
  }
}
    `

export const useDeveloperDiligenceRequestQuery = <
  TData = Types.DeveloperDiligenceRequestQuery,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>({
    queryKey: ["DeveloperDiligenceRequest", variables],
    queryFn: publicFetcher<
      Types.DeveloperDiligenceRequestQuery,
      Types.DeveloperDiligenceRequestQueryVariables
    >(DeveloperDiligenceRequestDocument, variables),
    ...options,
  })
}

useDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest", variables]

useDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest"

export const useSuspenseDeveloperDiligenceRequestQuery = <
  TData = Types.DeveloperDiligenceRequestQuery,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>({
    queryKey: ["DeveloperDiligenceRequestSuspense", variables],
    queryFn: publicFetcher<
      Types.DeveloperDiligenceRequestQuery,
      Types.DeveloperDiligenceRequestQueryVariables
    >(DeveloperDiligenceRequestDocument, variables),
    ...options,
  })
}

useSuspenseDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequestSuspense", variables]

useSuspenseDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest"

export const useInfiniteDeveloperDiligenceRequestQuery = <
  TData = InfiniteData<Types.DeveloperDiligenceRequestQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperDiligenceRequest.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperDiligenceRequestQuery,
            Types.DeveloperDiligenceRequestQueryVariables
          >(DeveloperDiligenceRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest.infinite", variables]

useInfiniteDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest.infinite"

export const useSuspenseInfiniteDeveloperDiligenceRequestQuery = <
  TData = InfiniteData<Types.DeveloperDiligenceRequestQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "DeveloperDiligenceRequest.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperDiligenceRequestQuery,
            Types.DeveloperDiligenceRequestQueryVariables
          >(DeveloperDiligenceRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest.infiniteSuspense", variables]

useSuspenseInfiniteDeveloperDiligenceRequestQuery.rootKey =
  "DeveloperDiligenceRequest.infinite"

useDeveloperDiligenceRequestQuery.fetcher = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeveloperDiligenceRequestQuery,
    Types.DeveloperDiligenceRequestQueryVariables
  >(DeveloperDiligenceRequestDocument, variables, options)

export const GmailSignatureDocument = `
    query GmailSignature {
  googleApi {
    sendAs {
      signature
    }
  }
}
    `

export const useGmailSignatureQuery = <
  TData = Types.GmailSignatureQuery,
  TError = unknown,
>(
  variables?: Types.GmailSignatureQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GmailSignatureQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GmailSignatureQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GmailSignatureQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["GmailSignature"] : ["GmailSignature", variables],
    queryFn: publicFetcher<
      Types.GmailSignatureQuery,
      Types.GmailSignatureQueryVariables
    >(GmailSignatureDocument, variables),
    ...options,
  })
}

useGmailSignatureQuery.getKey = (variables?: Types.GmailSignatureQueryVariables) =>
  variables === undefined ? ["GmailSignature"] : ["GmailSignature", variables]

useGmailSignatureQuery.rootKey = "GmailSignature"

export const useSuspenseGmailSignatureQuery = <
  TData = Types.GmailSignatureQuery,
  TError = unknown,
>(
  variables?: Types.GmailSignatureQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GmailSignatureQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GmailSignatureQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GmailSignatureQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GmailSignatureSuspense"]
        : ["GmailSignatureSuspense", variables],
    queryFn: publicFetcher<
      Types.GmailSignatureQuery,
      Types.GmailSignatureQueryVariables
    >(GmailSignatureDocument, variables),
    ...options,
  })
}

useSuspenseGmailSignatureQuery.getKey = (
  variables?: Types.GmailSignatureQueryVariables,
) =>
  variables === undefined
    ? ["GmailSignatureSuspense"]
    : ["GmailSignatureSuspense", variables]

useSuspenseGmailSignatureQuery.rootKey = "GmailSignature"

export const useInfiniteGmailSignatureQuery = <
  TData = InfiniteData<Types.GmailSignatureQuery>,
  TError = unknown,
>(
  variables: Types.GmailSignatureQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GmailSignatureQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GmailSignatureQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GmailSignatureQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GmailSignature.infinite"]
            : ["GmailSignature.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GmailSignatureQuery, Types.GmailSignatureQueryVariables>(
            GmailSignatureDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGmailSignatureQuery.getKey = (
  variables?: Types.GmailSignatureQueryVariables,
) =>
  variables === undefined
    ? ["GmailSignature.infinite"]
    : ["GmailSignature.infinite", variables]

useInfiniteGmailSignatureQuery.rootKey = "GmailSignature.infinite"

export const useSuspenseInfiniteGmailSignatureQuery = <
  TData = InfiniteData<Types.GmailSignatureQuery>,
  TError = unknown,
>(
  variables: Types.GmailSignatureQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GmailSignatureQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GmailSignatureQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GmailSignatureQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GmailSignature.infiniteSuspense"]
            : ["GmailSignature.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GmailSignatureQuery, Types.GmailSignatureQueryVariables>(
            GmailSignatureDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGmailSignatureQuery.getKey = (
  variables?: Types.GmailSignatureQueryVariables,
) =>
  variables === undefined
    ? ["GmailSignature.infiniteSuspense"]
    : ["GmailSignature.infiniteSuspense", variables]

useSuspenseInfiniteGmailSignatureQuery.rootKey = "GmailSignature.infinite"

useGmailSignatureQuery.fetcher = (
  variables?: Types.GmailSignatureQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GmailSignatureQuery, Types.GmailSignatureQueryVariables>(
    GmailSignatureDocument,
    variables,
    options,
  )

export const ReminderToBidDocument = `
    query ReminderToBid($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    biddingClosedAt
    bundles {
      pid
      financier {
        orgSlug
        name
        orgUsers {
          user {
            ...UserRef
          }
          userRoles {
            role
          }
        }
      }
      totalBids
      archivedAt
    }
  }
  currentUser {
    pid
    name
  }
}
    ${UserRefFragmentDoc}`

export const useReminderToBidQuery = <
  TData = Types.ReminderToBidQuery,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ReminderToBidQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ReminderToBidQuery, TError, TData>({
    queryKey: ["ReminderToBid", variables],
    queryFn: publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
      ReminderToBidDocument,
      variables,
    ),
    ...options,
  })
}

useReminderToBidQuery.getKey = (variables: Types.ReminderToBidQueryVariables) => [
  "ReminderToBid",
  variables,
]

useReminderToBidQuery.rootKey = "ReminderToBid"

export const useSuspenseReminderToBidQuery = <
  TData = Types.ReminderToBidQuery,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ReminderToBidQuery, TError, TData>({
    queryKey: ["ReminderToBidSuspense", variables],
    queryFn: publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
      ReminderToBidDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBidSuspense", variables]

useSuspenseReminderToBidQuery.rootKey = "ReminderToBid"

export const useInfiniteReminderToBidQuery = <
  TData = InfiniteData<Types.ReminderToBidQuery>,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ReminderToBidQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ReminderToBid.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
            ReminderToBidDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBid.infinite", variables]

useInfiniteReminderToBidQuery.rootKey = "ReminderToBid.infinite"

export const useSuspenseInfiniteReminderToBidQuery = <
  TData = InfiniteData<Types.ReminderToBidQuery>,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ReminderToBidQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ReminderToBid.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
            ReminderToBidDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBid.infiniteSuspense", variables]

useSuspenseInfiniteReminderToBidQuery.rootKey = "ReminderToBid.infinite"

useReminderToBidQuery.fetcher = (
  variables: Types.ReminderToBidQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
    ReminderToBidDocument,
    variables,
    options,
  )

export const BidResultForLosersDocument = `
    query BidResultForLosers($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    recvr {
      name
    }
    bundles {
      pid
      financier {
        pid
        orgSlug
        name
        orgUsers {
          user {
            ...UserRef
          }
          userRoles {
            role
          }
        }
      }
      archivedAt
    }
    rows {
      id
      awardedFinancier {
        pid
      }
    }
  }
  currentUser {
    pid
    name
    meetingSchedulingUrl
  }
}
    ${UserRefFragmentDoc}`

export const useBidResultForLosersQuery = <
  TData = Types.BidResultForLosersQuery,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BidResultForLosersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidResultForLosersQuery, TError, TData>({
    queryKey: ["BidResultForLosers", variables],
    queryFn: publicFetcher<
      Types.BidResultForLosersQuery,
      Types.BidResultForLosersQueryVariables
    >(BidResultForLosersDocument, variables),
    ...options,
  })
}

useBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers", variables]

useBidResultForLosersQuery.rootKey = "BidResultForLosers"

export const useSuspenseBidResultForLosersQuery = <
  TData = Types.BidResultForLosersQuery,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidResultForLosersQuery, TError, TData>({
    queryKey: ["BidResultForLosersSuspense", variables],
    queryFn: publicFetcher<
      Types.BidResultForLosersQuery,
      Types.BidResultForLosersQueryVariables
    >(BidResultForLosersDocument, variables),
    ...options,
  })
}

useSuspenseBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosersSuspense", variables]

useSuspenseBidResultForLosersQuery.rootKey = "BidResultForLosers"

export const useInfiniteBidResultForLosersQuery = <
  TData = InfiniteData<Types.BidResultForLosersQuery>,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidResultForLosersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidResultForLosers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidResultForLosersQuery,
            Types.BidResultForLosersQueryVariables
          >(BidResultForLosersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers.infinite", variables]

useInfiniteBidResultForLosersQuery.rootKey = "BidResultForLosers.infinite"

export const useSuspenseInfiniteBidResultForLosersQuery = <
  TData = InfiniteData<Types.BidResultForLosersQuery>,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidResultForLosersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidResultForLosers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidResultForLosersQuery,
            Types.BidResultForLosersQueryVariables
          >(BidResultForLosersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers.infiniteSuspense", variables]

useSuspenseInfiniteBidResultForLosersQuery.rootKey = "BidResultForLosers.infinite"

useBidResultForLosersQuery.fetcher = (
  variables: Types.BidResultForLosersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidResultForLosersQuery, Types.BidResultForLosersQueryVariables>(
    BidResultForLosersDocument,
    variables,
    options,
  )

export const BidLaunchNoticeDocument = `
    query BidLaunchNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    totalPvSizeKwDc
    pricingNote
    biddingClosedAt
    type
    recvr {
      name
    }
    bundles {
      pid
      financier {
        orgSlug
        name
        orgUsers {
          user {
            ...UserRef
          }
          userRoles {
            role
          }
        }
      }
    }
    rows {
      id
      specs {
        ppa {
          id
          system {
            pvSpecs {
              pvTypes
            }
          }
        }
        cs {
          id
          systemLite {
            pvSpecs {
              pvTypes
            }
          }
        }
      }
      site {
        state
      }
    }
  }
  currentUser {
    pid
    meetingSchedulingUrl
  }
}
    ${UserRefFragmentDoc}`

export const useBidLaunchNoticeQuery = <
  TData = Types.BidLaunchNoticeQuery,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidLaunchNoticeQuery, TError, TData>({
    queryKey: ["BidLaunchNotice", variables],
    queryFn: publicFetcher<
      Types.BidLaunchNoticeQuery,
      Types.BidLaunchNoticeQueryVariables
    >(BidLaunchNoticeDocument, variables),
    ...options,
  })
}

useBidLaunchNoticeQuery.getKey = (variables: Types.BidLaunchNoticeQueryVariables) => [
  "BidLaunchNotice",
  variables,
]

useBidLaunchNoticeQuery.rootKey = "BidLaunchNotice"

export const useSuspenseBidLaunchNoticeQuery = <
  TData = Types.BidLaunchNoticeQuery,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidLaunchNoticeQuery, TError, TData>({
    queryKey: ["BidLaunchNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.BidLaunchNoticeQuery,
      Types.BidLaunchNoticeQueryVariables
    >(BidLaunchNoticeDocument, variables),
    ...options,
  })
}

useSuspenseBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNoticeSuspense", variables]

useSuspenseBidLaunchNoticeQuery.rootKey = "BidLaunchNotice"

export const useInfiniteBidLaunchNoticeQuery = <
  TData = InfiniteData<Types.BidLaunchNoticeQuery>,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidLaunchNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidLaunchNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidLaunchNoticeQuery,
            Types.BidLaunchNoticeQueryVariables
          >(BidLaunchNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNotice.infinite", variables]

useInfiniteBidLaunchNoticeQuery.rootKey = "BidLaunchNotice.infinite"

export const useSuspenseInfiniteBidLaunchNoticeQuery = <
  TData = InfiniteData<Types.BidLaunchNoticeQuery>,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidLaunchNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidLaunchNotice.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidLaunchNoticeQuery,
            Types.BidLaunchNoticeQueryVariables
          >(BidLaunchNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNotice.infiniteSuspense", variables]

useSuspenseInfiniteBidLaunchNoticeQuery.rootKey = "BidLaunchNotice.infinite"

useBidLaunchNoticeQuery.fetcher = (
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidLaunchNoticeQuery, Types.BidLaunchNoticeQueryVariables>(
    BidLaunchNoticeDocument,
    variables,
    options,
  )

export const HostAwardNoticeDocument = `
    query HostAwardNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    recvr {
      name
    }
    rows {
      awardedFinancier {
        name
        orgUsers {
          user {
            ...UserRef
          }
          userRoles {
            role
          }
        }
      }
      portfolio {
        title
      }
      site {
        name
        buildingType
        state
      }
    }
  }
  currentUser {
    pid
    meetingSchedulingUrl
  }
}
    ${UserRefFragmentDoc}`

export const useHostAwardNoticeQuery = <
  TData = Types.HostAwardNoticeQuery,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HostAwardNoticeQuery, TError, TData>({
    queryKey: ["HostAwardNotice", variables],
    queryFn: publicFetcher<
      Types.HostAwardNoticeQuery,
      Types.HostAwardNoticeQueryVariables
    >(HostAwardNoticeDocument, variables),
    ...options,
  })
}

useHostAwardNoticeQuery.getKey = (variables: Types.HostAwardNoticeQueryVariables) => [
  "HostAwardNotice",
  variables,
]

useHostAwardNoticeQuery.rootKey = "HostAwardNotice"

export const useSuspenseHostAwardNoticeQuery = <
  TData = Types.HostAwardNoticeQuery,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HostAwardNoticeQuery, TError, TData>({
    queryKey: ["HostAwardNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.HostAwardNoticeQuery,
      Types.HostAwardNoticeQueryVariables
    >(HostAwardNoticeDocument, variables),
    ...options,
  })
}

useSuspenseHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNoticeSuspense", variables]

useSuspenseHostAwardNoticeQuery.rootKey = "HostAwardNotice"

export const useInfiniteHostAwardNoticeQuery = <
  TData = InfiniteData<Types.HostAwardNoticeQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HostAwardNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HostAwardNoticeQuery,
            Types.HostAwardNoticeQueryVariables
          >(HostAwardNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNotice.infinite", variables]

useInfiniteHostAwardNoticeQuery.rootKey = "HostAwardNotice.infinite"

export const useSuspenseInfiniteHostAwardNoticeQuery = <
  TData = InfiniteData<Types.HostAwardNoticeQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HostAwardNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardNotice.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HostAwardNoticeQuery,
            Types.HostAwardNoticeQueryVariables
          >(HostAwardNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNotice.infiniteSuspense", variables]

useSuspenseInfiniteHostAwardNoticeQuery.rootKey = "HostAwardNotice.infinite"

useHostAwardNoticeQuery.fetcher = (
  variables: Types.HostAwardNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HostAwardNoticeQuery, Types.HostAwardNoticeQueryVariables>(
    HostAwardNoticeDocument,
    variables,
    options,
  )

export const ProjectKickoffNoticeDocument = `
    query ProjectKickoffNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    rows {
      awardedFinancier {
        name
      }
    }
  }
}
    `

export const useProjectKickoffNoticeQuery = <
  TData = Types.ProjectKickoffNoticeQuery,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectKickoffNoticeQuery, TError, TData>({
    queryKey: ["ProjectKickoffNotice", variables],
    queryFn: publicFetcher<
      Types.ProjectKickoffNoticeQuery,
      Types.ProjectKickoffNoticeQueryVariables
    >(ProjectKickoffNoticeDocument, variables),
    ...options,
  })
}

useProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice", variables]

useProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice"

export const useSuspenseProjectKickoffNoticeQuery = <
  TData = Types.ProjectKickoffNoticeQuery,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectKickoffNoticeQuery, TError, TData>({
    queryKey: ["ProjectKickoffNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.ProjectKickoffNoticeQuery,
      Types.ProjectKickoffNoticeQueryVariables
    >(ProjectKickoffNoticeDocument, variables),
    ...options,
  })
}

useSuspenseProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNoticeSuspense", variables]

useSuspenseProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice"

export const useInfiniteProjectKickoffNoticeQuery = <
  TData = InfiniteData<Types.ProjectKickoffNoticeQuery>,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectKickoffNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectKickoffNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectKickoffNoticeQuery,
            Types.ProjectKickoffNoticeQueryVariables
          >(ProjectKickoffNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice.infinite", variables]

useInfiniteProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice.infinite"

export const useSuspenseInfiniteProjectKickoffNoticeQuery = <
  TData = InfiniteData<Types.ProjectKickoffNoticeQuery>,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectKickoffNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ProjectKickoffNotice.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectKickoffNoticeQuery,
            Types.ProjectKickoffNoticeQueryVariables
          >(ProjectKickoffNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice.infiniteSuspense", variables]

useSuspenseInfiniteProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice.infinite"

useProjectKickoffNoticeQuery.fetcher = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectKickoffNoticeQuery,
    Types.ProjectKickoffNoticeQueryVariables
  >(ProjectKickoffNoticeDocument, variables, options)
